import { useState, useEffect, useRef, useCallback, createContext, useContext, lazy, Suspense } from "react";

// Admin Components — lazy-loaded to keep initial bundle small
const AdminLogin = lazy(() => import('./components/admin/AdminLogin'));
const AdminPanel = lazy(() => import('./components/admin/AdminPanel'));
import adminAPI from './components/admin/api';

// ─── Contact API ──────────────────────────────────────────────────────────────
const API_BASE = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:3001/api' : 'https://api.letstravel.ee/api');

// ═══ i18n SYSTEM ═══
const LANGS = [
  { code: 'et', label: 'EST', full: 'Eesti' },
  { code: 'ru', label: 'РУС', full: 'Русский' },
  { code: 'en', label: 'ENG', full: 'English' },
  { code: 'lv', label: 'LAT', full: 'Latviešu' },
];

const TR = {
  // NAV
  nav_dest: { ru:'Направления', et:'Sihtkohad', lv:'Galamērķi', en:'Destinations' },
  nav_svc: { ru:'Услуги', et:'Teenused', lv:'Pakalpojumi', en:'Services' },
  nav_about: { ru:'О нас', et:'Meist', lv:'Par mums', en:'About' },
  nav_val: { ru:'Ценности', et:'Väärtused', lv:'Vērtības', en:'Values' },
  nav_blog: { ru:'Блог', et:'Blogi', lv:'Blogs', en:'Blog' },
  nav_contact: { ru:'Контакты', et:'Kontakt', lv:'Kontakti', en:'Contact' },
  nav_cta: { ru:'Спланировать поездку', et:'Planeerige reis', lv:'Plānot ceļojumu', en:'Plan Your Journey' },
  nav_home: { ru:'Главная', et:'Avaleht', lv:'Sākumlapa', en:'Home' },
  nav_acc: { ru:'Проживание', et:'Majutus', lv:'Izmitināšana', en:'Accommodation' },
  acc_l: { ru:'Комфорт и стиль', et:'Mugavus ja stiil', lv:'Komforts un stils', en:'Comfort & Style' },
  acc_t: { ru:'Наши отели', et:'Meie hotellid', lv:'Mūsu viesnīcas', en:'Our Hotels' },
  acc_s: { ru:'Тщательно отобранные отели для вашего идеального отдыха', et:'Hoolikalt valitud hotellid teie ideaalseks puhkuseks', lv:'Rūpīgi atlasītas viesnīcas jūsu ideālajam atpūtai', en:'Carefully selected hotels for your perfect stay' },
  acc_ppn: { ru:'за ночь', et:'öö eest', lv:'par nakti', en:'per night' },
  acc_am: { ru:'Удобства', et:'Mugavused', lv:'Ērtības', en:'Amenities' },
  acc_back: { ru:'← Все отели', et:'← Kõik hotellid', lv:'← Visas viesnīcas', en:'← All Hotels' },
  // HERO
  hero_ey: { ru:'Индивидуальные путешествия', et:'Eritellimusel reisid', lv:'Individuāli ceļojumi', en:'Bespoke Travel Experiences' },
  hero_t1: { ru:'Путешествия,', et:'Reisid,', lv:'Ceļojumi,', en:'Journeys That' },
  hero_t2: { ru:'которые меняют', et:'mis muudavad', lv:'kas pārveido', en:'Transform' },
  hero_sub: { ru:'Индивидуальные маршруты для тех, кто ценит время, комфорт и уникальные впечатления. Каждая поездка — как произведение искусства.', et:'Individuaalsed marsruudid neile, kes hindavad aega, mugavust ja unikaalseid elamusi.', lv:'Individuāli maršruti tiem, kas novērtē laiku, komfortu un unikālus iespaidus.', en:'Individually crafted itineraries for discerning travellers who value time, comfort, and unforgettable moments.' },
  hero_btn1: { ru:'Составить маршрут', et:'Koosta marsruut', lv:'Izveidot maršrutu', en:'Plan Your Journey' },
  hero_btn2: { ru:'Смотреть направления', et:'Vaata sihtkohti', lv:'Skatīt galamērķus', en:'Explore Destinations' },
  hero_scroll: { ru:'Листайте', et:'Kerige', lv:'Ritiniet', en:'Discover' },
  // SECTIONS
  s_dest: { ru:'Направления', et:'Sihtkohad', lv:'Galamērķi', en:'Destinations' },
  s_dest_t: { ru:'Куда отправимся?', et:'Kuhu läheme?', lv:'Uz kurieni dosimies?', en:'Where Shall We Take You?' },
  s_dest_s: { ru:'Каждое направление — тщательно продуманная история, ждущая своего путешественника', et:'Iga sihtkoht on hoolikalt koostatud lugu, mis ootab oma reisijat', lv:'Katrs galamērķis ir rūpīgi izstrādāts stāsts, kas gaida savu ceļotāju', en:'Each destination is a carefully curated story, waiting for its traveller' },
  s_about: { ru:'О нас', et:'Meist', lv:'Par mums', en:'About Us' },
  s_about_t: { ru:'Больше, чем агентство', et:'Rohkem kui agentuur', lv:'Vairāk nekā aģentūra', en:'More Than an Agency' },
  s_about_p1: { ru:"Добро пожаловать в Let's Travel — ваш надёжный проводник в мир незабываемых путешествий! Мы предлагаем полный спектр туристических услуг, чтобы сделать ваш отдых идеальным, комфортным и беззаботным.", et:"Tere tulemast Let's Traveli — teie usaldusväärne giid unustamatute reiside maailma! Pakume täielikku turismi teenuste valikut, et muuta teie puhkus ideaalseks, mugavaks ja murdevabaks.", lv:"Laipni lūdzam Let's Travel — jūsu uzticamais ceļvedis neaizmirstamu ceļojumu pasaulē! Mēs piedāvājam pilnu tūrisma pakalpojumu klāstu, lai padarītu jūsu atvaļinājumu ideālu, ērtu un bezrūpīgu.", en:"Welcome to Let's Travel — your trusted guide to a world of unforgettable journeys! We offer a full range of travel services to make your holiday perfect, comfortable, and carefree." },
  s_about_p2: { ru:'Мы работаем с ведущими туроператорами Эстонии и предлагаем все турпакеты, доступные на территории Эстонии, а также организуем вылеты из любой точки мира.', et:'Töötame Eesti juhtivate reisikorraldajatega ja pakume kõiki Eestis saadaval olevaid reisipakette, samuti korraldame väljumisi mis tahes punktist maailmas.', lv:'Mēs sadarbojamies ar Igaunijas vadošajiem tūroperatoriem un piedāvājam visas Igaunijā pieejamās tūru paketes, kā arī organizējam izlidošanas no jebkuras pasaules vietas.', en:"We work with Estonia's leading tour operators and offer all tour packages available in Estonia, as well as organising departures from anywhere in the world." },
  s_about_more: { ru:'Узнать больше', et:'Loe rohkem', lv:'Uzzināt vairāk', en:'Discover Our Story' },
  s_svc: { ru:'Наши услуги', et:'Meie teenused', lv:'Mūsu pakalpojumi', en:'Our Services' },
  s_svc_t: { ru:'Всё для идеального путешествия', et:'Kõik täiusliku reisi jaoks', lv:'Viss ideālam ceļojumam', en:'Everything for the Perfect Journey' },
  s_svc_s: { ru:'От первой идеи до последнего дня — мы берём на себя каждую деталь', et:'Esimesest ideest viimase päevani — hoolitseme iga detaili eest', lv:'No pirmās idejas līdz pēdējai dienai — rūpējamies par katru detaļu', en:'From the first spark of an idea to the final day — we handle every detail' },
  s_val: { ru:'Наши ценности', et:'Meie väärtused', lv:'Mūsu vērtības', en:'Our Values' },
  s_val_t: { ru:'Во что мы верим', et:'Millesse me usume', lv:'Kam mēs ticam', en:'What We Believe In' },
  s_blog: { ru:'Блог', et:'Blogi', lv:'Blogs', en:'Blog' },
  s_blog_t: { ru:'Истории и вдохновение', et:'Lood ja inspiratsioon', lv:'Stāsti un iedvesma', en:'Stories & Inspiration' },
  s_cta_l: { ru:'Начните путешествие', et:'Alustage reisi', lv:'Sāciet ceļojumu', en:'Begin Your Journey' },
  s_cta_t: { ru:'Готовы к приключению?', et:'Valmis seikluseks?', lv:'Gatavi piedzīvojumam?', en:'Ready to Explore?' },
  s_cta_s: { ru:'Расскажите о вашей мечте — мы воплотим её в реальность', et:'Rääkige oma unistustest — teeme need teoks', lv:'Pastāstiet par savu sapni — mēs to piepildīsim', en:"Tell us about your dream journey — we'll make it a reality" },
  s_cta_btn: { ru:'Начать планирование', et:'Alusta planeerimist', lv:'Sākt plānošanu', en:'Start Planning' },
  // QUOTE
  quote: { ru:'«Мы не продаём туры. Мы создаём истории, которые вы будете рассказывать всю жизнь.»', et:'«Me ei müü tuure. Me loome lugusid, mida räägite kogu elu.»', lv:'«Mēs nepārdodam tūres. Mēs radām stāstus, kurus stāstīsiet visu mūžu.»', en:'"We don\'t sell tours. We create stories you\'ll tell for the rest of your life."' },
  quote_a: { ru:"— Философия Let's Travel", et:"— Let's Travel filosoofia", lv:"— Let's Travel filozofija", en:"— The Let's Travel Philosophy" },
  // BUTTONS
  btn_det: { ru:'Подробнее', et:'Loe lähemalt', lv:'Sīkāk', en:'View Details' },
  btn_enq: { ru:'Оставить запрос', et:'Saada päring', lv:'Iesniegt pieprasījumu', en:'Enquire' },
  btn_book: { ru:'Забронировать', et:'Broneeri', lv:'Rezervēt', en:'Book This Journey' },
  btn_ask: { ru:'Задать вопрос', et:'Esita küsimus', lv:'Uzdot jautājumu', en:'Ask a Question' },
  btn_custom: { ru:'Запросить индивидуальный тур', et:'Taotle individuaalset tuuri', lv:'Pieprasīt individuālu tūri', en:'Request Custom Tour' },
  btn_close: { ru:'Закрыть', et:'Sulge', lv:'Aizvērt', en:'Close' },
  btn_send: { ru:'Отправить сообщение', et:'Saada sõnum', lv:'Nosūtīt ziņojumu', en:'Send Message' },
  btn_submit: { ru:'Отправить запрос', et:'Saada päring', lv:'Iesniegt pieprasījumu', en:'Submit Request' },
  btn_read: { ru:'Читать далее', et:'Loe edasi', lv:'Lasīt tālāk', en:'Read More' },
  btn_all_d: { ru:'← Все направления', et:'← Kõik sihtkohad', lv:'← Visi galamērķi', en:'← All Destinations' },
  btn_consult: { ru:'Запросить консультацию', et:'Taotle konsultatsiooni', lv:'Pieprasīt konsultāciju', en:'Request a Consultation' },
  l_all: { ru:'Все', et:'Kõik', lv:'Visi', en:'All' },
  l_from: { ru:'от', et:'alates', lv:'no', en:'from' },
  l_from_pp: { ru:'От, за человека', et:'Alates, inimese kohta', lv:'No, par personu', en:'Starting from per person' },
  l_pp: { ru:'За человека', et:'Inimese kohta', lv:'Par personu', en:'Per person' },
  l_total_price: { ru:'За тур / за всех', et:'Reisi eest / kõigile', lv:'Par ceļojumu / visiem', en:'For the journey / all guests' },
  l_pkg: { ru:'пакетов', et:'paketti', lv:'pakešu', en:'packages' },
  l_avail: { ru:'Доступные пакеты', et:'Saadaolevad paketid', lv:'Pieejamās paketes', en:'Available Packages' },
  l_dur: { ru:'Длительность', et:'Kestus', lv:'Ilgums', en:'Duration' },
  l_season: { ru:'Сезон', et:'Hooaeg', lv:'Sezona', en:'Season' },
  l_style: { ru:'Формат', et:'Formaat', lv:'Formāts', en:'Style' },
  l_dest: { ru:'Направление', et:'Sihtkoht', lv:'Galamērķis', en:'Destination' },
  l_itin: { ru:'Маршрут по дням', et:'Päevaplaan', lv:'Maršruts pa dienām', en:'Day-by-Day Itinerary' },
  l_incl: { ru:'Включено в стоимость', et:'Hinna sees', lv:'Iekļauts cenā', en:'Included' },
  l_not_incl: { ru:'Не включено', et:'Ei sisalda', lv:'Nav iekļauts', en:'Not Included' },
  l_add_svc: { ru:'Дополнительные услуги', et:'Lisateenused', lv:'Papildu pakalpojumi', en:'Additional Services' },
  l_add_desc: { ru:'К любому пакету можно добавить: подбор авиабилетов, трансферы, расширенную страховку, частный перелёт.', et:'Igale paketile saab lisada: lennupiletid, transferid, laiendatud kindlustus, eralennud.', lv:'Jebkurai paketei var pievienot: aviobiļetes, transferus, paplašinātu apdrošināšanu, privātos lidojumus.', en:'Enhance any package with: premium flights, executive transfers, comprehensive insurance, or private aviation.' },
  nf_t: { ru:'Не нашли подходящий пакет?', et:'Ei leidnud sobivat paketti?', lv:'Neatradāt piemērotu paketi?', en:'Looking for Something Unique?' },
  nf_s: { ru:'Мы создадим индивидуальный маршрут специально для вас', et:'Loome teile individuaalse marsruudi', lv:'Izveidosim individuālu maršrutu tieši jums', en:"We'll create a bespoke itinerary tailored just for you" },
  // PAGES
  dp_l: { ru:'Откройте мир', et:'Avastage maailm', lv:'Atklājiet pasauli', en:'Explore the World' },
  dp_t: { ru:'Наши направления', et:'Meie sihtkohad', lv:'Mūsu galamērķi', en:'Our Destinations' },
  dp_s: { ru:'Каждое место — история, ждущая вас', et:'Iga koht on lugu, mis ootab teid', lv:'Katra vieta ir stāsts, kas gaida jūs', en:'Every place is a story waiting to be told' },
  sp_l: { ru:'Полный спектр', et:'Täisteenused', lv:'Pilns spektrs', en:'Full Spectrum' },
  sp_t: { ru:'Наши услуги', et:'Meie teenused', lv:'Mūsu pakalpojumi', en:'Our Services' },
  sp_s: { ru:'Всё для безупречного путешествия — в одном месте', et:'Kõik laitmatu reisi jaoks — ühes kohas', lv:'Viss nevainojamam ceļojumam — vienuviet', en:'Everything for a flawless journey — in one place' },
  ap_l: { ru:'Познакомьтесь с нами', et:'Tutvuge meiega', lv:'Iepazīstieties ar mums', en:'Get to Know Us' },
  ap_t: { ru:"О Let's Travel", et:"Let's Travelist", lv:"Par Let's Travel", en:"About Let's Travel" },
  ap_s: { ru:'Команда, превратившая страсть к путешествиям в искусство', et:'Meeskond, kes muutis reisikirge kunstiks', lv:'Komanda, kas pārvērta ceļošanas aizraušanos mākslā', en:'A team that turned a passion for exploration into art' },
  ap_p1: { ru:"Добро пожаловать в Let's Travel — ваш надёжный проводник в мир незабываемых путешествий! Мы предлагаем полный спектр туристических услуг, чтобы сделать ваш отдых идеальным, комфортным и беззаботным.", et:"Tere tulemast Let's Traveli — teie usaldusväärne giid unustamatute reiside maailma! Pakume täielikku turismi teenuste valikut, et muuta teie puhkus ideaalseks, mugavaks ja murdevabaks.", lv:"Laipni lūdzam Let's Travel — jūsu uzticamais ceļvedis neaizmirstamu ceļojumu pasaulē! Mēs piedāvājam pilnu tūrisma pakalpojumu klāstu, lai padarītu jūsu atvaļinājumu ideālu, ērtu un bezrūpīgu.", en:"Welcome to Let's Travel — your trusted guide to a world of unforgettable journeys! We offer a full range of travel services to make your holiday perfect, comfortable, and carefree." },
  ap_p2: { ru:'Мы работаем с ведущими туроператорами Эстонии и предлагаем все турпакеты, доступные на территории Эстонии, а также организуем вылеты из любой точки мира.', et:'Töötame Eesti juhtivate reisikorraldajatega ja pakume kõiki Eestis saadaval olevaid reisipakette, samuti korraldame väljumisi mis tahes punktist maailmas.', lv:'Mēs sadarbojamies ar Igaunijas vadošajiem tūroperatoriem un piedāvājam visas Igaunijā pieejamās tūru paketes, kā arī organizējam izlidošanas no jebkuras pasaules vietas.', en:"We work with Estonia's leading tour operators and offer all tour packages available in Estonia, as well as organising departures from anywhere in the world." },
  ap_p3: { ru:'Наша цель — сделать каждое путешествие лёгким, комфортным и незабываемым! Доверьте нам свой отдых, и мы позаботимся обо всём. Путешествуйте с нами — надёжно, удобно и без забот!', et:'Meie eesmärk on muuta iga reis lihtsaks, mugavaks ja unustamatuks! Usaldage meile oma puhkus ja me hoolitseme kõige eest. Reisige meiega — usaldusväärselt, mugavalt ja murevabalt!', lv:'Mūsu mērķis ir padarīt katru ceļojumu vieglu, ērtu un neaizmirstamu! Uzticiet mums savu atvaļinājumu un mēs parūpēsimies par visu. Ceļojiet ar mums — uzticami, ērti un bez raizēm!', en:'Our goal is to make every journey easy, comfortable, and unforgettable! Entrust your holiday to us and we will take care of everything. Travel with us — reliably, conveniently, and worry-free!' },
  ap_s1: { ru:'Путешествий', et:'Reisi', lv:'Ceļojumu', en:'Journeys' },
  ap_s2: { ru:'Стран', et:'Riiki', lv:'Valstis', en:'Countries' },
  ap_s3: { ru:'Лет опыта', et:'Aastat kogemust', lv:'Gadu pieredzes', en:'Years' },
  ap_s4: { ru:'Рекомендуют', et:'Soovitavad', lv:'Iesaka', en:'Satisfaction' },
  ap_why_l: { ru:'Почему мы', et:'Miks meie', lv:'Kāpēc mēs', en:'Why Us' },
  ap_why_t: { ru:"Почему выбирают нас?", et:"Miks valida Let's Travel?", lv:"Kāpēc izvēlēties Let's Travel?", en:"Why Choose Us?" },
  ap_ready: { ru:'Готовы познакомиться ближе?', et:'Valmis lähemalt tutvuma?', lv:'Gatavi iepazīties tuvāk?', en:'Ready to Get Acquainted?' },
  vp_l: { ru:'Философия', et:'Filosoofia', lv:'Filozofija', en:'Philosophy' },
  vp_t: { ru:'Наши ценности', et:'Meie väärtused', lv:'Mūsu vērtības', en:'Our Values' },
  vp_s: { ru:'Принципы, определяющие каждое наше решение', et:'Põhimõtted, mis juhivad iga otsust', lv:'Principi, kas nosaka katru lēmumu', en:'The principles that guide every decision' },
  vp_p1: { ru:"В мире массового туризма мы выбрали другой путь. Let's Travel — про качество впечатлений.", et:"Massiturismia maailmas valisime teise tee. Let's Travel — elamuste kvaliteet.", lv:"Masu tūrisma pasaulē izvēlējāmies citu ceļu. Let's Travel — iespiedu kvalitāte.", en:"In a world of mass tourism, we chose a different path — quality of experiences." },
  vp_p2: { ru:'Наши ценности — ежедневные решения, которые мы принимаем, создавая ваши путешествия.', et:'Meie väärtused on igapäevased otsused teie reiside loomisel.', lv:'Mūsu vērtības ir ikdienas lēmumi, veidojot jūsu ceļojumus.', en:"Our values are the daily decisions we make while crafting your journeys." },
  bp_l: { ru:'Блог', et:'Blogi', lv:'Blogs', en:'Blog' },
  bp_t: { ru:'Истории и вдохновение', et:'Lood ja inspiratsioon', lv:'Stāsti un iedvesma', en:'Stories & Inspiration' },
  bp_s: { ru:'Путеводители, советы и истории с дороги', et:'Reisijuhid, nõuanded ja lood teelt', lv:'Ceļveži, padomi un stāsti no ceļa', en:'Guides, insights, and tales from the road' },
  b_all: { ru:'Все', et:'Kõik', lv:'Visi', en:'All' },
  b_dest: { ru:'Направления', et:'Sihtkohad', lv:'Galamērķi', en:'Destinations' },
  b_adv: { ru:'Советы', et:'Nõuanded', lv:'Padomi', en:'Advice' },
  b_insp: { ru:'Вдохновение', et:'Inspiratsioon', lv:'Iedvesma', en:'Inspiration' },
  b_news: { ru:'Новости', et:'Uudised', lv:'Jaunumi', en:'News' },
  // CONTACT
  cp_l: { ru:'Свяжитесь с нами', et:'Võtke ühendust', lv:'Sazinieties ar mums', en:'Get in Touch' },
  cp_t: { ru:'Контакты', et:'Kontakt', lv:'Kontakti', en:'Contact Us' },
  cp_s: { ru:'Мы рады рассказать о вашем следующем путешествии', et:'Räägime hea meelega teie järgmisest reisist', lv:'Ar prieku pastāstīsim par nākamo ceļojumu', en:"We'd love to hear about your next adventure" },
  cp_how: { ru:'Как с нами связаться', et:'Kuidas ühendust saada', lv:'Kā ar mums sazināties', en:'How to Reach Us' },
  cp_how_s: { ru:'Позвоните или напишите. Отвечаем в течение 2 часов.', et:'Helistage või kirjutage. Vastame 2 tunni jooksul.', lv:'Zvaniet vai rakstiet. Atbildēsim 2 stundu laikā.', en:'Call or write. We respond within 2 hours.' },
  cp_phone: { ru:'Телефон', et:'Telefon', lv:'Tālrunis', en:'Phone' },
  cp_email: { ru:'Эл. почта', et:'E-post', lv:'E-pasts', en:'Email' },
  cp_addr: { ru:'Адрес', et:'Aadress', lv:'Adrese', en:'Address' },
  cp_hours: { ru:'Часы работы', et:'Tööaeg', lv:'Darba laiks', en:'Hours' },
  cp_follow: { ru:'Мы в соцсетях', et:'Jälgi meid', lv:'Seko mums', en:'Follow Us' },
  cp_form: { ru:'Напишите нам', et:'Kirjutage meile', lv:'Rakstiet mums', en:'Send a Message' },
  cp_sent: { ru:'Сообщение отправлено', et:'Sõnum saadetud', lv:'Ziņojums nosūtīts', en:'Message Sent' },
  cp_sent_s: { ru:'Мы свяжемся с вами в ближайшее время', et:'Võtame peagi ühendust', lv:'Sazināsimies tuvākajā laikā', en:"We'll be in touch shortly" },
  // FORMS
  f_fn: { ru:'Имя', et:'Eesnimi', lv:'Vārds', en:'First Name' },
  f_ln: { ru:'Фамилия', et:'Perekonnanimi', lv:'Uzvārds', en:'Last Name' },
  f_em: { ru:'Эл. почта', et:'E-post', lv:'E-pasts', en:'Email' },
  f_ph: { ru:'Телефон', et:'Telefon', lv:'Tālrunis', en:'Phone' },
  f_topic: { ru:'Тема запроса', et:'Päringu teema', lv:'Pieprasījuma tēma', en:'Topic' },
  f_t_gen: { ru:'Общий запрос', et:'Üldpäring', lv:'Vispārīgs pieprasījums', en:'General Enquiry' },
  f_t_tour: { ru:'Индивидуальный тур', et:'Individuaalne tuur', lv:'Individuāla tūre', en:'Bespoke Tour' },
  f_t_fl: { ru:'Авиабилеты', et:'Lennupiletid', lv:'Aviobiļetes', en:'Flights' },
  f_t_htl: { ru:'Проживание', et:'Majutus', lv:'Izmitināšana', en:'Accommodation' },
  f_t_jet: { ru:'Частный перелёт', et:'Eralend', lv:'Privātais lidojums', en:'Private Aviation' },
  f_t_oth: { ru:'Другое', et:'Muu', lv:'Cits', en:'Other' },
  f_msg: { ru:'Сообщение', et:'Sõnum', lv:'Ziņojums', en:'Message' },
  f_msg_ph: { ru:'Расскажите о вашем запросе...', et:'Rääkige oma päringust...', lv:'Pastāstiet par pieprasījumu...', en:'Tell us about your enquiry...' },
  f_consent: { ru:'Я согласен(-на) с политикой конфиденциальности', et:'Nõustun privaatsuspoliitikaga', lv:'Piekrītu privātuma politikai', en:'I agree to the privacy policy' },
  // BOOKING
  bk_t: { ru:'Забронировать тур', et:'Broneeri reis', lv:'Rezervēt tūri', en:'Book Your Journey' },
  bk_s: { ru:'Заполните форму — мы свяжемся для подтверждения', et:'Täitke vorm — võtame ühendust', lv:'Aizpildiet formu — mēs sazināsimies', en:"Complete the form — we'll confirm" },
  bk_pkg: { ru:'Пакет', et:'Pakett', lv:'Pakete', en:'Package' },
  bk_dates: { ru:'Даты поездки', et:'Reisikuupäevad', lv:'Ceļojuma datumi', en:'Travel Dates' },
  bk_approx: { ru:'Даты примерные', et:'Kuupäevad ligikaudsed', lv:'Datumi aptuveni', en:'Dates are approximate' },
  bk_approx_f: { ru:'Примерные даты', et:'Ligikaudsed kuupäevad', lv:'Aptuvenie datumi', en:'Approximate dates' },
  bk_approx_ph: { ru:'Напр.: вторая половина сентября 2026', et:'Nt: septembri teine pool 2026', lv:'Piem.: septembra otrā puse 2026', en:'e.g. second half of September 2026' },
  bk_ds: { ru:'Дата начала', et:'Alguskuupäev', lv:'Sākuma datums', en:'Start date' },
  bk_de: { ru:'Дата окончания', et:'Lõppkuupäev', lv:'Beigu datums', en:'End date' },
  bk_guests: { ru:'Гости', et:'Külalised', lv:'Viesi', en:'Guests' },
  bk_adults: { ru:'Взрослые', et:'Täiskasvanud', lv:'Pieaugušie', en:'Adults' },
  bk_kids: { ru:'Дети', et:'Lapsed', lv:'Bērni', en:'Children' },
  bk_kids_age: { ru:'Возраст детей', et:'Laste vanused', lv:'Bērnu vecums', en:"Children's ages" },
  bk_kids_ph: { ru:'Напр.: 5 и 8 лет', et:'Nt: 5 ja 8 aastat', lv:'Piem.: 5 un 8 gadi', en:'e.g. 5 and 8 years' },
  bk_extras: { ru:'Доп. услуги', et:'Lisateenused', lv:'Papildu pakalpojumi', en:'Additional Services' },
  bk_fl: { ru:'Нужны авиабилеты', et:'Vajan lennupileteid', lv:'Nepieciešamas aviobiļetes', en:'Flight booking required' },
  bk_tr: { ru:'Нужен трансфер / аренда авто', et:'Vajan transferi / autorent', lv:'Nepieciešams transfers / auto noma', en:'Transfer / car rental' },
  bk_ins: { ru:'Нужна страховка', et:'Vajan kindlustust', lv:'Nepieciešama apdrošināšana', en:'Insurance required' },
  bk_jet: { ru:'Нужен частный перелёт', et:'Vajan eralendu', lv:'Nepieciešams privātais lidojums', en:'Private aviation' },
  bk_contact: { ru:'Контактные данные', et:'Kontaktandmed', lv:'Kontaktinformācija', en:'Your Details' },
  bk_comment: { ru:'Комментарий', et:'Kommentaar', lv:'Komentārs', en:'Comments' },
  bk_comment_ph: { ru:'Пожелания, вопросы...', et:'Soovid, küsimused...', lv:'Vēlmes, jautājumi...', en:'Wishes, questions...' },
  bk_thx: { ru:'Спасибо', et:'Aitäh', lv:'Paldies', en:'Thank you' },
  bk_recv: { ru:'Ваш запрос принят. Менеджер свяжется в течение 2 часов.', et:'Päring vastu võetud. Haldur võtab ühendust 2 tunni jooksul.', lv:'Pieprasījums saņemts. Menedžeris sazināsies 2 stundu laikā.', en:'Request received. A consultant will contact you within 2 hours.' },
  bk_summary: { ru:'Резюме запроса', et:'Päringu kokkuvõte', lv:'Pieprasījuma kopsavilkums', en:'Request Summary' },
  // PACKAGE TYPES & FORMATS
  pkg_type: { ru:'Турпакет', et:'Reisipakett', lv:'Tūrisma pakete', en:'Tour Package' },
  pkg_fmt_family: { ru:'Семейный', et:'Perekond', lv:'Ģimenei', en:'Family' },
  pkg_fmt_romance: { ru:'Романтика', et:'Romanss', lv:'Romantika', en:'Romance' },
  pkg_fmt_luxury: { ru:'Люкс', et:'Luksus', lv:'Luksuss', en:'Luxury' },
  pkg_fmt_adventure: { ru:'Приключения', et:'Seiklus', lv:'Piedzīvojums', en:'Adventure' },
  pkg_avail: { ru:'Доступные пакеты', et:'Saadaolevad paketid', lv:'Pieejamās paketes', en:'Available Packages' },
  // ERRORS
  e_req: { ru:'Обязательное поле', et:'Kohustuslik väli', lv:'Obligāts lauks', en:'Required' },
  e_em_ph: { ru:'Укажите email или телефон', et:'Sisestage e-post või telefon', lv:'Norādiet e-pastu vai tālruni', en:'Email or phone required' },
  e_em_inv: { ru:'Некорректный email', et:'Vigane e-post', lv:'Nederīgs e-pasts', en:'Invalid email' },
  e_min1: { ru:'Минимум 1', et:'Vähemalt 1', lv:'Vismaz 1', en:'Min 1' },
  e_consent: { ru:'Необходимо согласие', et:'Nõusolek vajalik', lv:'Nepieciešama piekrišana', en:'Consent required' },
  // FOOTER
  ft_tag: { ru:'Индивидуальные путешествия премиум-уровня с 2014 года. Маршруты по 65 странам мира.', et:'Premium individuaalreisid aastast 2014. Marsruudid 65 riiki.', lv:'Individuāli premium ceļojumi kopš 2014. Maršruti 65 valstīs.', en:'Bespoke luxury travel since 2014. Journeys across 65 countries.' },
  ft_nav: { ru:'Навигация', et:'Navigatsioon', lv:'Navigācija', en:'Navigate' },
  ft_dest: { ru:'Направления', et:'Sihtkohad', lv:'Galamērķi', en:'Destinations' },
  ft_contact: { ru:'Контакты', et:'Kontakt', lv:'Kontakti', en:'Get in Touch' },
  ft_copy: { ru:"© 2026 LETSTRAVEL OÜ. Все права защищены.", et:"© 2026 LETSTRAVEL OÜ. Kõik õigused kaitstud.", lv:"© 2026 LETSTRAVEL OÜ. Visas tiesības aizsargātas.", en:"© 2026 LETSTRAVEL OÜ. All rights reserved." },
  ft_terms: { ru:'Условия и положения', et:'Tingimused', lv:'Noteikumi', en:'Terms & Conditions' },
  // TOUR CATEGORIES
  cat_all:    { ru:'Все туры', et:'Kõik reisid', lv:'Visi tūri', en:'All Tours' },
  cat_hot:    { ru:'Горящие туры', et:'Põlevad pakkumised', lv:'Karstie piedāvājumi', en:'Hot Deals' },
  cat_beach:  { ru:'Пляжные туры', et:'Rannapuhkus', lv:'Pludmales tūri', en:'Beach Holidays' },
  cat_family: { ru:'Семейные туры', et:'Peredele', lv:'Ģimenes tūri', en:'Family Tours' },
  cat_exotic: { ru:'Экзотические туры', et:'Eksootiline', lv:'Eksotiskie tūri', en:'Exotic Tours' },
  // WHY US ITEMS
  w1_t: { ru:'Персональный подход', et:'Isiklik lähenemine', lv:'Personāla pieeja', en:'Personal Approach' },
  w1_d: { ru:'Каждый маршрут создаётся с нуля. Мы начинаем с разговора о ваших мечтах.', et:'Iga marsruut luuakse nullist. Alustame teie unistuste aruteluga.', lv:'Katrs maršruts tiek veidots no nulles. Sākam ar sarunu par jūsu sapņiem.', en:'Every route is created from scratch. We start with your dreams.' },
  w2_t: { ru:'Эксклюзивный доступ', et:'Eksklusiivne juurdepääs', lv:'Ekskluzīva piekļuve', en:'Exclusive Access' },
  w2_d: { ru:'Закрытые отели, частные острова, приватные дегустации — мы открываем недоступные двери.', et:'Kinnised hotellid, erasaared, privaatsed degusteerimised — avame kättesaamatuid uksi.', lv:'Slēgtas viesnīcas, privātas salas, privātas degustācijas — atveram nepieejamas durvis.', en:'Closed hotels, private islands, private tastings — we open inaccessible doors.' },
  w3_t: { ru:'Прозрачные цены', et:'Läbipaistvad hinnad', lv:'Caurspīdīgas cenas', en:'Transparent Prices' },
  w3_d: { ru:'Никаких скрытых комиссий. Вы знаете, за что платите.', et:'Peidetud komisjonitasusid pole. Teate, mille eest maksate.', lv:'Nekādu slēptu komisiju. Jūs zināt, par ko maksājat.', en:'No hidden commissions. You know what you pay for.' },
  w4_t: { ru:'Поддержка 24/7', et:'Tugi 24/7', lv:'Atbalsts 24/7', en:'24/7 Support' },
  w4_d: { ru:'Ваш менеджер на связи в любое время и в любой точке мира.', et:'Teie haldur on ühenduses igal ajal, kõikjal maailmas.', lv:'Jūsu menedžeris ir saziņā jebkurā laikā un vietā pasaulē.', en:'Your manager is available anytime, anywhere in the world.' },
  w5_t: { ru:'Ответственный туризм', et:'Vastutustundlik turism', lv:'Atbildīgs tūrisms', en:'Responsible Tourism' },
  w5_d: { ru:'Сотрудничаем с эко-отелями и поддерживаем локальные сообщества.', et:'Teeme koostööd ökohotellidega ja toetame kohalikke kogukondi.', lv:'Sadarbojamies ar eko-viesnīcām un atbalstām vietējās kopienas.', en:'We partner with eco-hotels and support local communities.' },
  w6_t: { ru:'Безопасность данных', et:'Andmekaitse', lv:'Datu drošība', en:'Data Security' },
  w6_d: { ru:'Полная конфиденциальность на каждом этапе — от запроса до возвращения.', et:'Täielik konfidentsiaalsus igal etapil — päringust naasmiseni.', lv:'Pilna konfidencialitāte katrā posmā — no pieprasījuma līdz atgriešanai.', en:'Full confidentiality at every stage — from inquiry to return.' },
};

const HERO_BACKGROUND_IMAGES = [
  { url: 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1920&q=80&fm=webp', credit: null },
  { url: `${import.meta.env.BASE_URL}images/mohammed-unsplash.jpg`, credit: 'Photo by Mohammed Nasim on Unsplash' },
  { url: `${import.meta.env.BASE_URL}images/weliton-unsplash.jpg`, credit: 'Photo by Weliton Soranzo on Unsplash' },
  { url: `${import.meta.env.BASE_URL}images/andreas-unsplash.jpg`, credit: 'Photo by Andreas M on Unsplash' },
];

function t(k, lang) { return TR[k]?.[lang] || TR[k]?.en || k; }
function pkgFormat(fmt,lang) { const m={Family:'pkg_fmt_family',Romance:'pkg_fmt_romance',Luxury:'pkg_fmt_luxury',Adventure:'pkg_fmt_adventure'}; return t(m[fmt]||'pkg_fmt_family',lang); }


// ─── DATA ───
const DEST_NAMES = {
  1: {n:{ru:"Турция",et:"Türgi",lv:"Turcija",en:"Turkey"},r:{ru:"Европа/Азия",et:"Euroopa/Aasia",lv:"Eiropa/Āzija",en:"Europe/Asia"},b:{ru:"Лазурное побережье, Каппадокия и богатейшая история",et:"Türkisinine rannik, Cappadocia ja rikas ajalugu",lv:"Lazūrā piekraste, Kapadokija un bagāta vēsture",en:"Azure coastline, Cappadocia and rich history"}},
  2: {n:{ru:"Египет",et:"Egiptus",lv:"Ēģipte",en:"Egypt"},r:{ru:"Африка",et:"Aafrika",lv:"Āfrika",en:"Africa"},b:{ru:"Пирамиды, Красное море и вечная загадка цивилизации",et:"Püramiidid, Punane meri ja tsivilisatsiooni igavene mõistatus",lv:"Piramīdas, Sarkanā jūra un civilizācijas mūžīgā mīkla",en:"Pyramids, the Red Sea and the eternal mystery of civilisation"}},
  3: {n:{ru:"Греция",et:"Kreeka",lv:"Grieķija",en:"Greece"},r:{ru:"Европа",et:"Euroopa",lv:"Eiropa",en:"Europe"},b:{ru:"Белоснежные деревни, оливковые рощи и вино богов",et:"Lumivalged külad, oliivisalud ja jumalate vein",lv:"Balti ciemati, olīvu birzis un dievu vīns",en:"Whitewashed villages, olive groves and the wine of the gods"}},
  4: {n:{ru:"Черногория",et:"Montenegro",lv:"Melnkalne",en:"Montenegro"},r:{ru:"Европа",et:"Euroopa",lv:"Eiropa",en:"Europe"},b:{ru:"Адриатика, горные фьорды и аутентичный уют",et:"Aadria meri, mägifjordid ja autentne hubasus",lv:"Adriatika, kalnu fjordi un autentisks mājīgums",en:"Adriatic, mountain fjords and authentic charm"}},
  5: {n:{ru:"Албания",et:"Albaania",lv:"Albānija",en:"Albania"},r:{ru:"Европа",et:"Euroopa",lv:"Eiropa",en:"Europe"},b:{ru:"Нетронутая Ривьера, вкус дикой природы Балкан",et:"Puutumatu Riviera, Balkani metsiku looduse maitse",lv:"Neaizskarta Rivjēra, Balkānu savvaļas garša",en:"Unspoiled Riviera and the taste of wild Balkans"}},
  6: {n:{ru:"Болгария",et:"Bulgaaria",lv:"Bulgārija",en:"Bulgaria"},r:{ru:"Европа",et:"Euroopa",lv:"Eiropa",en:"Europe"},b:{ru:"Золотые пески Чёрного моря и горные курорты",et:"Musta mere kuldsed liivarannad ja mäekuurordid",lv:"Melnās jūras zeltainas smiltis un kalnu kūrorti",en:"Golden sands of the Black Sea and mountain resorts"}},
  7: {n:{ru:"Тунис",et:"Tuneesia",lv:"Tunisija",en:"Tunisia"},r:{ru:"Африка",et:"Aafrika",lv:"Āfrika",en:"Africa"},b:{ru:"Средиземноморье, пустыня Сахара и рынки медины",et:"Vahemere rand, Sahara kõrb ja medina turud",lv:"Vidusjūras piekraste, Sahāras tuksnesis un medīnas tirgi",en:"Mediterranean coast, Sahara desert and medina markets"}},
  8: {n:{ru:"Испания",et:"Hispaania",lv:"Spānija",en:"Spain"},r:{ru:"Европа",et:"Euroopa",lv:"Eiropa",en:"Europe"},b:{ru:"Солнце, море и яркие впечатления",et:"Päike, meri ja helged muljed",lv:"Saule, jūra un spilgtas iesapusmes",en:"Sun, sea and vibrant impressions"}},
  9: {n:{ru:"ОАЭ",et:"AÜE",lv:"AAE",en:"UAE"},r:{ru:"Азия",et:"Aasia",lv:"Āzija",en:"Asia"},b:{ru:"Дубай и Абу-Даби: роскошь без границ",et:"Dubai ja Abu Dhabi: piiritu luksus",lv:"Dubaija un Abu Dabī: neierobežota greznība",en:"Dubai & Abu Dhabi: luxury without limits"}},
  10:{n:{ru:"Таиланд",et:"Tai",lv:"Taizeme",en:"Thailand"},r:{ru:"Азия",et:"Aasia",lv:"Āzija",en:"Asia"},b:{ru:"Острова, буддийские храмы и уличная кухня",et:"Saared, budistlikud templid ja tänavakuisine",lv:"Salas, budisma tempļi un ielu virtuve",en:"Islands, Buddhist temples and vibrant street food"}},
  11:{n:{ru:"Мальдивы",et:"Maldiivid",lv:"Maldīvija",en:"Maldives"},r:{ru:"Азия",et:"Aasia",lv:"Āzija",en:"Asia"},b:{ru:"Бирюзовые лагуны и виллы на воде",et:"Türkiissinised laguunid ja veepeal villad",lv:"Tirkīza lagūnas un villas uz ūdens",en:"Turquoise lagoons and overwater villas"}},
  12:{n:{ru:"Италия",et:"Itaalia",lv:"Itālija",en:"Italy"},r:{ru:"Европа",et:"Euroopa",lv:"Eiropa",en:"Europe"},b:{ru:"Гастрономия, история и эксклюзивные впечатления",et:"Gastronoomia, ajalugu ja eksklusiivsed muljed",lv:"Gastronomija, vēsture un eksklusīvas iesūtņas",en:"Gastronomy, history and exclusive experiences"}},
  13:{n:{ru:"Узбекистан",et:"Usbekistan",lv:"Uzbekistāna",en:"Uzbekistan"},r:{ru:"Азия",et:"Aasia",lv:"Āzija",en:"Asia"},b:{ru:"Древние города, Шелковый путь и яркие впечатления",et:"Iidsed linnad, Siidiraja ja helged muljed",lv:"Senas pilsētas, Zīda ceļš un spilgtas iesūtņas",en:"Ancient cities, Silk Route and vibrant impressions"}},
  14:{n:{ru:"Япония",et:"Jaapan",lv:"Japāna",en:"Japan"},r:{ru:"Азия",et:"Aasia",lv:"Āzija",en:"Asia"},b:{ru:"Традиции, футуризм и незабываемые впечатления",et:"Traditsioonid, futurism ja unustamatud muljed",lv:"Tradīcijas, futurisms un neatkarīgas iesūtņas",en:"Traditions, futurism and unforgettable impressions"}},
  15:{n:{ru:"Грузия",et:"Gruusia",lv:"Gruzija",en:"Georgia"},r:{ru:"Азия",et:"Aasia",lv:"Āzija",en:"Asia"},b:{ru:"Горы, виноградники и восточный колорит",et:"Mäed, viinamarjaistikud ja idamaine värvingus",lv:"Kalni, vīngardi un austrumu nokrāsa",en:"Mountains, vineyards and oriental flair"}},
};
const DESTINATIONS = [
  { id:1,  name:"Turkey",      country:"Turkey",      categories:["beach","family","hot"],    brief:"Azure coastline, Cappadocia and rich history",       description:"Турция — это удивительное сочетание истории, культуры и природы. Древние города и руины, живописные пляжи с кристально чистой водой, горные пейзажи. Турецкая кухня — настоящая гастрономическая сказка с ароматными специями и свежими морепродуктами. От уютных улочек Стамбула до побережья Анталии и сказочных ландшафтов Каппадокии — Турция предлагает отдых на любой вкус для всей семьи.", description_en:"Turkey is an amazing blend of history, culture, and nature. Here, you will find ancient cities and ruins, picturesque beaches with crystal-clear water, and mountain landscapes for active holidays. Turkish cuisine is a true gastronomic fairy tale: aromatic spices, fresh seafood, and desserts that are impossible to resist. Turkey is an ideal destination for families with children: safe beaches, water parks, exciting excursions, and entertainment for every age. From the cozy streets of Istanbul to the coast of Antalya and the magical landscapes of Cappadocia, Turkey offers a holiday for every taste. Turkey is a place where the whole family can relax and enjoy!", description_lv:"Turcija ir pārsteidzošs vēstures, kultūras un dabas apvienojums. Šeit atradīsiet senas pilsētas un drupas, gleznainas pludmales ar kristāldzidru ūdeni un kalnu ainavas aktīvai atpūtai. Turku virtuve ir īsta gastronomiska pasaka: aromātiskas garšvielas, svaigas jūras veltes un saldumi, no kuriem nav iespējams atteikties. Turcija ir ideāla vieta atpūtai ar bērniem: drošas pludmales, akvaparki, interesantas ekskursijas un izklaide visiem vecumiem. No mājīgajām Stambulas ieliņām līdz Antālijas piekrastei un pasakainajām Kapadokijas ainavām — Turcija piedāvā atpūtu ikvienai gaumei. Turcija ir vieta, kur atpūšas un bauda visa ģimene!", description_et:"Türgi on ajaloo, kultuuri ja looduse uskumatu segamine. Siit leiate iidseid linnu ja varemeeid, maaliusi randu kristallselge veega ja mäeauke aktiivsete puhkuste jaoks. Türgi köök on tõeline kulinaarne muinasjutt: aroomirikkad maustused, värsked mereannid ja magustused, millest on võimatu keelduda. Türgi on ideaalne paikkogu perele: ohutud rannad, veeparid, põnevad ekskursioonid ja meelelahutus igale vanusele. Istanbuli mutatavatest tänavastest Antalya rannikuni ja Cappadocia nõidusvotulike maastikuni — Türgi pakub puhkust igale maitsele. Türgi on koht, kus puhkab ja nautsib kogu pere!", image:"https://images.unsplash.com/photo-1527838832700-5059252407fa?w=800&q=80&fm=webp", packages:[4] },
  { id:2,  name:"Egypt",       country:"Egypt",       categories:["beach","family","hot"],    brief:"Pyramids, the Red Sea and the eternal mystery",          description:"Египет — это солнце, песчаные пляжи и уникальная история. Древние пирамиды и храмы соседствуют с кристально чистым Красным морем и невероятными коралловыми рифами. Подводный мир Египта завораживает яркими кораллами, тропическими рыбами и морскими черепахами. Это одно из самых доступных направлений, где отдых сочетает комфорт, сервис и приятные цены, идеальное место для отдыха всей семьёй.", description_en:"Egypt is sunshine, sandy beaches, and unique history you can see with your own eyes. Ancient pyramids and temples stand side by side with the crystal-clear Red Sea and incredible coral reefs. The underwater world of Egypt is truly mesmerizing: bright corals, tropical fish, and sea turtles create impressions that stay with you forever. You can relax here with the whole family, enjoying the sea, excursions, and walks together while creating vivid memories. And importantly, it is one of the most affordable destinations: holidays here combine comfort and service with pleasant prices. Egypt is a country where people relax and enjoy together!", description_lv:"Ēģipte ir saule, smilšainas pludmales un unikāla vēsture, ko var redzēt savām acīm. Senās piramīdas un tempļi te kaimiņos atrodas ar kristāldzidro Sarkano jūru un neticamiem koraļļu rifiem. Ēģiptes zemūdens pasaule patiesi apbur: koši koraļļi, tropu zivis un jūras bruņurupuči rada iespaidus, kas paliks atmiņā uz mūžu. Šeit var atpūsties kopā ar visu ģimeni, baudot jūru, ekskursijas un pastaigas, radot spilgtas atmiņas. Un, kas nav mazsvarīgi, tas ir viens no pieejamākajiem galamērķiem: atpūta šeit apvieno komfortu un servisu ar patīkamām cenām. Ēģipte ir valsts, kur atpūšas un bauda kopā!", description_et:"Egiptus on päikesepaiste, liivased rannad ja ainulaadne ajalugu, mida saab oma silmaga näha. Muistsed püramiidid ja templid seisavad kõrvuti kristallselge Punase merega ja uskumatu koralliriffidega. Egiptuse veealune maailm on tõeliselt võluv: heledad korallid, troopika kalad ja merekelbased loovad muljeid, mis jäävad sulle igaveseks. Siin saad terve perega puhata, meri nautida, ekskursioonidele minna ja koos käia ning luua eredaid mälestusi. Ja mis veelgi olulisem, see on üks kõige taskukohasetest sihtkohist: puhkus siin ühendab mugavust ja teenust mõistlike hindadega. Egiptus on riik, kus puhata ja koos nautida!", image:"https://images.unsplash.com/photo-1539650116574-75c0c6d73f6e?w=800&q=80&fm=webp", packages:[] },
  { id:3,  name:"Greece",      country:"Greece",      categories:["beach","family"],          brief:"Whitewashed villages, olive groves and the wine of the gods", description:"Греция — страна, где каждый день наполнен солнцем и ароматом моря. Белоснежные деревни с гармонией истории и природы, оливковые рощи и виноградники создают неповторимый колорит. Местная кухня удивляет свежестью продуктов и насыщенными вкусами. Для семей — множество пляжей и экскурсий. Для романтических пар Санторини с белоснежными домиками — идеальное место для медового месяца и романтических праздников. Греция сочетает комфорт, атмосферу Средиземноморья и незабываемые впечатления для всех.", description_en:"Greece is a country where every day is filled with sunshine, the scent of the sea, and the amazing atmosphere of the Mediterranean. Walking through whitewashed villages, you feel the harmony of history and nature, while olive groves and vineyards create the unique character of the islands. Local cuisine impresses with the freshness of its ingredients and rich flavors, and the famous wine of the gods makes every evening special. You can holiday here with the whole family, enjoying beaches, excursions, and walks. For romantic couples, Greece is a real treasure: Santorini, with its white houses and sunsets over the sea, is the perfect place for a honeymoon, wedding, or romantic celebration. Greece is a country where holidays combine comfort, Mediterranean atmosphere, and unforgettable experiences for everyone!", description_lv:"Grieķija ir valsts, kur katra diena ir piepildīta ar sauli, jūras smaržu un brīnišķīgo Vidusjūras atmosfēru. Pastaigājoties pa baltajiem ciematiem, jūtama vēstures un dabas harmonija, bet olīvu birzis un vīna dārzi rada neatkārtojamu salu noskaņu. Vietējā virtuve pārsteidz ar svaigiem produktiem un bagātīgām garšām, bet slavenais dievu vīns padara katru vakaru īpašu. Šeit var atpūsties ar visu ģimeni, baudot pludmales, ekskursijas un pastaigas. Romantiskajiem pāriem Grieķija ir īsts dārgums: Santorini ar baltajām mājiņām un saulrietiem pie jūras ir ideāla vieta medusmēnesim, kāzām vai romantiskiem svētkiem. Grieķija ir valsts, kur atpūta apvieno komfortu, Vidusjūras atmosfēru un neaizmirstamus iespaidus ikvienam!", description_et:"Kreeka on riik, kus iga päev on täidetud päikesepaisteega, mere löhnaga ja Vahemere kirgliku atmosfäärga. Kõndides läbi valgetest küladest tunned ajaloo ja looduse harmoonia, kusjuures oliiviharud ja viinamarjaiστатad loovad saarete ainulaadse iseloomu. Kohalik köök muljetab värskete koostisosadega ja rikkalike maitsega, ning jumalate kuulus viin muudab iga õhtu eriliseksi. Siin saad terve perega puhata, randu, ekskursioone ja paadisõite nautida. Armastavaile paaridele on Kreeka tõeline aare: Santorini valgete majade ja mere kohal olevate päikeseloojangutega on ideaalne koht mesuaadiks, pulma või romantilisteks pidustusteks. Kreeka on riik, kus puhkus ühendab mugavust, Vahemere atmosfääri ja unustamatuid kogemusi kõigile!", image:"https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=800&q=80&fm=webp", packages:[1,2] },
  { id:4,  name:"Montenegro",  country:"Montenegro",  categories:["beach","family"],          brief:"Adriatic, mountain fjords and authentic charm",           description:"Черногория — жемчужина Адриатики с кристально чистым морем, живописными бухтами, горными вершинами и зелеными долинами. Местная кухня порадует свежими морепродуктами и региональными блюдами. Мягкий климат делает отдых комфортным в любое время года. Разнообразие пляжей, прогулок и экскурсионных маршрутов подходит для отдыха с детьми. Черногория — идеальное место, чтобы соединить отдых, приключения и наслаждение природой, создавая яркие воспоминания для всей семьи.", description_en:"Montenegro is a true gem of the Adriatic: crystal-clear sea, picturesque bays, mountain peaks, and green valleys create a unique natural landscape. Local cuisine delights with fresh seafood and regional dishes that the whole family will enjoy. The mild climate makes holidays comfortable at any time of year, and the variety of beaches, walks, and excursion routes is perfect for families with children. Montenegro is the ideal place to combine relaxation, adventure, and the joy of nature, creating bright memories for the whole family.", description_lv:"Melnkalne ir īsta Adrijas pērle: kristāldzidra jūra, gleznaini līči, kalnu virsotnes un zaļas ielejas veido unikālu dabas ainavu. Vietējā virtuve iepriecinās ar svaigām jūras veltēm un reģionālajiem ēdieniem, kas patiks visai ģimenei. Maigais klimats padara atpūtu komfortablu jebkurā gadalaikā, bet pludmaļu, pastaigu un ekskursiju maršrutu daudzveidība ir piemērota atpūtai ar bērniem. Melnkalne ir ideāla vieta, kur apvienot atpūtu, piedzīvojumus un dabas baudīšanu, radot spilgtas atmiņas visai ģimenei.", description_et:"Montenegro on Adrija tõeline pärl: kristallselge merevesi, maaliusad lahekesed, mäeharjad ja rohelised oruvad loovad unikaalse loodusmaastiku. Kohalik köök rõõmustab värskete mereandidega ja regioonpõhiste roogadega, mis meeldivad kogu perele. Pehme klimaat muudab puhkuse mugavaks aastat ringi ning randade, jalutuste ja ekskursioonimaršrutite rohkus sobib suurepäraselt lastega perelepuhkuseks. Montenegro on ideaalne koht lõõgastumine, seiklused ja looduse nautimine kombineerida, loodes ere mälestusi kogu perele.", image:"https://images.unsplash.com/photo-1555990793-da11153b2473?w=800&q=80&fm=webp", packages:[] },
  { id:5,  name:"Albania",     country:"Albania",     categories:["beach","hot"],             brief:"Unspoiled Riviera and the taste of wild Balkans",         description:"Албания — удивительная страна с кристально чистым морем, золотыми пляжами и живописными горными пейзажами. Местная кухня порадует свежими морепродуктами, ароматными блюдами и домашними сладостями. Мягкий климат делает отдых комфортным в любое время года. Пляжи и разнообразные маршруты подходят для отдыха с детьми. Албания — идеальное место для тех, кто ценит природу, спокойствие и доступный отдых всей семьёй.", description_en:"Albania is an amazing country with crystal-clear sea, golden beaches, and picturesque mountain landscapes. Local cuisine delights with fresh seafood, aromatic dishes, and homemade sweets that the whole family will enjoy. The mild climate makes holidays comfortable at any time of year, and the beaches and variety of routes are perfect for families with children. Albania is the ideal place for those who value nature, tranquility, and affordable family holidays!", description_lv:"Albānija ir pārsteidzoša valsts ar kristāldzidru jūru, zeltainām pludmalēm un gleznainām kalnu ainavām. Vietējā virtuve iepriecinās ar svaigām jūras veltēm, aromātiskiem ēdieniem un mājas saldumiem, kas patiks visai ģimenei. Maigais klimats padara atpūtu komfortablu jebkurā gadalaikā, bet pludmales un dažādie maršruti ir piemēroti atpūtai ar bērniem. Albānija ir ideāla vieta tiem, kas novērtē dabu, mieru un pieejamu ģimenes atpūtu!", description_et:"Albaania on ajalooline riik kristallselge merega, kullaksa randadega ja maaliusate mäeainetega. Kohalik köök rõõmustab värskete mereannidega, aroomuste rikkagate roogadega ja koduste magustustega, mis meeldivad kogu perele. Pehme klimaat teeb puhkuse mugavaks aastat ringi ning rannad ja erinevad maršrudid sobivad ideaalselt lasteriga perepuhkusele. Albaania on ideaalne koht neile, kes hindavad loodust, rahulikkust ja tasúkohast perepuhkust!", image:"https://images.unsplash.com/photo-1587168370878-3e44c1513985?w=800&q=80&fm=webp", packages:[] },
  { id:6,  name:"Bulgaria",    country:"Bulgaria",    categories:["beach","family","hot"],    brief:"Golden sands of the Black Sea and mountain resorts",     description:"Болгария — мягкий климат, солнечные пляжи Чёрного моря и живописные горные курорты. Идеальное место для отдыха всей семьи: игры на пляже, прогулки по набережным и наслаждение природой вместе с детьми. Местная кухня порадует свежими морепродуктами и домашними блюдами. Отдых здесь остаётся доступным и комфортным. Болгария — сочетание природы, комфорта и доступного отдыха для всей семьи.", description_en:"Bulgaria offers a mild climate, sunny Black Sea beaches, and picturesque mountain resorts. It is an ideal place for a family holiday — playing on the beach, walking along the promenades, and enjoying nature together with children. Local cuisine delights with fresh seafood and homemade dishes, while holidays here remain affordable and comfortable. Bulgaria is a combination of nature, comfort, and affordable holidays for the whole family!", description_lv:"Bulgārija piedāvā maigu klimatu, saulainas Melnās jūras pludmales un gleznainus kalnu kūrortus. Tā ir ideāla vieta visas ģimenes atpūtai — spēlēm pludmalē, pastaigām pa promenādēm un dabas baudīšanai kopā ar bērniem. Vietējā virtuve iepriecinās ar svaigām jūras veltēm un mājīgiem ēdieniem, bet atpūta šeit joprojām ir pieejama un komfortabla. Bulgārija ir dabas, komforta un pieejamas atpūtas apvienojums visai ģimenei!", description_et:"Bulgaaria pakub pehmet kliimat, päikeselisi Musta mere randu ja maalilisi mägikuurorte. See on ideaalne koht kogu pere puhkuseks — rannas mängimiseks, promenaadidel jalutamiseks ja looduse nautimiseks koos lastega. Kohalik köök rõõmustab värskete mereandide ja koduste roogadega ning puhkus on siin endiselt soodne ja mugav. Bulgaaria on looduse, mugavuse ja taskukohase puhkuse ideaalne kooslus kogu perele!", image:"https://images.unsplash.com/photo-1560969184-10fe8719e047?w=800&q=80&fm=webp", packages:[] },
  { id:7,  name:"Tunisia",     country:"Tunisia",     categories:["beach","hot"],             brief:"Mediterranean coast, Sahara and medina markets",          description:"Тунис — сочетание лазурного Средиземного моря, золотых пляжей и завораживающей атмосферы пустыни Сахара. Прогулки по рынкам Медины погружают в колорит местной культуры: специи, керамика, ароматные сладости и традиционные ремесла. Местная кухня порадует свежими морепродуктами и блюдами с насыщенным вкусом для всей семьи. Отдыхать можно всей семьёй и в романтических поездках: уютные пляжи, морские прогулки и закаты создают идеальную атмосферу. Тунис — доступное направление с мягким климатом, где отдых сочетается с приключениями, культурой и вкусной едой.", description_en:"Tunisia combines the azure Mediterranean Sea, golden beaches, and the captivating atmosphere of the Sahara Desert. Walks through the Medina markets immerse you in the colors of local culture: spices, ceramics, fragrant sweets, and traditional crafts. Local cuisine delights with fresh seafood and richly flavored dishes that the whole family will enjoy. In Tunisia, you can travel with the whole family or on a romantic trip: cozy beaches, sea walks, and sunsets create the perfect atmosphere for memories. Tunisia is an affordable destination with a mild climate, where relaxation is combined with adventure, culture, and delicious food.", description_lv:"Tunisija ir debeszilās Vidusjūras, zeltaino pludmaļu un valdzinošās Sahāras tuksnesa atmosfēras apvienojums. Pastaigas pa Medīnas tirgiem ļauj iejusties vietējās kultūras kolorītā: garšvielas, keramika, smaržīgi saldumi un tradicionālie amatniecības izstrādājumi. Vietējā virtuve iepriecinās ar svaigām jūras veltēm un bagātīgām garšām, kas patiks visai ģimenei. Tunisijā var atpūsties ar visu ģimeni vai doties romantiskā ceļojumā: mājīgas pludmales, pastaigas pie jūras un saulrieti rada ideālu atmosfēru atmiņām. Tunisija ir pieejams galamērķis ar maigu klimatu, kur atpūta apvienojas ar piedzīvojumiem, kultūru un gardu ēdienu.", description_et:"Tuneesia ühendab kristallinepurse Vahemerd, kullaksa randu ja Sahara kõrbe võluva atmosfääri. Medina turul jalutamine sukeldab teid kohaliku kultuuri värvide sisse: maustused, keramika, aroomitingmagustused ja traditsioonilised käsitööd. Kohalik köök rõõmustab värskete mereandidega ja rikkalikult maitsestatud roogadega, mis meeldivad kogu perele. Tuneesias saad reisida terve perega või romantilistel reisidel: maalili rannad, mere jalutused ja päikeseloojangud loovad täiuslikke mäletanuse hetki. Tuneesia on taskukohane sihtkoh pehme kliimaga, kus puhkus ühendub seikluste, kultuuri ja maitsvate toitudega.", image:"https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&q=80&fm=webp", packages:[] },
  { id:8, name:"Spain",       country:"Spain",       categories:["beach","family"],          brief:"Sun, sea and vibrant impressions",                      description:"Испания сочетает пляжный отдых Тенерифе и Малаги с яркими городами Барселоны и Мадрида. Здесь можно гулять по старинным улочкам, посещать знаменитые архитектурные памятники и рынки, наслаждаться вкусной кухней: свежими морепродуктами, паэльей, тапас и сладостями. Для семей с детьми — множество парков развлечений. Любители спорта могут посетить футбольные матчи на стадионах Барселоны, Мадрида и других городов. Испания — идеальное сочетание солнца, моря, культуры и ярких впечатлений для всей семьи.", description_en:"Spain combines beach holidays in Tenerife and Málaga with vibrant cities like Barcelona and Madrid. Here, you can stroll through old streets, visit famous architectural landmarks and markets, and enjoy delicious cuisine: fresh seafood, paella, tapas, and sweets. For families with children, it is a true find: there are many amusement parks, and sports fans can visit football matches in Barcelona, Madrid, and other cities. Spain is the perfect combination of sun, sea, culture, and vivid experiences for the whole family!", description_lv:"Spānija apvieno pludmales atpūtu Tenerifē un Mālagā ar spilgtiem miestiem, piemēram, Barcelonu un Madridi. Šeit var pastaigāties pa vecpilsētas ieliņām, apmeklēt slavenos arhitektūras pieminekļus un tirgus, kā arī baudīt gardu virtuvi: svaigas jūras veltes, paelju, tapas un saldumus. Ģimenēm ar bērniem tā ir īsta atradne: daudz atrakciju parku, bet sporta fani var apmeklēt futbola spēles Barselonā, Madridē un citās pilsētās. Spānija ir idealus saules, jūras, kultūras un spilgtu iespaidu apvienojums visai ģimenei!", description_et:"Hispaania ühendab rannapuhkuse Tenerifel ja Málagas elavate linnadega nagu Barcelona ja Madrid. Siin saab jalutada vanalinna tänavaitel, külastada kuulsaid arhitektuurimemälestisi ja turge ning nautida maitsvat kööki: värsked mereannid, paella, tapased ja maiustused. Lastega peredele on see tõeline leid: palju lõbustusparke ning spordifännid saavad külastada jalgpallimatsse Barcelonas, Madridis ja teistes linnades. Hispaania on ideaalne päikese, mere, kultuuri ja eredate elamuste kooslus kogu perele!", image:"https://images.unsplash.com/photo-1562883676-8c6fb65b76d5?w=800&q=80&fm=webp", packages:[] },
  { id:9, name:"UAE",         country:"UAE",         categories:["beach","family","exotic"], brief:"Dubai & Abu Dhabi: luxury without limits",                description:"ОАЭ — страна роскоши и ярких впечатлений, где роскошь и комфорт ощущаются в каждой детали. Шикарные отели, знаменитые небоскрёбы и дизайнерские бутики создают атмосферу исключительного стиля. Здесь можно наслаждаться дорогими ресторанами, изысканной кухней и элитными развлечениями: сафари в пустыне, яхтенные прогулки. Отдых подходит и для всей семьи: множество роскошных отелей с потрясающим сервисом, просторными номерами и чистыми пляжами создают идеальные условия. ОАЭ — идеальное направление для тех, кто ценит роскошь, комфорт и впечатления премиум-класса.", description_en:"The United Arab Emirates is a place where luxury and comfort are felt in every detail. Stylish hotels, famous skyscrapers, and designer boutiques create an atmosphere of exceptional elegance. Here, you can enjoy fine restaurants, exquisite cuisine, and elite entertainment — from desert safaris to yacht trips on the Persian Gulf. A holiday in the UAE is also perfect for the whole family: many luxurious hotels with outstanding service, spacious rooms, and clean beaches create ideal conditions for traveling with children. The UAE is the perfect destination for those who value luxury, comfort, and premium-class experiences, where every moment becomes special.", description_lv:"Apvienotie Arābu Emīrati ir vieta, kur greznība un komforts jūtami katrā detaļā. Greznas viesnīcas, slaveni debesskrāpji un dizaineru veikali rada izsmalcināta stila atmosfēru. Šeit var baudīt augstas klases restorānus, izsmalcinātu virtuvi un elitāras izklaides — no safari tuksnesī līdz jahtu braucieniem Persijas līcī. Atpūta AAE ir piemērota arī visai ģimenei: daudzas greznas viesnīcas ar izcilu servisu, plašiem numuriem un tīrām pludmalēm rada ideālus apstākļus atpūtai ar bērniem. AAE ir ideāls galamērķis tiem, kas novērtē greznību, komfortu un premium klases iespaidus, kur katrs mirklis kļūst īpašs.", description_et:"Araabia Ühendemiraadid on paik, kus luksus ja mugavus on tuntavad igas detailis. Luksuslikud hotellid, kuulsad pilvelõhkujad ja disainerbutiigid loovad erakordse stiili atmosfääri. Siin saab nautida tipprestoranide kööki ja eliitmeelelahutust — kõrbesafarist kuni jahisõiduni Pjersia lahel. Puhkus AE-s sobib ka kogu perele: paljud luksushotellid suurepärase teeninduse, avarate tubade ja puhaste randadega loovad ideaalsed tingimused lastega reisimiseks. AE on ideaalne sihtkoht neile, kes hindavad luksust, mugavust ja premium-klassi elamusi, kus iga hetk muutub eriliseks.", image:"https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80&fm=webp", packages:[7] },
  { id:10, name:"Thailand",    country:"Thailand",    categories:["beach","exotic"],          brief:"Islands, Buddhist temples and vibrant street food",       description:"Таиланд — страна удивительных пляжей, тропических лагун и густых джунглей. Здесь каждый найдёт что-то для себя: уединённые бухты для спокойного отдыха, экскурсии по древним храмам и прогулки по оживлённым рынкам. Местная кухня порадует всех: свежие морепродукты, ароматные тайские блюда и экзотические фрукты. Отдыхать можно всей семьёй: мягкий климат, чистые пляжи и разнообразные развлечения создают комфортные условия для детей и взрослых. Таиланд — идеальное сочетание природы, культуры, моря и незабываемых впечатлений для всей семьи.", description_en:"Thailand is a country of amazing beaches, tropical lagoons, and lush jungles. Everyone can find something here: secluded bays for peaceful relaxation, excursions to ancient temples, and walks through lively markets. Local cuisine delights everyone: fresh seafood, fragrant Thai dishes, and exotic fruits. A holiday in Thailand is perfect for the whole family: a mild climate, clean beaches, and varied entertainment create comfortable conditions for children and adults. Thailand is the ideal combination of nature, culture, sea, and unforgettable experiences for the whole family!", description_lv:"Taizeme ir valsts ar brīnišķīgām pludmalēm, tropiskām lagūnām un sulīgiem džungļiem. Šeit ikviens atradīs ko sev: nomaļus līčus mierīgai atpūtai, ekskursijas uz seniem tempļiem un pastaigas pa dzīvīgiem tirgiem. Vietējā virtuve priecēs ikvienu: svaigas jūras veltes, aromātiski taju ēdieni un eksotiski augļi. Atpūta Taizemē ir piemērota visai ģimenei: maigs klimats, tīras pludmales un daudzveidīgas izklaides rada komfortablus apstākļus bērniem un pieaugušajiem. Taizeme ir ideāls dabas, kultūras, jūras un neaizmirstamu iespaidu apvienojums visai ģimenei!", description_et:"Tayland on hämmastutavate randade, troopiliste lagoonide ja tudevate džunglidega riik. Siin leiab igaüks midagi endale: eraldatavad lahed rahuliku puhkuse jaoks, ekskursioonid iidsetele templiga ja käigud elusat turul. Kohalik köök rõõmustab kõiki: värsked mereannid, aroomilined tai roogad ja eksootikaed marjad. Puhkus Tailandis on kogu perega ideaalne: pehme klimaat, puhas rannad ja erineva meelelahutusega loovad lastele ja täiskasvanule mugavad tingimused. Tayland on ideaalne looduse, kultuuri, mere ja unustamatute elamuste kombinatsioon kogu perele!", image:"https://images.unsplash.com/photo-1528181304800-259b08848526?w=800&q=80&fm=webp", packages:[5] },
  { id:11, name:"Maldives",    country:"Maldives",    categories:["beach","exotic"],          brief:"Turquoise lagoons and overwater villas",                 description:"Мальдивы — райские острова с бескрайними белоснежными пляжами, бирюзовыми лагунами и коралловыми рифами, создающими настоящую атмосферу тропического рая. Идеальное место для романтического отдыха: уединённые виллы на воде, закаты над океаном и роскошный сервис делают каждый момент незабываемым. Отдых подходит и для всей семьи: мягкий климат, чистые пляжи и разнообразные активности от снорклинга до прогулок на катамаране обеспечивают комфорт и безопасность для детей. Мальдивы — сочетание природы, уединения и роскоши, где каждый день дарит впечатления на всю жизнь.", description_en:"The Maldives offer endless white beaches, turquoise lagoons, and coral reefs that create the true atmosphere of a tropical paradise. It is an ideal place for a romantic holiday: secluded overwater villas, sunsets over the ocean, and luxurious service make every moment unforgettable. A holiday here is also suitable for the whole family: a mild climate, clean beaches, and a wide range of activities — from snorkeling to catamaran rides — provide comfort and safety for children. The Maldives are a blend of nature, privacy, and luxury, where every day brings memories for a lifetime.", description_lv:"Maldīvija piedāvā bezgalīgas baltas pludmales, tirkīzzilas lagūnas un koraļļu rifus, kas rada īstu tropu paradīzes sajūtu. Tā ir ideāla vieta romantiskai atpūtai: nomaļas villas virs ūdens, saulrieti virs okeāna un grezns serviss padara katru mirkli neaizmirstamu. Atpūta šeit ir piemērota arī visai ģimenei: maigs klimats, tīras pludmales un dažādas aktivitātes — no snorkelēšanas līdz braucieniem ar katamarānu — nodrošina komfortu un drošību bērniem. Maldīvija ir dabas, privātuma un greznības apvienojums, kur katra diena dāvā atmiņas uz visu mūžu.", description_et:"Maldiivid on paradiisesaarid lopumatute valgesärava randadega, türkiissetärvust laguunide ja koraalifidega, luues troopilise paradiisi tõelist õhustikku. See on ideaalne koht romantilistele puhkusele: isoleeritud veekohal asuvad villad, suurepärased päikeseloojangud ookeani kohal ja luksuslik teenus teevad iga hetkest unustamatu. Puhkus on sobiv ka kogu perele: pehme klimaat, puhas rand ja mitmekesised tegevused — ujumisest snorgeldamiseni ja katamaraan-sõitudeni — kindlustavad mugavuse ja ohutuse lastele. Maldiivid on looduse, privaatsuse ja luksuse kombinatsioon, kus iga päev toob mälestusi eluks aega.", image:"https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80&fm=webp", packages:[3] },
  { id:12, name:"Italy",       country:"Italy",       categories:["beach","family"],          brief:"Gastronomy, history and exclusive experiences",         description:"Италия — одно из наших любимых направлений! Живописные города, исторические улочки, знаменитые памятники и вкуснейшая кухня создают неповторимую атмосферу Средиземноморья. Особое место занимает озеро Комо — эксклюзивные туры, которых больше нет! Насладитесь прогулками на яхте, роскошными виллами и потрясающими видами. Навсегда запомнятся. Рим, Флоренция, Венеция, Тоскана — пляжи, винные маршруты, исторические города и гастрономические открытия для каждого. Италия — идеальное сочетание истории, гастрономии, природы и эксклюзивного отдыха для всей семьи и романтических поездок.", description_en:"Italy is one of our favorite destinations! Picturesque cities, historic streets, famous landmarks, and the most delicious cuisine create a unique Mediterranean atmosphere. Lake Como holds a special place — exclusive tours that you won't find anywhere else! Enjoy yacht cruises, luxurious villas, and stunning views that will remain in your memory forever. Rome, Florence, Venice, Tuscany — beaches, wine routes, historic cities, and gastronomic discoveries for everyone. Italy is the perfect combination of history, gastronomy, nature, and exclusive leisure for the whole family and romantic trips!", description_lv:"Itālija ir viena no mūsu mīļākajiem galamērķiem! Gleznaini miesti, vēsturiski šķēršļi, slaveni pieminekļi un vistīrākā virtuves rada unikālu Vidusjūras atmosfēru. Komersijas ezers ieņem īpašu vietu — eksklusīvi tūri, kurus jūs neatradīsiet nekur citur! Baudiet jahtu braucienus, izsmalcinus villus un pārsteidzošus skatus, kas jums paliks atmiņā mūžīgi. Roma, Florencija, Venecija, Toskāna — rannas, vīna maršrutus, vēsturiskos miestus un kulināriskus atklājumus visiem. Itālija ir vēstures, gastronomiķas, dabas un eksklusīvu brīvdienu līdzsvarota tūra visas ģimenes un romantiskiem ceļojumiem!", description_et:"Itaalia on üks meie lemmikutest sihtkohist! Maaliusad linnakesed, ajaloohilised tänavad, kuulsad mälestised ja kõige maitsvam toit loovad ainulaadse Vahemere atmosfääri. Komo järv omab erilist kohta — eksklusiivsed reisid, mida te kuskil mujal ei leia! Nautige jachtisõite, luksuslikke vilju ja vapustavaid vaateid, mis jäävad teile igaveseks meelde. Rooma, Firenze, Venezia, Toskana — rannad, viinamarjade rajad, ajaloolised linnad ja kulinaarsed avastused kõigile. Itaalia on ajaloo, gastronoomia, looduse ja eksklusiivsete briiode ideaalne kombinatsioon kogu perele ja romantilistele reisidele!", image:"https://images.unsplash.com/photo-1566392377107-430dacbbf56d?w=800&q=80&fm=webp", packages:[] },
  { id:13, name:"Uzbekistan", country:"Uzbekistan", categories:["exotic"],                  brief:"Ancient cities, Silk Route and vibrant impressions",   description:"Узбекистан — страна, где история оживает на каждом шагу. Самарканд, Бухара и Хива поражают архитектурой, яркими медресе и живописными базарами, где все ещё ощущается дух Великого Шёлкового пути. Мы предлагаем лучшие туры с опытными гидами и эксклюзивной экскурсионной программой для полного погружения в культуру и традиции. Местная кухня порадует ароматными блюдами, свежей выпечкой и сладостями, а гостеприимство местных жителей создаёт уникальную атмосферу. Отдых в Узбекистане — возможность совместить комфорт, историю и культуру, открывая настоящие сокровища Центральной Азии.", description_en:"Uzbekistan is a country where history comes alive at every turn. Samarkand, Bukhara, and Khiva impress with their architecture, vibrant madrasahs, and picturesque bazaars where the spirit of the Great Silk Road is still felt. We offer the best tours with experienced guides and an exclusive excursion program for complete immersion in culture and traditions. Local cuisine delights with aromatic dishes, fresh pastries, and sweets, while the hospitality of local residents creates a unique atmosphere. A holiday in Uzbekistan is an opportunity to combine comfort, history, and culture, discovering the true treasures of Central Asia.", description_lv:"Uzbekistāna ir valsts, kur vēsture dzīvo uz katra soļa. Samarkands, Buhāra un Hiva iespaidina ar arhitektūru, vidēja-laikmeta minaretliem un gleznaimen bazāriem, kur joprojām jūtams Lielā Zīda Ceļa gars. Mēs piedāvājam labākās tūres ar pieredzējušiem ceļvežiem un eksklusīvu ekskursiju programmu pilnīgai iegrimšanai kultūrā un tradīcijās. Vietējā virtuuve priecēs ar aromatiskiem ēdienim, svalgiem maizes izstrādājumiem un saldumiem, kamēr vietējo iedzīvotāju viesmīlības rada unikālu atmosfēru. Atpūta Uzbekistānā ir iespēja apvienot komfortu, vēsturi un kultūru, atklājot patiesi brīnumu Vidusāzijā.", description_et:"Uzbekistan on riik, kus ajalugu elab igal sammul. Samarkand, Bukhara ja Khiva võluvad arhitektuuri, vibratsiooniliste medreslitega ja ohutuvaim ja maiusale, kus endiselt tunneb Suure Siiditeede vaimu. Pakume parimaid reise kogenud juhendajate ja eksklusiivsete ekskursioonikavanditega täieliku sukeldumise jaoks kultuuri ja traditsioone. Kohalik köök rõõmustab aromaalaste roogade, tuorete leivaste ja maiusate üle, kusjuures kohalike elanike külalislahkus loob ainulaadse õhustiku. Puhkus Uzbekis on võimalus ühendada mugavus, ajalugu ja kultuuri, avastades päris aardeid Kesk-Aasias.", image:"https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80&fm=webp", packages:[] },
  { id:14, name:"Japan",       country:"Japan",       categories:["exotic"],                  brief:"Traditions, futurism and unforgettable impressions",   description:"Япония — страна, где современность встречается с тысячелетними традициями. От ярких улиц Токио и исторических храмов Киото до живописной природы Фудзи и сакуры весной — каждый день полон открытиями. Мы предлагаем эксклюзивные туры с лучшими гидами и тщательно продуманной программой для полного погружения в культуру, историю и современную жизнь страны. Местная кухня поражает разнообразием: суши, рамен, свежие морепродукты и сладости, которые впечатляют даже самых искушённых гурманов. Отдыхать удобно всей семьёй: безопасные города, парки развлечений и культурные экскурсии создают комфортные условия. Япония — уникальное сочетание истории, современных технологий и природы, где каждое путешествие становится незабываемым.", description_en:"Japan is a country where modernity meets millennia-old traditions. From the bright streets of Tokyo and historic temples of Kyoto to the scenic beauty of Mount Fuji and cherry blossoms in spring — every day is full of discoveries. We offer exclusive tours with the best guides and a carefully planned program for complete immersion in the culture, history, and contemporary life of the country. Local cuisine impresses with its diversity: sushi, ramen, fresh seafood, and sweets that impress even the most discerning gourmands. It is convenient to relax with the whole family: safe cities, amusement parks, and cultural excursions create comfortable conditions. Japan is a unique combination of history, modern technology, and nature, where every journey becomes unforgettable.", description_lv:"Japāna ir valsts, kur modernizme satiekas ar gadu tūkstoši senas tradīcijas. No spilgtajiem Tōkijas ielām un vēsturiskajiem Kioto tempļiem līdz Fudži kalna maaliusajai dabai un pavasarīgiem ziedu kviešiem — katra diena ir pilna atklājumu. Piedāvājam eksklusīvus tūrus ar labākajiem ceļvežiem un rūpīgi izstrādātu programmu pilnīgai iegrimšanai kultūrā, vēsturē un valsts mūsdienu dzīvē. Vietējā virtuves pārsteidz ar daudzveidību: sushi, ramens, svaigas jūras veltes un saldumi, kas impresionu ar visvairāk diskriminējošiem vēlējumiem. Ir ērti atpūsties ar visu ģimeni: draudzīgi miesti, atrakciju parki un kultūras ekskursijas rada komfortablus apstākļus. Japāna ir vēstures, modernu tehnoloģiju un dabas ainvu kombinācija, kur katra brīvdiena kļūst neaizmirstama.", description_et:"Jaapan on riik, kus modernne kohtub tuhanded aastaid tagasi traditsioonidega. Tokio heledatest tänavastest ja Kyoto ajaloolistest templitest kuni Fuji mäe maaliusate vaateni ja kevadise kirsiõitsuseni — iga päev on täis avastusi. Pakkume eksklusiivseid reise parimade juhendajatega ja hoolikalt kavandatud programmiga täieliku sukeldumisega riigi kultuuri, ajaloo ja kaasaegses elus. Kohalik köök hämmastab mitmekesisusega: sushi, ramen, värsked mereannid ja magustused, mis impresioonivad isegi kõige nõudlikumate maitsearstide poolt. Mugav on puhata terve perega: ohutud linnakesed, lõbustuspargi ja kultuurilised ekskursioonid loovad mugavad tingimused. Jaapan on ajaloo, modernsed tehnoloogia ja looduse aineline kombinatsioon, kus iga reisimata muutub unustamatu.", image:"https://images.unsplash.com/photo-1540959375944-7049f642e9bc?w=800&q=80&fm=webp", packages:[] },
  { id:15, name:"Georgia",     country:"Georgia",     categories:["exotic"],                  brief:"Mountains, vineyards and oriental flair",               description:"Грузия — страна могучих Кавказских гор, живописных долин и бескрайних виноградников. Старинные города и крепости соседствуют с уютными улочками и яркими рынками. Мы предлагаем эксклюзивные туры с лучшими гидами и тщательно продуманной экскурсионной программой — на рынке таких же нет. Отдых становится максимально насыщенным, комфортным и безопасным. Местная кухня впечатляет вкусом: хачапури, хинкали, свежие продукты и ароматные специи подарят гастрономическое удовольствие. Грузия — идеальное сочетание природы, культуры и истории, где каждый день приносит новые впечатления.", description_en:"Georgia is a country of mighty Caucasus Mountains, picturesque valleys, and endless vineyards. Ancient cities and fortresses stand alongside cozy streets and vibrant markets. We offer exclusive tours with the best guides and a carefully planned excursion program — you won't find anything like it on the market. Your holiday becomes maximally rich, comfortable, and safe. Local cuisine impresses with its taste: khachapuri, khinkali, fresh produce, and aromatic spices will delight your palate and provide true gastronomic pleasure. Georgia is a perfect combination of nature, culture, and history, where every day brings new impressions.", description_lv:"Gruzija ir valsts ar varenas Kaukāza kalniem, gleznainām ielejām un bezgalīgiem vīna dārziem. Senie miesti un cietokšņi atrodas blakus mājīgām ielitļām un biedriem tirgiem. Piedāvājam eksklusīvus tūrus ar labākajiem ceļvežiem un rūpīgi izstrādātu ekskursiju programmu — tā nebūs ne meklēt tirgū. Jūsu brīvdiena kļūst maksimāli bagāta, komfortabla un draugi. Vietējā kulinārija impresionu ar smaržu: khachapuri, khinkali, svaigi produkti un aromātiski garšvielas dāvinātu patīkamu un patiesu gastronomisko baudu. Gruzija ir ideāla dabas, kultūras un vēstures kombinācija, kurā katra diena nes jaunus iespaidus.", description_et:"Gruusia on riik võirde Kaukaasus'e mäedate, maaliusate orudega ja lõpmatute viinamarjade. Muistsed linnakesed ja kindlused seisavad kõrvuti armsate tänavasaega ja elava bazar. Me pakkume eksklusiivne reise parimaidja juhendade ja hoolika kavandatu ekskursiooni programmiga --- midagi sellist turul ei leita. Teie puhkus muutub maksimaalselt rikkalikuks, mugavaks ja turvaliseks. Kohalik köök impoonib maitsega: khachapuri, khinkali, värsked toorained ja aroomi maisused annavad tõelise kulinaraariline naudingu. Gruusia on ideaalne looduse, kultuuri ja ajaloo kombinatsioon, kus kõi päev toob uusi iespaideid.", image:"https://images.unsplash.com/photo-1555739814-e89e442dd00f?w=800&q=80&fm=webp", packages:[] },
];

let RUNTIME_DESTINATIONS = DESTINATIONS;
function setRuntimeDestinations(list) { RUNTIME_DESTINATIONS = list; }
function getDestinations() { return RUNTIME_DESTINATIONS; }

function normalizeDestination(raw, packages) {
  if (!raw || typeof raw !== 'object') return null;
  const id = Number(raw.id);
  const categories = Array.isArray(raw.categories)
    ? raw.categories
    : (raw.categories || '').toString().split(',').map(c => c.trim()).filter(Boolean);
  const image = raw.image || raw.heroImage || '';
  const relatedPackages = Array.isArray(raw.packages)
    ? raw.packages
    : packages.filter(p => p.destinationId === id).map(p => p.id);

  return {
    id,
    name: raw.name_ru || raw.name_en || raw.name || '',
    name_ru: raw.name_ru || '',
    name_et: raw.name_et || '',
    name_lv: raw.name_lv || '',
    name_en: raw.name_en || '',
    region_ru: raw.region_ru || '',
    region_et: raw.region_et || '',
    region_lv: raw.region_lv || '',
    region_en: raw.region_en || '',
    brief: raw.brief_ru || raw.brief_en || raw.brief || '',
    brief_ru: raw.brief_ru || '',
    brief_et: raw.brief_et || '',
    brief_lv: raw.brief_lv || '',
    brief_en: raw.brief_en || '',
    description: raw.description_ru || raw.description || '',
    description_ru: raw.description_ru || '',
    description_et: raw.description_et || '',
    description_lv: raw.description_lv || '',
    description_en: raw.description_en || '',
    country: raw.country || '',
    categories,
    image,
    packages: relatedPackages
  };
}

// ═══ PACKAGES i18n ═══
const PKG_I18N = {
  1: {n:{ru:"Островная Греция",et:"Kreeka saarehüpped",lv:"Grieķijas salu ceļojums",en:"Greece Island Hopping"},b:{ru:"10 ночей: Афины → Миконос → Санторини",et:"10 ööd: Ateena → Mykonos → Santorini",lv:"10 naktis: Atēnas → Mikonoss → Santorini",en:"10 nights: Athens → Mykonos → Santorini"},desc:{ru:"День 1: Афины, экскурсия в Акрополь.\nДни 2-4: Миконос — пляжи и старый город.\nДень 5: Паром на Санторини.\nДни 6-9: Ойя, кальдера, дегустация вин.\nДень 10: Фира, круиз на закате.\nДень 11: Вылет.",et:"Päev 1: Ateena, Akropolise ekskursioon.\nPäevad 2-4: Mykonos — rannad ja vanalinn.\nPäev 5: Parvlaev Santorinile.\nPäevad 6-9: Oia, caldera, veinidegu.\nPäev 10: Fira, päikeseloojangukruiis.\nPäev 11: Väljasõit.",lv:"1. diena: Atēnas, Akropoles ekskursija.\n2.-4. diena: Mikonoss — pludmales un vecpilsēta.\n5. diena: Prāmis uz Santorini.\n6.-9. diena: Oja, kaldera, vīna degustācija.\n10. diena: Fira, saulrieta kruīzs.\n11. diena: Izlidošana.",en:"Day 1: Athens, Acropolis tour.\nDays 2-4: Mykonos — beaches & old town.\nDay 5: Ferry to Santorini.\nDays 6-9: Oia, caldera, wine tasting.\nDay 10: Fira, sunset cruise.\nDay 11: Departure."},incl:{ru:["Отели 4*","Завтраки","Трансферы","Билеты на паром","Экскурсии с гидом"],et:["4* hotellid","Hommikusöök","Transferid","Praampiletid","Giidiekskursioonid"],lv:["4* viesnīcas","Brokastis","Transferi","Prāmja biļetes","Gida ekskursijas"],en:["4* hotels","Daily breakfast","Transfers","Ferry tickets","Guided excursions"]},notIncl:{ru:["Авиабилеты","Страховка","Личные расходы"],et:["Lennupiletid","Kindlustus","Isiklikud kulud"],lv:["Aviobiļetes","Apdrošināšana","Personīgie izdevumi"],en:["Flights","Insurance","Personal expenses"]}},
  2: {n:{ru:"Романтика Санторини",et:"Santorini romanss",lv:"Santorini romance",en:"Santorini Romance"},b:{ru:"7 ночей в бутик-отеле на краю кальдеры с приватным ужином на закате",et:"7 ööd butiikhkhkkkkkk kaldeeraäärel koos privaatse õhtusöögiga",lv:"7 naktis butikas viesnīcā pie kalderas ar privātu vakariņām saulrietā",en:"7 nights in a boutique caldera-edge hotel with a private sunset dinner"},desc:{ru:"День 1: Прибытие, бутик-отель в Ойе, приветственный коктейль.\nДень 2: Приватная экскурсия с фотографом.\nДень 3: Яхтенный круиз вокруг кальдеры.\nДень 4: Свободный день — пляж Перисса.\nДень 5: Дегустация вин на винодельне Santo.\nДень 6: Экскурсия в Фиру и Пиргос.\nДень 7: Приватный ужин на закате на террасе кальдеры.\nДень 8: Вылет.",et:"Päev 1: Saabumine, butiikhotell Oias, tervituskokteil.\nPäev 2: Privaatne giidiga tuur fotograafiga.\nPäev 3: Jahtikruiis caldera ümber.\nPäev 4: Vaba päev — Perissa rand.\nPäev 5: Veinidegu Santo viinakojas.\nPäev 6: Ekskursioon Firasse ja Pyrgosesse.\nPäev 7: Privaatne õhtusöök päikeseloojangul caldera terrassil.\nPäev 8: Väljasõit.",lv:"1. diena: Ierašanās, butika viesnīca Ojā, sveiciena kokteilis.\n2. diena: Privāta ekskursija ar fotogrāfu.\n3. diena: Jahtas kruīzs ap kalderu.\n4. diena: Brīva diena — Perisas pludmale.\n5. diena: Vīna degustācija Santo vīna dārzā.\n6. diena: Ekskursija uz Firu un Pirgos.\n7. diena: Privātas vakariņas saulrietā uz kalderas terases.\n8. diena: Izlidošana.",en:"Day 1: Arrival, boutique hotel in Oia, welcome cocktail.\nDay 2: Private guided tour with photographer.\nDay 3: Yacht cruise around the caldera.\nDay 4: Free day — Perissa beach.\nDay 5: Wine tasting at Santo vineyards.\nDay 6: Excursion to Fira and Pyrgos.\nDay 7: Private sunset dinner on caldera terrace.\nDay 8: Departure."},incl:{ru:["Бутик-отель 5*","Завтраки","Трансферы из аэропорта","Яхтенный тур","Дегустация вин","Приватный ужин","Личный фотограф"],et:["5* butiikhotell","Hommikusöök","Lennujaamatransferid","Jahtituur","Veinidegu","Privaatne õhtusöök","Isiklik fotograaf"],lv:["5* butika viesnīca","Brokastis","Lidostas transferi","Jahtas tūre","Vīna degustācija","Privātas vakariņas","Personīgais fotogrāfs"],en:["5* boutique hotel","Daily breakfast","Airport transfers","Yacht tour","Wine tasting","Private dinner","Personal photographer"]},notIncl:{ru:["Авиабилеты","Страховка","Личные расходы"],et:["Lennupiletid","Kindlustus","Isiklikud kulud"],lv:["Aviobiļetes","Apdrošināšana","Personīgie izdevumi"],en:["Flights","Insurance","Personal expenses"]}},
  3: {n:{ru:"Мальдивы Райские: Вилла на воде",et:"Maldiivid Paradiis: Veevilla",lv:"Maldīvija Paradīze: Villa uz ūdens",en:"Maldives Paradise: Overwater Villa"},b:{ru:"7 ночей в вилле на воде с приватным бассейном",et:"7 ööd veevillаs privaatse lõputu basseiniga",lv:"7 naktis villā uz ūdens ar privātu bezgalīgu baseinu",en:"7 nights in an overwater villa with private infinity pool"},desc:{ru:"День 1: Прибытие на гидросамолёте, заселение в виллу.\nДни 2-3: Дайвинг и снорклинг со скатами манта.\nДень 4: Полный спа-день.\nДень 5: Рыбалка на закате + ужин на пляже.\nДни 6-7: Свободные дни.\nДень 8: Вылет.",et:"Päev 1: Hüdroplaaniga saabumine, villasse sisseregistreerimine.\nPäevad 2-3: Sukeldumine ja snorgeldamine mantadega.\nPäev 4: Täielik spaa-päev.\nPäev 5: Päikeseloojangul kalapüük + õhtusöök rannas.\nPäevad 6-7: Vabad päevad.\nPäev 8: Väljasõit.",lv:"1. diena: Ierašanās ar hidroplānu, reģistrācija villā.\n2.-3. diena: Niršana un snorkelēšana ar manta rejām.\n4. diena: Pilna SPA diena.\n5. diena: Makšķerēšana saulrietā + vakariņas pludmalē.\n6.-7. diena: Brīvas dienas.\n8. diena: Izlidošana.",en:"Day 1: Seaplane arrival, overwater villa check-in.\nDays 2-3: Diving and snorkelling with manta rays.\nDay 4: Full spa day.\nDay 5: Sunset fishing + beach dinner.\nDays 6-7: Free days.\nDay 8: Departure."},incl:{ru:["Вилла на воде с бассейном","Полный пансион","Трансфер на гидросамолёте","2 сеанса дайвинга","Спа-процедуры","Рыбалка + ужин на пляже"],et:["Veevilla basseiniga","Täispension","Hüdroplaani transfer","2 sukeldumisseansi","Spaa-protseduurid","Kalapüük + õhtusöök rannas"],lv:["Villa uz ūdens ar baseinu","Pilna pansija","Hidroplāna transferis","2 niršanas sesijas","SPA procedūras","Makšķerēšana + vakariņas pludmalē"],en:["Overwater villa with pool","Full board","Seaplane transfer","2 dive sessions","Spa treatments","Fishing + beach dinner"]},notIncl:{ru:["Международные авиабилеты","Страховка","Премиум-алкоголь"],et:["Rahvusvahelised lennupiletid","Kindlustus","Premium-alkohol"],lv:["Starptautiskie avioreisi","Apdrošināšana","Premium alkohols"],en:["International flights","Insurance","Premium alcohol"]}},
  4: {n:{ru:"Турция All-Inclusive",et:"Türgi kõik hinnas",lv:"Turcija viss iekļauts",en:"Turkey All-Inclusive"},b:{ru:"7 ночей все включено на Эгейском побережье",et:"7 ööd kõik hinnas Egeuse rannikul",lv:"7 naktis viss iekļauts Egejas piekrastē",en:"7 nights all-inclusive on the Aegean coast"},desc:{ru:"День 1: Прибытие, заселение в отель, пляж.\nДни 2-6: All-inclusive курорт: бассейны, пляж, развлечения.\nДень 7: Опциональная экскурсия в Эфес или джип-сафари.\nДень 8: Вылет.",et:"Päev 1: Saabumine, hotelli sisseregistreerimine, rand.\nPäevad 2-6: All-inclusive kuurort: basseini, rand, meelelahutus.\nPäev 7: Valikuline ekskursioon Ephesusesse või džiip-safari.\nPäev 8: Väljasõit.",lv:"1. diena: Ierašanās, reģistrācija viesnīcā, pludmale.\n2.-6. diena: Viss iekļauts kūrorts: baseini, pludmale, izklaides.\n7. diena: Izvēles ekskursija uz Efesu vai džipu safari.\n8. diena: Izlidošana.",en:"Day 1: Arrival, hotel check-in, beach.\nDays 2-6: All-inclusive resort: pools, beach, entertainment.\nDay 7: Optional excursion to Ephesus or jeep safari.\nDay 8: Departure."},incl:{ru:["Отель 5* все включено","Все блюда и напитки","Трансферы из аэропорта","Анимационная программа"],et:["5* kõik hinnas hotell","Kõik toidud ja joogid","Lennujaamatransferid","Animatsiooniprogramm"],lv:["5* viesnīca viss iekļauts","Visi ēdieni un dzērieni","Lidostas transferi","Animācijas programma"],en:["5* all-inclusive hotel","All meals & drinks","Airport transfers","Animation programme"]},notIncl:{ru:["Авиабилеты","Страховка","Опциональные экскурсии"],et:["Lennupiletid","Kindlustus","Valikulised ekskursioonid"],lv:["Aviobiļetes","Apdrošināšana","Izvēles ekskursijas"],en:["Flights","Insurance","Optional excursions"]}},
  5: {n:{ru:"Таиланд: Острова и Бангкок",et:"Tayland: Saared ja Bangkok",lv:"Taizeme: Salas un Bangkoka",en:"Thailand: Islands & Bangkok"},b:{ru:"10 ночей: Бангкок → Чиангмай → Ко Самуи",et:"10 ööd: Bangkok → Chiang Mai → Koh Samui",lv:"10 naktis: Bangkoka → Čiangmaja → Kosamui",en:"10 nights: Bangkok → Chiang Mai → Koh Samui"},desc:{ru:"Дни 1-3: Бангкок — храмы, рынки, бары на крыше.\nДни 4-5: Чиангмай — треккинг в джунглях, ночной базар.\nДень 6: Перелёт на Ко Самуи.\nДни 7-10: Пляж, прогулка на лодке к Ко Пханган.\nДень 11: Вылет.",et:"Päevad 1-3: Bangkok — templid, turud, katusebaarid.\nPäevad 2-5: Chiang Mai — matkamine džunglis, ööbazar.\nPäev 6: Lend Koh Samuile.\nPäevad 7-10: Rand, paadisõit Koh Phangan.\nPäev 11: Väljasõit.",lv:"1.-3. diena: Bangkoka — tempļi, tirgi, bāri uz jumta.\n4.-5. diena: Čiangmaja — pārgājieni džungļos, nakts bazārs.\n6. diena: Lidojums uz Kosamui.\n7.-10. diena: Pludmale, laivu brauciens uz Kopanganu.\n11. diena: Izlidošana.",en:"Days 1-3: Bangkok — temples, markets, rooftop bars.\nDays 4-5: Chiang Mai — jungle trekking, night bazaar.\nDay 6: Flight to Koh Samui.\nDays 7-10: Beach, boat trip to Koh Phangan.\nDay 11: Departure."},incl:{ru:["Отели","Завтраки","Внутренний перелёт","Трансферы","Треккинг в джунглях"],et:["Hotellid","Hommikusöök","Sisemine lend","Transferid","Džunglimatkamine"],lv:["Viesnīcas","Brokastis","Iekšzemes lidojums","Transferi","Džungļu pārgājieni"],en:["Hotels","Breakfast","Domestic flight","Transfers","Jungle trek"]},notIncl:{ru:["Международные авиабилеты","Страховка","Виза"],et:["Rahvusvahelised lennupiletid","Kindlustus","Viisa"],lv:["Starptautiskie avioreisi","Apdrošināšana","Vīza"],en:["International flights","Insurance","Visa"]}},
  7: {n:{ru:"Дубай и Абу-Даби Люкс",et:"Dubai ja Abu Dhabi luksus",lv:"Dubaija un Abū Dabī luksuss",en:"Dubai & Abu Dhabi Luxury"},b:{ru:"7 ночей: Бурдж-Халифа, сафари в пустыне и Лувр Абу-Даби",et:"7 ööd: Burj Khalifa, kõrbesafari ja Louvre Abu Dhabi",lv:"7 naktis: Burdžhalifa, tuksneša safari un Luvra Abū Dabī",en:"7 nights: Burj Khalifa, desert safari & Louvre Abu Dhabi"},desc:{ru:"Дни 1-4: Дубай — Бурдж-Халифа, Молл Эмиратов, сафари в пустыне, ужин в Марине.\nДень 5: Экскурсия в Абу-Даби на день.\nДень 6: Палм Джумейра, Атлантис, Аквавенчер.\nДень 7: Шопинг и спа.\nДень 8: Вылет.",et:"Päevad 1-4: Dubai — Burj Khalifa, Mall of Emirates, kõrbesafari, õhtusöök Marinas.\nPäev 5: Päevaekskursioon Abu Dhabisse.\nPäev 6: Palm Jumeirah, Atlantis, Aquaventure.\nPäev 7: Shopping ja spaa.\nPäev 8: Väljasõit.",lv:"1.-4. diena: Dubaija — Burdžhalifa, Emirātu tirdzniecības centrs, tuksneša safari, vakariņas Marinā.\n5. diena: Dienas ekskursija uz Abū Dabī.\n6. diena: Palmu Džumeira, Atlantis, Akvavenčers.\n7. diena: Iepirkšanās un SPA.\n8. diena: Izlidošana.",en:"Days 1-4: Dubai — Burj Khalifa, Mall of Emirates, desert safari, Marina dinner.\nDay 5: Day trip to Abu Dhabi.\nDay 6: Palm Jumeirah, Atlantis, Aquaventure.\nDay 7: Shopping & spa.\nDay 8: Departure."},incl:{ru:["Отель 5*","Завтраки","Трансферы","Сафари в пустыне","Экскурсия в Абу-Даби","Билет на Бурдж-Халифа"],et:["5* hotell","Hommikusöök","Transferid","Kõrbesafari","Abu Dhabi ekskursioon","Burj Khalifa pilet"],lv:["5* viesnīca","Brokastis","Transferi","Tuksneša safari","Abū Dabī ekskursija","Burdžhalifas biļete"],en:["5* hotel","Breakfast","Transfers","Desert safari","Abu Dhabi excursion","Burj Khalifa entry"]},notIncl:{ru:["Международные авиабилеты","Страховка","Обеды/ужины"],et:["Rahvusvahelised lennupiletid","Kindlustus","Lõuna/õhtusöök"],lv:["Starptautiskie avioreisi","Apdrošināšana","Pusdienas/vakariņas"],en:["International flights","Insurance","Lunches/dinners"]}}
};
function pName(id,lang){const p=PKG_I18N[id];if(p)return p.n[lang]||p.n.en;const rp=getPackages().find(x=>x.id===id);return rp?(rp[`name_${lang}`]||rp.name_en||rp.name_ru||''):''}
function pBrief(id,lang){const p=PKG_I18N[id];if(p)return p.b[lang]||p.b.en;const rp=getPackages().find(x=>x.id===id);return rp?(rp[`brief_${lang}`]||rp.brief_en||rp.brief_ru||''):''}
function pDescription(id,lang){const p=PKG_I18N[id];if(p)return p.desc[lang]||p.desc.en;const rp=getPackages().find(x=>x.id===id);return rp?(rp[`description_${lang}`]||rp.description_en||rp.description_ru||''):''}
function pIncluded(id,lang){const p=PKG_I18N[id];if(p)return p.incl[lang]||p.incl.en;const rp=getPackages().find(x=>x.id===id);const raw=rp?(rp[`included_${lang}`]||rp.included_en||rp.included_ru||''):'';return raw?raw.split(',').map(s=>s.trim()).filter(Boolean):[];}
function pNotIncluded(id,lang){const p=PKG_I18N[id];if(p)return p.notIncl[lang]||p.notIncl.en;const rp=getPackages().find(x=>x.id===id);const raw=rp?(rp[`notIncluded_${lang}`]||rp.notIncluded_en||rp.notIncluded_ru||''):'';return raw?raw.split(',').map(s=>s.trim()).filter(Boolean):[];}
function pAdditionalServices(id,lang){const rp=getPackages().find(x=>x.id===id);if(rp){const v=rp[`additionalServices_${lang}`]||rp.additionalServices_en||rp.additionalServices_ru||'';if(v)return v;}return '';}
function pDuration(pkg,lang){return pkg[`duration_${lang}`]||pkg.duration||'';}
function pSeason(pkg,lang){return pkg[`season_${lang}`]||pkg.season||'';}

const PACKAGES = [
  { id: 1, destinationId: 3,  type: "Tour Package", duration: "11 days / 10 nights", season: "April — October", format: "Family", priceMode: "from", price: 2900, currency: "€", status: "active" },
  { id: 2, destinationId: 3,  type: "Tour Package", duration: "8 days / 7 nights", season: "April — October", format: "Romance", priceMode: "from", price: 3200, currency: "€", status: "active" },
  { id: 3, destinationId: 11, type: "Tour Package", duration: "8 days / 7 nights", season: "November — April", format: "Luxury", priceMode: "from", price: 8500, currency: "€", status: "active" },
  { id: 4, destinationId: 1,  type: "Tour Package", duration: "8 days / 7 nights", season: "May — October", format: "Family", priceMode: "from", price: 1200, currency: "€", status: "active" },
  { id: 5, destinationId: 10, type: "Tour Package", duration: "11 days / 10 nights", season: "November — April", format: "Adventure", priceMode: "from", price: 2400, currency: "€", status: "active" },
  { id: 7, destinationId: 9,  type: "Tour Package", duration: "8 days / 7 nights", season: "October — April", format: "Luxury", priceMode: "from", price: 3800, currency: "€", status: "active" },
];
let RUNTIME_PACKAGES = PACKAGES;
function setRuntimePackages(list) { RUNTIME_PACKAGES = list; }
function getPackages() { return RUNTIME_PACKAGES; }

let RUNTIME_HOTELS = [];
function setRuntimeHotels(list) { RUNTIME_HOTELS = list; }
function getHotels() { return RUNTIME_HOTELS; }
function hName(h, lang) { return h[`name_${lang}`] || h.name_en || h.name_ru || ''; }
function hBrief(h, lang) { return h[`brief_${lang}`] || h.brief_en || h.brief_ru || ''; }
function hDescription(h, lang) { return h[`description_${lang}`] || h.description_en || h.description_ru || ''; }
function hRoomType(h, lang) { return h[`roomType_${lang}`] || h.roomType_en || h.roomType_ru || ''; }
function hMealType(h, lang) { return h[`mealType_${lang}`] || h.mealType_en || h.mealType_ru || ''; }
function hPeopleCount(h) { return Number(h.peopleCount) || 0; }
function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}
function hDateRange(h) {
  if (h.availableFrom && h.availableTo) return `${fmtDate(h.availableFrom)} – ${fmtDate(h.availableTo)}`;
  return fmtDate(h.availableFrom) || fmtDate(h.availableTo) || '';
}
function mediaUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/uploads') && /^https?:\/\//i.test(API_BASE)) {
    return `${new URL(API_BASE).origin}${url}`;
  }
  return url;
}
function hPhotos(h) {
  const urls = h.images?.length ? h.images : h.image ? [h.image] : [];
  const captions = Array.isArray(h.imageCaptions) ? h.imageCaptions : [];
  return urls
    .map((url, index) => ({
      url,
      src: mediaUrl(url),
      caption: captions[index] || ''
    }))
    .filter(photo => photo.src);
}
function pPhotos(pkg) {
  const urls = pkg.images?.length ? pkg.images : pkg.image ? [pkg.image] : [];
  const captions = Array.isArray(pkg.imageCaptions) ? pkg.imageCaptions : [];
  return urls
    .map((url, index) => ({
      url,
      src: mediaUrl(url),
      caption: captions[index] || ''
    }))
    .filter(photo => photo.src);
}
function pRoomType(pkg, lang) { return pkg[`roomType_${lang}`] || pkg.roomType_en || pkg.roomType_ru || ''; }
function pMealType(pkg, lang) { return pkg[`mealType_${lang}`] || pkg.mealType_en || pkg.mealType_ru || ''; }
function pPriceCaption(pkg, lang) {
  if (pkg.priceMode === 'total') return t('l_total_price', lang);
  if (pkg.priceMode === 'perPerson') return t('l_pp', lang);
  return t('l_from_pp', lang);
}
function pDateRange(pkg) {
  if (!pkg.availableFrom && !pkg.availableTo) return '';
  if (pkg.availableFrom && pkg.availableTo) return `${fmtDate(pkg.availableFrom)} – ${fmtDate(pkg.availableTo)}`;
  return fmtDate(pkg.availableFrom) || fmtDate(pkg.availableTo);
}
const SVC_I18N = {
  1:{
    n:{ru:"Индивидуальные маршруты и авторские туры",et:"Individuaalsed marsruudid ja autorireisid",lv:"Individuāli maršruti un autoru tūres",en:"Individual Itineraries & Signature Tours"},
    d:{ru:"Маршруты с нуля под ваши желания — без шаблонов и компромиссов",et:"Marsruudid nullist teie soovide järgi — ilma mallideta",lv:"Maršruti no nulles pēc jūsu vēlmēm — bez šabloniem",en:"Journeys crafted from scratch — no templates, no compromises"},
    body:{
      ru:[
        {t:'p',v:'Мы создаём индивидуальные путешествия собственной разработки — это не стандартные турпакеты, а полностью продуманные маршруты, собранные нашей командой под конкретного клиента.'},
        {t:'p',v:'Каждый тур разрабатывается персонально: от идеи и концепции до мельчайших деталей пребывания. Мы подбираем лучшие отели, организуем частные трансферы, обеспечиваем комфортные перелёты и составляем программу с учётом ваших интересов, темпа отдыха и уровня сервиса.'},
        {t:'p',v:'Мы сотрудничаем только с проверенными и высококвалифицированными гидами и экскурсоводами, которые обеспечивают не просто экскурсию, а глубокое погружение в культуру и атмосферу страны. Наши маршруты включают эксклюзивные локации, нестандартные впечатления и доступ к уникальным форматам отдыха.'},
        {t:'p',v:'Полный контроль качества, внимание к деталям и круглосуточная поддержка 24/7 — обязательная часть каждого путешествия.'},
        {t:'p',v:'Индивидуальный тур от нас — это:'},
        {t:'list',v:['персонально разработанный маршрут','лучшие гиды и частные экскурсии','эксклюзивные локации и впечатления','высокий уровень сервиса','полное сопровождение на всех этапах']},
        {t:'p',v:'Мы создаём путешествия, которые невозможно повторить по шаблону.'},
      ],
      lv:[
        {t:'p',v:'Mēs veidojam individuālus ceļojumus, ko izstrādā mūsu komanda — tās nav standarta tūrisma paketes, bet pilnībā pārdomāti maršruti konkrētam klientam.'},
        {t:'p',v:'Katrs ceļojums tiek veidots personīgi: no idejas un koncepcijas līdz vissīkākajām detaļām. Mēs izvēlamies labākās viesnīcas, organizējam privātus transfērus, nodrošinām komfortablus lidojumus un sastādām programmu, ņemot vērā jūsu intereses, atpūtas tempu un vēlamo servisa līmeni.'},
        {t:'p',v:'Mēs sadarbojamies tikai ar pārbaudītiem un augsti kvalificētiem gidiem un ekskursiju vadītājiem, kuri nodrošina nevis tikai ekskursiju, bet dziļu iepazīšanos ar valsts kultūru un atmosfēru. Mūsu maršruti ietver ekskluzīvas vietas, nestandarta iespaidus un piekļuvi unikāliem atpūtas formātiem.'},
        {t:'p',v:'Pilna kvalitātes kontrole, uzmanība detaļām un atbalsts 24/7 ir obligāta katra ceļojuma sastāvdaļa.'},
        {t:'p',v:'Individuāls ceļojums pie mums ir:'},
        {t:'list',v:['personīgi izstrādāts maršruts','labākie gidi un privātas ekskursijas','ekskluzīvas vietas un iespaidi','augsts servisa līmenis','pilns atbalsts visos posmos']},
        {t:'p',v:'Mēs veidojam ceļojumus, kurus nav iespējams atkārtot pēc šablona.'},
      ],
      et:[
        {t:'p',v:'Loome oma meeskonna poolt välja töötatud individuaalseid reise — need ei ole standardsed paketid, vaid täielikult läbimõeldud marsruudid konkreetsele kliendile.'},
        {t:'p',v:'Iga reis koostatakse personaalselt: alates ideest ja kontseptsioonist kuni pisimate detailideni. Valime parimad hotellid, korraldame privaatsed transfeerid, tagame mugavad lennud ning koostame programmi vastavalt teie huvidele, puhkuse tempole ja soovitud teenindustasemele.'},
        {t:'p',v:'Teeme koostööd ainult usaldusväärsete ja kõrgelt kvalifitseeritud giidide ning ekskursioonijuhtidega, kes pakuvad mitte lihtsalt ekskursiooni, vaid sügavat sukeldumist sihtriigi kultuuri ja atmosfääri. Meie marsruudid sisaldavad eksklusiivseid kohti, eripäraseid elamusi ja ligipääsu unikaalsetele puhkusevormidele.'},
        {t:'p',v:'Kvaliteedikontroll, tähelepanu detailidele ja ööpäevaringne tugi 24/7 on iga reisi lahutamatu osa.'},
        {t:'p',v:'Individuaalne reis meiega tähendab:'},
        {t:'list',v:['personaalselt loodud marsruuti','parimaid giide ja privaatseid ekskursioone','eksklusiivseid kohti ja elamusi','kõrget teenindustaset','täielikku tuge kõikides etappides']},
        {t:'p',v:'Loome reise, mida ei ole võimalik šablooni järgi korrata.'},
      ],
      en:[
        {t:'p',v:'We create custom-designed journeys developed by our team — these are not standard tour packages, but fully curated itineraries tailored to each client.'},
        {t:'p',v:'Every trip is crafted personally: from the initial idea and concept to the smallest details. We select the best hotels, arrange private transfers, ensure comfortable flights, and build a program that matches your interests, travel pace, and desired level of service.'},
        {t:'p',v:'We work only with trusted, highly qualified guides and tour leaders who provide not just a tour, but a deep immersion into the culture and atmosphere of the country. Our routes include exclusive locations, unique experiences, and access to truly special formats of travel.'},
        {t:'p',v:'Full quality control, attention to detail, and 24/7 support are an essential part of every journey.'},
        {t:'p',v:'An individual tour with us is:'},
        {t:'list',v:['a personally designed itinerary','top guides and private excursions','exclusive locations and experiences','a high level of service','full support at every stage']},
        {t:'p',v:'We create journeys that cannot be copied from a template.'},
      ]
    }
  },
  2:{
    n:{ru:"Частные перелёты",et:"Eralennud",lv:"Privātie lidojumi",en:"Private Flights"},
    d:{ru:"Бизнес и первый класс, VIP-залы и приоритетная посадка",et:"Privaatjettidel igasse sihtkohta — mugavus, kiirus ja privaatsus",lv:"Privātajā lidmašīnā uz jebkuru pasaules punktu",en:"Business and first class, VIP lounges, priority boarding"},
    body:{
      ru:[
        {t:'p',v:'Мы организуем перелёты на частных самолётах в любую точку мира, обеспечивая максимальный комфорт, скорость и приватность.'},
        {t:'p',v:'Наши клиенты получают:'},
        {t:'list',v:['Доступ к частным джетам в любой точке мира','VIP-залы и приоритетное обслуживание в аэропортах','Полное сопровождение и круглосуточная поддержка 24/7','Персональный подход к каждому рейсу, учитывая ваши пожелания и график']},
      ],
      lv:[
        {t:'p',v:'Mēs organizējam lidojumus ar privātajām lidmašīnām uz jebkuru pasaules punktu, nodrošinot maksimālu komfortu, ātrumu un privātumu.'},
        {t:'p',v:'Mūsu klienti saņem:'},
        {t:'list',v:['Piekļuvi privātajiem džetiem jebkur pasaulē','VIP zāles un prioritāru apkalpošanu lidostās','Pilnu atbalstu un palīdzību 24/7','Individuālu pieeju katram reisam, ņemot vērā jūsu vēlmes un grafiku']},
      ],
      et:[
        {t:'p',v:'Korraldame eralennud privaatlendudel igasse sihtkohta maailmas — maksimaalse mugavuse, kiiruse ja privaatsusega.'},
        {t:'p',v:'Meie kliendid saavad:'},
        {t:'list',v:['Ligipääsu privaatjetidele üle maailma','VIP-salongid ja prioriteetne teenindus lennujaamades','Täielik tugi ja abi 24/7','Personaalse lähenemise igale lennule, arvestades teie soove ja ajakava']},
      ],
      en:[
        {t:'p',v:'We arrange private jet flights to any destination worldwide — with maximum comfort, speed, and privacy.'},
        {t:'p',v:'Our clients receive:'},
        {t:'list',v:['Access to private jets anywhere in the world','VIP lounges and priority airport service','Full support and 24/7 assistance','A personalized approach to every flight, based on your preferences and schedule']},
      ]
    }
  },
  3:{
    n:{ru:"Проверенное проживание",et:"Kontrollitud majutus",lv:"Pārbaudīta naktsmītne",en:"Verified Accommodation"},
    d:{ru:"Бутик-отели, виллы, палаццо — только проверенные адреса",et:"Butiikhotellid, villad, palazzod — ainult usaldusväärsed valikud",lv:"Butika viesnīcas, villas, palazzo — tikai pārbaudītas adreses",en:"Boutique hotels, villas, palazzos — only trusted addresses"},
    body:{
      ru:[
        {t:'p',v:'Мы предлагаем проверенные варианты проживания в любой точке мира — от уютных бутик‑отелей до роскошных сетевых резортов. Благодаря прямым связям с гостиницами, партнёрским платформам и эксклюзивным тарифам наши цены часто выгоднее, чем на обычных онлайн‑сервисах.'},
        {t:'p',v:'С нами вы получаете:'},
        {t:'list',v:['Доступ к тысячам объектов жилья по всему миру — от городских отелей до курортных комплексов.','Лучшие цены и эксклюзивные предложения, которые мы подбираем специально для вас.','Только проверенные варианты с высоким рейтингом и качественным сервисом.','Поддержку на всех этапах — до и во время вашего путешествия 24/7.']},
      ],
      lv:[
        {t:'p',v:'Mēs piedāvājam pārbaudītas naktsmītnes visā pasaulē — no mājīgām butika viesnīcām līdz grezniem tīkla kūrortiem. Pateicoties tiešām sadarbībām ar viesnīcām, partneru platformām un ekskluzīviem tarifiem, mūsu cenas bieži ir izdevīgākas nekā standarta tiešsaistes rezervēšanas servisos.'},
        {t:'p',v:'Ar mums jūs iegūstat:'},
        {t:'list',v:['Piekļuvi tūkstošiem naktsmītņu visā pasaulē — no pilsētas viesnīcām līdz kūrortu kompleksiem','Labākās cenas un ekskluzīvus piedāvājumus, kas piemeklēti tieši jums','Tikai pārbaudītas izvēles ar augstiem reitingiem un kvalitatīvu servisu','Atbalstu 24/7 — pirms ceļojuma un ceļojuma laikā']},
      ],
      et:[
        {t:'p',v:'Pakume kontrollitud majutusvõimalusi üle maailma — alates hubastest butiikhotellidest kuni luksuslike kuurortideni. Tänu otsekontaktidele hotellidega, partnerplatvormidele ja eksklusiivsetele tariifidele on meie hinnad sageli soodsamad kui tavapärastes online-broneerimisteenustes.'},
        {t:'p',v:'Meiega saate:'},
        {t:'list',v:['Ligipääsu tuhandetele majutuskohtadele üle maailma — linnahotellidest kuurortkompleksideni','Parimad hinnad ja eksklusiivsed pakkumised, mis valitakse just teile','Ainult kontrollitud valikud kõrge reitingu ja kvaliteetse teenindusega','Tugi 24/7 — enne reisi ja reisi ajal']},
      ],
      en:[
        {t:'p',v:'We offer carefully selected accommodation options worldwide — from cozy boutique hotels to luxury branded resorts. Thanks to direct hotel relationships, partner platforms, and exclusive rates, our prices are often better than those on standard online booking services.'},
        {t:'p',v:'With us you get:'},
        {t:'list',v:['Access to thousands of properties worldwide — from city hotels to resort complexes','The best prices and exclusive offers selected specifically for you','Only verified options with high ratings and strong service quality','24/7 support — before and during your trip']},
      ]
    }
  },
  4:{
    n:{ru:"Трансферы",et:"Transfeerid",lv:"Transfēri",en:"Transfers"},
    d:{ru:"Встреча в аэропорту, представительские авто, яхты и вертолёты",et:"Individuaalsed transfeerid igas marsruudi punktis",lv:"Individuāli transfēri jebkurā maršruta posmā",en:"Airport meet-and-greet, executive vehicles, yachts, helicopters"},
    body:{
      ru:[
        {t:'p',v:'Мы организуем индивидуальные трансферы в любой точке мира. Наши клиенты получают надёжный и комфортный транспорт в любой точке маршрута, от аэропорта до отеля и между городами.'},
        {t:'p',v:'Что мы предлагаем:'},
        {t:'list',v:['Индивидуальные трансферы — персональный комфорт и гибкий график','Лучшие партнёры по всему миру — проверенные водители и транспорт.','Выгодные цены — сравните с любыми сервисами, мы найдём оптимальный вариант.','Поддержка 24/7 — сопровождение на всех этапах поездки.']},
        {t:'p',v:'С нами ваши перемещения становятся удобными, безопасными и максимально комфортными, независимо от страны и маршрута.'},
      ],
      lv:[
        {t:'p',v:'Mēs organizējam individuālus transfērus jebkur pasaulē. Mūsu klienti saņem uzticamu un komfortablu transportu jebkurā maršruta posmā — no lidostas līdz viesnīcai un starp pilsētām.'},
        {t:'p',v:'Ko piedāvājam:'},
        {t:'list',v:['Individuāli transfēri — personīgs komforts un elastīgs grafiks','Labākie partneri visā pasaulē — pārbaudīti šoferi un transports','Izdevīgas cenas — salīdziniet ar jebkuru servisu, mēs atradīsim optimālu variantu','Atbalsts 24/7 — palīdzība visos ceļojuma posmos']},
        {t:'p',v:'Ar mums pārvietošanās ir ērta, droša un maksimāli komfortabla — neatkarīgi no valsts vai maršruta.'},
      ],
      et:[
        {t:'p',v:'Korraldame individuaalsed transfeerid üle maailma. Meie kliendid saavad usaldusväärse ja mugava transpordi igas marsruudi punktis — lennujaamast hotelli ja linnade vahel.'},
        {t:'p',v:'Mida pakume:'},
        {t:'list',v:['Individuaalsed transfeerid — isiklik mugavus ja paindlik ajakava','Parimad partnerid üle maailma — kontrollitud juhid ja transport','Soodsad hinnad — võrrelge ükskõik millise teenusega, leiame optimaalse lahenduse','Tugi 24/7 — abi igas reisi etapis']},
        {t:'p',v:'Meiega on liikumine mugav, turvaline ja maksimaalselt komfortne — sõltumata riigist või marsruudist.'},
      ],
      en:[
        {t:'p',v:'We arrange individual transfers anywhere in the world. Our clients receive reliable and comfortable transport at every stage of the journey — from airport to hotel and between cities.'},
        {t:'p',v:'What we offer:'},
        {t:'list',v:['Individual transfers — personal comfort and a flexible schedule','Best partners worldwide — trusted drivers and vehicles','Great prices — compare with any service, we\'ll find the optimal option','24/7 support — assistance at every stage of your trip']},
        {t:'p',v:'With us, getting around becomes convenient, safe, and comfortable — regardless of the country or itinerary.'},
      ]
    }
  },
  5:{
    n:{ru:"Авиаперелёты",et:"Lennupiletid",lv:"Aviobiļetes",en:"Flights"},
    d:{ru:"Бизнес-джеты, чартеры, вертолёты — путешествуйте без границ",et:"Turistiklass, äriklass ja esimene klass — üle maailma",lv:"Ekonomiskā, biznesa un pirmā klase — visā pasaulē",en:"Economy, business and first class — worldwide"},
    body:{
      ru:[
        {t:'p',v:'Мы предлагаем перелёты по всему миру — эконом, бизнес и первый класс. Благодаря прямым связям с авиакомпаниями и партнёрским платформам наши клиенты получают наилучшие цены и условия, которые сложно найти самостоятельно.'},
        {t:'p',v:'Что мы предлагаем:'},
        {t:'list',v:['Перелёты эконом, бизнес и первый класс в любую точку мира.','Выгодные тарифы, включая специальные предложения и акции авиакомпаний.','Доступ ко всем авиакомпаниям и рейсам без ограничений по странам.','Поддержка 24/7 на всех этапах бронирования и путешествия.']},
        {t:'p',v:'С нами вы путешествуете удобно, безопасно и экономно, независимо от класса и направления.'},
      ],
      lv:[
        {t:'p',v:'Mēs piedāvājam lidojumus visā pasaulē — ekonomisko, biznesa un pirmo klasi. Pateicoties tiešai sadarbībai ar aviokompānijām un partneru platformām, mūsu klienti saņem vislabākās cenas un nosacījumus, ko bieži ir grūti atrast patstāvīgi.'},
        {t:'p',v:'Ko piedāvājam:'},
        {t:'list',v:['Ekonomiskās, biznesa un pirmās klases lidojumus uz jebkuru galamērķi','Izdevīgus tarifus, tostarp īpašos piedāvājumus un aviokompāniju akcijas','Piekļuvi visām aviokompānijām un reisiem bez valstu ierobežojumiem','Atbalstu 24/7 rezervācijas un ceļojuma laikā']},
        {t:'p',v:'Ar mums jūs ceļojat ērti, droši un izdevīgi — neatkarīgi no galamērķa un klases.'},
      ],
      et:[
        {t:'p',v:'Pakume lende üle maailma — turistiklass, äriklass ja esimene klass. Tänu otsekontaktidele lennufirmadega ja partnerplatvormidele saavad meie kliendid parimad hinnad ja tingimused, mida on keeruline iseseisvalt leida.'},
        {t:'p',v:'Mida pakume:'},
        {t:'list',v:['Turistiklassi, äriklassi ja esimese klassi lennud igasse sihtkohta','Soodsad tariifid, sh eripakkumised ja lennufirmade kampaaniad','Ligipääs kõigile lennufirmadele ja lendudele ilma riigipiiranguteta','Tugi 24/7 broneerimise ja reisi igas etapis']},
        {t:'p',v:'Meiega reisite mugavalt, turvaliselt ja soodsalt — sõltumata sihtkohast ja klassist.'},
      ],
      en:[
        {t:'p',v:'We offer worldwide flights — economy, business, and first class. Thanks to direct airline relationships and partner platforms, our clients receive the best prices and conditions that are hard to find on their own.'},
        {t:'p',v:'What we offer:'},
        {t:'list',v:['Economy, business, and first-class flights to any destination','Competitive fares, including special offers and airline promotions','Access to all airlines and routes without country limitations','24/7 support at every stage of booking and travel']},
        {t:'p',v:'With us, you travel conveniently, safely, and cost-effectively — regardless of destination or class.'},
      ]
    }
  },
  6:{
    n:{ru:"Страхование",et:"Reisikindlustus",lv:"Ceļojumu apdrošināšana",en:"Travel Insurance"},
    d:{ru:"Полное покрытие: медицина, отмена, багаж, эвакуация",et:"Usaldusväärne kaitse reisimiseks üle maailma",lv:"Uzticams segums ceļojumiem visā pasaulē",en:"Full coverage: medical, cancellation, luggage, evacuation"},
    body:{
      ru:[
        {t:'p',v:'Мы сотрудничаем с ведущими страховыми компаниями, которые предлагают надежное покрытие для путешествий по всему миру. Мы подбираем для вас оптимальные условия и самые выгодные тарифы, чтобы ваше путешествие было безопасным и спокойным.'},
        {t:'p',v:'Что мы предлагаем:'},
        {t:'list',v:['Полное покрытие для путешествий — медицинская страховка, страхование багажа, от отмены рейса и другие опции.','Сотрудничество с лучшими страховыми компаниями, гарантирующими надежность и быстрое урегулирование вопросов.','Выгодные цены — мы подбираем страховые пакеты с учетом ваших потребностей и бюджета.','Поддержка 24/7 — помощь и консультации на всех этапах поездки.']},
        {t:'p',v:'С нами ваши путешествия становятся безопасными, а вопросы страхования решаются быстро и удобно.'},
      ],
      lv:[
        {t:'p',v:'Mēs sadarbojamies ar vadošajām apdrošināšanas kompānijām, kas piedāvā uzticamu segumu ceļojumiem visā pasaulē. Mēs piemeklējam optimālus nosacījumus un izdevīgākos tarifus, lai jūsu ceļojums būtu drošs un mierīgs.'},
        {t:'p',v:'Ko piedāvājam:'},
        {t:'list',v:['Pilnu ceļojuma segumu — medicīniskā apdrošināšana, bagāža, reisa atcelšana un citas opcijas','Sadarbību ar labākajām apdrošināšanas kompānijām, kas garantē uzticamību un ātru atlīdzību izskatīšanu','Izdevīgas cenas — apdrošināšanu piemeklējam atbilstoši jūsu vajadzībām un budžetam','Atbalstu 24/7 — palīdzība un konsultācijas visos ceļojuma posmos']},
        {t:'p',v:'Ar mums jūsu ceļojumi ir drošāki, un apdrošināšanas jautājumi tiek atrisināti ātri un ērti.'},
      ],
      et:[
        {t:'p',v:'Teeme koostööd juhtivate kindlustusfirmadega, kes pakuvad usaldusväärset kaitset reisimiseks üle maailma. Valime teile parimad tingimused ja soodsaimad tariifid, et teie reis oleks turvaline ja rahulik.'},
        {t:'p',v:'Mida pakume:'},
        {t:'list',v:['Täielik reisikaitse — ravikindlustus, pagasikindlustus, lennu tühistamine ja muud valikud','Koostöö parimate kindlustusfirmadega, mis tagavad usaldusväärsuse ja kiire kahjukäsitluse','Soodsad hinnad — kindlustuspakett vastavalt teie vajadustele ja eelarvele','Tugi 24/7 — abi ja nõustamine igas reisi etapis']},
        {t:'p',v:'Meiega on reisimine turvalisem ning kindlustusküsimused lahenevad kiiresti ja mugavalt.'},
      ],
      en:[
        {t:'p',v:'We work with leading insurance providers offering reliable coverage for travel worldwide. We select the best terms and the most cost-effective rates, so your trip stays safe and worry-free.'},
        {t:'p',v:'What we offer:'},
        {t:'list',v:['Comprehensive travel coverage — medical insurance, baggage, trip cancellation, and other options','Cooperation with top insurance companies ensuring reliability and fast claim handling','Great prices — we tailor insurance packages to your needs and budget','24/7 support — help and guidance at every stage of your trip']},
        {t:'p',v:'With us, your travels are safer, and insurance matters are handled quickly and conveniently.'},
      ]
    }
  },
};
function getServices(lang) { return [1,2,3,4,5,6].map(id=>({id, name:SVC_I18N[id].n[lang]||SVC_I18N[id].n.en, desc:SVC_I18N[id].d[lang]||SVC_I18N[id].d.en, icon:['◈','✦','◆','⬡','△','◇'][id-1], body:SVC_I18N[id].body?.[lang]||null})); }

const VALUES_I18N = [
  { icon:'01', t:{ru:'Полный комплекс услуг',et:'Täielik teenustekompleks',lv:'Pilns pakalpojumu klāsts',en:'Full Range of Services'}, d:{ru:'Мы подбираем турпакеты по всему миру, авиабилеты, проживание, трансферы, аренду автомобилей (включая премиальные и частные), а также организуем перелёты на частных самолётах.',et:'Valime turismipakette üle maailma, lennupileteid, majutust, transfereid, autorentali (sh premium ja privaatsõidukid) ning korraldame eralennukiga lende.',lv:'Atlasām tūru paketes visā pasaulē, aviobiļetes, naktsmītnes, transferus, automašīnu nomu (tostarp premium un privātās), kā arī organizējam privāto lidmašīnu lidojumus.',en:'We select tour packages worldwide, flights, accommodation, transfers, car rentals (including premium and private), and organise private jet flights.'} },
  { icon:'02', t:{ru:'Работа с ведущими туроператорами',et:'Koostöö juhtivate reisikorraldajatega',lv:'Sadarbība ar vadošajiem tūroperatoriem',en:'Working with Leading Tour Operators'}, d:{ru:'Мы предлагаем все турпакеты, доступные на территории Эстонии, а также организуем вылеты из любой точки мира.',et:'Pakume kõiki Eestis saadaval olevaid reisipakette ning korraldame väljumisi mis tahes punktist maailmas.',lv:'Piedāvājam visas Igaunijā pieejamās tūru paketes un organizējam izlidošanas no jebkuras pasaules vietas.',en:'We offer all tour packages available in Estonia and organise departures from any point in the world.'} },
  { icon:'03', t:{ru:'Гибкость и индивидуальный подход',et:'Paindlikkus ja individuaalne lähenemine',lv:'Elastīgums un individuāla pieeja',en:'Flexibility and Individual Approach'}, d:{ru:'Мы подбираем путешествия под ваши пожелания – от бюджетных поездок до роскошного VIP-отдыха.',et:'Kohandame reise teie soovide järgi — eelarvereisidest kuni luksuslike VIP-puhkusteni.',lv:'Pielāgojam ceļojumus jūsu vēlmēm — no budžeta braucieniem līdz luksusa VIP atvaļinājumiem.',en:'We tailor journeys to your wishes — from budget trips to luxury VIP holidays.'} },
  { icon:'04', t:{ru:'Безопасность и комфорт',et:'Turvalisus ja mugavus',lv:'Drošība un komforts',en:'Safety and Comfort'}, d:{ru:'Оформляем страховки, визовую поддержку и предоставляем консультации по любым вопросам, связанным с поездкой.',et:'Korraldame kindlustuse, viisatoe ja anname nõu kõikides reisiga seotud küsimustes.',lv:'Nokārtojam apdrošināšanu, vīzu atbalstu un sniedzam konsultācijas par visiem ar ceļojumu saistītajiem jautājumiem.',en:'We arrange insurance, visa support, and provide consultations on any travel-related matters.'} },
  { icon:'05', t:{ru:'Поддержка 24/7',et:'Tugi 24/7',lv:'Atbalsts 24/7',en:'24/7 Support'}, d:{ru:'Наша команда на связи круглосуточно, чтобы помочь вам в любой ситуации, где бы вы ни находились.',et:'Meie meeskond on ümber kella kättesaadav, et aidata teid igas olukorras, kus iganes te olete.',lv:'Mūsu komanda ir pieejama visu diennakti, lai palīdzētu jums jebkurā situācijā, lai kur jūs atrastos.',en:'Our team is available around the clock to help you in any situation, wherever you are.'} },
  { icon:'06', t:{ru:'Лучшие предложения и эксклюзивные условия',et:'Parimad pakkumised ja eksklusiivsed tingimused',lv:'Labākie piedāvājumi un ekskluzīvie nosacījumi',en:'Best Offers and Exclusive Terms'}, d:{ru:'Благодаря партнёрству с мировыми лидерами в сфере туризма, мы предлагаем выгодные цены и специальные предложения, которые недоступны в открытых системах бронирования.',et:'Tänu koostööle turismi maailmaliidritega pakume soodsaid hindu ja eripakkumisi, mis ei ole avatud broneerimissüsteemides kättesaadavad.',lv:'Pateicoties partnerattiecībām ar tūrisma pasaules līderiem, piedāvājam izdevīgas cenas un īpašos piedāvājumus, kas nav pieejami atvērtajās rezervācijas sistēmās.',en:'Thanks to partnerships with world leaders in tourism, we offer competitive prices and special deals unavailable through public booking systems.'} },
];
function getValues(lang) { return VALUES_I18N.map(v => ({icon:v.icon, title:v.t[lang]||v.t.en, desc:v.d[lang]||v.d.en})); }

const BLOG_I18N = [
  {id:1,t:{ru:'10 скрытых жемчужин Средиземноморья',et:'10 Vahemere peidetud pärlit',lv:'10 slēptās Vidusjūras pērles',en:'10 Hidden Mediterranean Gems for 2026'},c:{ru:'Вдохновение',et:'Inspiratsioon',lv:'Iedvesma',en:'Inspiration'},dt:{ru:'12 фев 2026',et:'12. veebr 2026',lv:'12. febr 2026',en:'12 Feb 2026'},ex:{ru:'Забудьте о переполненных курортах. Мы собрали десять мест, где время остановилось...',et:'Unustage ülerahvastatud kuurordid. Kogusime kümme kohta...',lv:'Aizmirstiet pārpildītus kūrortus. Esam apkopojuši desmit vietas...',en:'Forget the overcrowded resorts. Ten places where time stands still...'}},
  {id:2,t:{ru:'Как спланировать идеальный медовый месяц',et:'Kuidas planeerida ideaalset mesinädalat',lv:'Kā ieplānot ideālu medusmēnesi',en:'How to Plan the Perfect Honeymoon'},c:{ru:'Советы',et:'Nõuanded',lv:'Padomi',en:'Advice'},dt:{ru:'5 фев 2026',et:'5. veebr 2026',lv:'5. febr 2026',en:'5 Feb 2026'},ex:{ru:'От выбора направления до последнего штриха — полное руководство...',et:'Sihtkoha valikust viimase detailini — täielik juhend...',lv:'No galamērķa izvēles līdz pēdējam akcentam — pilnīga rokasgrāmata...',en:'From choosing the destination to the final flourish — a complete guide...'}},
  {id:3,t:{ru:'Гастрономическая Япония: за пределами суши',et:'Gastronoomiline Jaapan: kaugemale sushist',lv:'Gastronomiskā Japāna: tālāk par suši',en:'Gastronomic Japan: Beyond Sushi'},c:{ru:'Направления',et:'Sihtkohad',lv:'Galamērķi',en:'Destinations'},dt:{ru:'28 янв 2026',et:'28. jaan 2026',lv:'28. janv 2026',en:'28 Jan 2026'},ex:{ru:'Кайсэки, рамен, якитори — погружение в кулинарную вселенную...',et:'Kaiseki, ramen, yakitori — süvenemine kulinaarsesse universumisse...',lv:'Kaiseki, ramens, jakitori — iegremdēšanās kulinārijas visumā...',en:'Kaiseki, ramen, yakitori — an immersion into the culinary universe...'}},
];
function getBlog(lang) { return BLOG_I18N.map(b=>({id:b.id,title:b.t[lang]||b.t.en,cat:b.c[lang]||b.c.en,date:b.dt[lang]||b.dt.en,excerpt:b.ex[lang]||b.ex.en,image:''})); }

const CAT_LABELS = {
  destinations:{en:'Destinations',ru:'Направления',et:'Sihtkohad',lv:'Galamērķi'},
  advice:{en:'Advice',ru:'Советы',et:'Nõuanded',lv:'Padomi'},
  inspiration:{en:'Inspiration',ru:'Вдохновение',et:'Inspiratsioon',lv:'Iedvesma'},
  news:{en:'News',ru:'Новости',et:'Uudised',lv:'Ziņas'},
};
function mapApiPosts(posts, lang) {
  return posts.map(p => ({
    id: p.id,
    title: p[`title_${lang}`] || p.title_en || p.title_ru || '',
    cat: CAT_LABELS[p.category]?.[lang] || p.category || '',
    date: p.date || '',
    excerpt: p[`excerpt_${lang}`] || p.excerpt_en || p.excerpt_ru || '',
    body: p[`content_${lang}`] || p.content_en || p.content_ru || p[`excerpt_${lang}`] || p.excerpt_en || p.excerpt_ru || '',
    image: p.image || '',
    category: p.category || '',
  }));
}

function dName(id,lang){
  const dest = getDestinations().find(d=>d.id===id);
  if (dest) {
    const byLang = dest[`name_${lang}`] || dest.name;
    if (byLang) return byLang;
  }
  const d = DEST_NAMES[id];
  return d ? d.n[lang] || d.n.en : '';
}
function dRegion(id,lang){
  const dest = getDestinations().find(d=>d.id===id);
  if (dest) {
    const byLang = dest[`region_${lang}`] || dest.region || '';
    if (byLang) return byLang;
  }
  const d = DEST_NAMES[id];
  return d ? d.r[lang] || d.r.en : '';
}
function dBrief(id,lang){
  const dest = getDestinations().find(d=>d.id===id);
  if (dest) {
    const byLang = dest[`brief_${lang}`] || dest.brief || '';
    if (byLang) return byLang;
  }
  const d = DEST_NAMES[id];
  return d ? d.b[lang] || d.b.en : '';
}
function dDescription(id,lang){
  const dest = getDestinations().find(d=>d.id===id);
  if (!dest) return '';
  if (lang === 'ru') return dest.description_ru || dest.description || '';
  if (lang === 'en') return dest.description_en || dest.description || '';
  if (lang === 'lv') return dest.description_lv || dest.description_en || dest.description || '';
  if (lang === 'et') return dest.description_et || dest.description_en || dest.description || '';
  return dest.description || '';
}

// ─── Scroll animation hook ───
function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = '', style = {} }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div ref={ref} className={className} style={{
      ...style,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(50px)',
      transition: `opacity 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}s`
    }}>{children}</div>
  );
}

// ─── Parallax image hook ───
function ParallaxImg({ src, alt, height = 500, intensity = 0.15, children, overlay = true }) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      const viewCenter = window.innerHeight / 2;
      setOffset((center - viewCenter) * intensity);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [intensity]);
  return (
    <div ref={ref} style={{ height, overflow: 'hidden', position: 'relative' }}>
      <div style={{
        position: 'absolute', inset: '-15% 0', backgroundImage: `url(${src})`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        transform: `translateY(${offset}px)`, willChange: 'transform'
      }} />
      {overlay && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(15,15,20,0.2) 0%, rgba(15,15,20,0.5) 100%)' }} />}
      {children && <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '0 0 48px 48px' }}>{children}</div>}
    </div>
  );
}

// ─── CSS ───
const css = `

/* ═══════════ LANGUAGE SWITCHER ═══════════ */
.lang-sw {
  display: flex;
  gap: 8px;
  margin-left: 24px;
}
/* Языковые кнопки — базовые стили */
.lang-btn {
  padding: 6px 14px;
  border: 1px solid rgba(255,255,255,0.25);
  background: none;
  color: var(--white);
  font-size: 12px;
  border-radius: 18px;
  transition: all 0.3s;
  letter-spacing: 1px;
  margin: 0;
}
.lang-btn.active, .lang-btn:hover {
  background: var(--sand-light);
  color: var(--ink);
  border-color: var(--sand-light);
}
/* При светлой шапке (nav.solid) — неактивные языки с тёмной рамкой и текстом */
.nav.solid .lang-btn {
  border: 1px solid var(--ink-light);
  color: var(--ink);
  background: none;
}
.nav.solid .lang-btn.active, .nav.solid .lang-btn:hover {
  background: var(--sand-light);
  color: var(--ink);
  border-color: var(--sand-light);
}
@media (max-width: 1024px) {
  .lang-sw { margin-left: 0; }
}
.mobile-lang-sw {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 18px;
}
.mobile-lang-btn {
  padding: 7px 16px;
  border: 1px solid rgba(255,255,255,0.18);
  background: none;
  color: var(--cream);
  font-size: 13px;
  border-radius: 18px;
  transition: all 0.3s;
  margin: 0;
}
.mobile-lang-btn.active, .mobile-lang-btn:hover {
  background: var(--sand);
  color: var(--ink);
  border-color: var(--sand);
}

:root {
  --forest: #2C3E2D;
  --forest-deep: #1E2B1F;
  --sage: #8B9D77;
  --sage-light: #A8B896;
  --sand: #C8B88A;
  --sand-light: #D6CA9F;
  --cream: #F4F0E8;
  --cream-warm: #EDE7DA;
  --parchment: #FAF8F3;
  --ink: #1A1A1E;
  --ink-light: #2E2E34;
  --text: #4A4A50;
  --text-soft: #7C7C84;
  --white: #FEFDFB;
  --accent: #A67C52;
  --accent-light: #BF9B6F;
  --danger: #B85C5C;
  --serif: 'Playfair Display', Georgia, 'Times New Roman', serif;
  --sans: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  --ease-out-expo: cubic-bezier(0.22, 1, 0.36, 1);
  --ease-out-quint: cubic-bezier(0.23, 1, 0.32, 1);
}

*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
html { scroll-behavior: smooth; }
body { font-family: var(--sans); color: var(--text); background: var(--parchment); -webkit-font-smoothing: antialiased; line-height: 1.65; font-weight: 400; }
.app { overflow-x: hidden; }
img { max-width: 100%; display: block; }
button { font-family: var(--sans); cursor: pointer; }

/* ═══════════ NAVIGATION ═══════════ */
.nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 60px; height: 80px;
  transition: all 0.6s var(--ease-out-expo);
}
.nav.transparent { background: transparent; }
.nav.solid {
  background: rgba(254,253,251,0.92); backdrop-filter: blur(24px) saturate(1.4);
  border-bottom: 1px solid rgba(0,0,0,0.04);
  box-shadow: 0 1px 40px rgba(0,0,0,0.03);
}
.nav.solid .nav-logo { color: var(--ink); }
.nav.solid .nav-logo span { color: var(--accent); }
.nav.solid .nav-link { color: var(--text); }
.nav.solid .nav-link:hover { color: var(--accent); }
.nav.solid .nav-cta { border-color: var(--forest); color: var(--forest); }
.nav.solid .nav-cta:hover { background: var(--forest); color: var(--white); }

.nav-logo {
  font-family: var(--serif); font-size: 20px; font-weight: 500;
  color: var(--white); letter-spacing: 0.5px; cursor: pointer;
  transition: color 0.4s;
}
.nav-logo span { color: var(--sand-light); transition: color 0.4s; }
.nav-links { display: flex; align-items: center; gap: 36px; }
.nav-link {
  font-size: 13px; font-weight: 400; letter-spacing: 0.8px;
  color: rgba(255,255,255,0.75); background: none; border: none;
  transition: all 0.4s; position: relative; text-transform: uppercase;
}
.nav-link::after {
  content: ''; position: absolute; bottom: -6px; left: 50%; width: 0; height: 1px;
  background: currentColor; transition: all 0.4s var(--ease-out-expo);
  transform: translateX(-50%);
}
.nav-link:hover { color: var(--white); }
.nav-link:hover::after { width: 100%; }
.nav-cta {
  padding: 11px 28px; border: 1px solid rgba(255,255,255,0.35);
  background: none; color: var(--white); font-size: 11px;
  letter-spacing: 2px; text-transform: uppercase;
  transition: all 0.4s var(--ease-out-expo);
}
.nav-cta:hover { background: var(--white); color: var(--ink); }
.mobile-toggle { display: none; background: none; border: none; font-size: 22px; color: inherit; }

/* ═══════════ MOBILE MENU ═══════════ */
.mobile-menu-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 10000;
  background: #1A2A1B;
  display: flex; flex-direction: column;
  overflow-y: auto;
  animation: mobileMenuIn 0.4s var(--ease-out-expo);
}
@keyframes mobileMenuIn {
  from { opacity: 0; transform: translateX(100%); }
  to { opacity: 1; transform: translateX(0); }
}
.mobile-menu-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 24px 28px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-shrink: 0;
}
.mobile-menu-close {
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  background: none; border: 1px solid rgba(255,255,255,0.1);
  color: var(--cream); font-size: 18px; cursor: pointer;
  transition: all 0.3s;
}
.mobile-menu-close:hover { border-color: var(--sand); color: var(--sand); }
.mobile-menu-links {
  flex: 1;
  display: flex; flex-direction: column;
  padding: 16px 28px;
}
.mobile-menu-link {
  display: block; width: 100%; text-align: left;
  padding: 18px 0;
  background: none; border: none;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  font-family: var(--serif);
  font-size: 26px; font-weight: 400;
  color: var(--cream);
  cursor: pointer;
  transition: color 0.3s, padding-left 0.3s var(--ease-out-expo);
}
.mobile-menu-link:last-child { border-bottom: none; }
.mobile-menu-link:hover,
.mobile-menu-link:active {
  color: var(--sand);
  padding-left: 12px;
}
.mobile-menu-footer {
  flex-shrink: 0;
  padding: 28px;
  border-top: 1px solid rgba(255,255,255,0.06);
  background: rgba(0,0,0,0.15);
}
.mobile-menu-cta {
  display: block; width: 100%; text-align: center;
  padding: 16px;
  background: var(--sand); color: var(--ink);
  border: none;
  font-size: 12px; letter-spacing: 2.5px; text-transform: uppercase;
  font-family: var(--sans); font-weight: 500;
  cursor: pointer;
  margin-bottom: 24px;
  transition: background 0.3s;
}
.mobile-menu-cta:hover { background: var(--sand-light); }
.mobile-menu-contacts {
  display: flex; flex-direction: column; gap: 8px;
  margin-bottom: 20px;
}
.mobile-menu-contacts span {
  font-size: 13px; color: rgba(255,255,255,0.4); font-weight: 300;
}
.mobile-menu-socials {
  display: flex; gap: 12px;
}
.mobile-menu-social {
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid rgba(255,255,255,0.1);
  font-size: 11px; color: rgba(255,255,255,0.4);
  cursor: pointer; transition: all 0.3s; letter-spacing: 1px;
}
.mobile-menu-social:hover { border-color: var(--sand); color: var(--sand); }

/* ═══════════ HERO ═══════════ */
.hero {
  position: relative; height: 100vh; height: 100dvh; min-height: 700px;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden; background: var(--ink);
}
.hero-bg {
  position: absolute; inset: 0;
  overflow: hidden;
}
.hero-bg-layer {
  position: absolute; inset: 0;
  background-size: cover; background-position: center;
  opacity: 0;
  transform: scale(1.08);
  transition: opacity 1.4s ease, transform 1.4s ease;
}
.hero-bg-layer.is-active {
  opacity: 1;
  transform: scale(1);
  animation: heroZoom 20s ease-in-out infinite alternate;
}
@keyframes heroZoom { 0% { transform: scale(1); } 100% { transform: scale(1.08); } }
.hero-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(180deg,
    rgba(15,15,20,0.35) 0%,
    rgba(15,15,20,0.15) 40%,
    rgba(15,15,20,0.5) 100%
  );
}
.hero-grain {
  position: absolute; inset: 0; opacity: 0.035; mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
}
.hero-content {
  position: relative; z-index: 3; text-align: center; max-width: 1000px; padding: 0 40px;
}
.hero-eyebrow {
  display: inline-block; font-size: 11px; letter-spacing: 5px;
  text-transform: uppercase; color: var(--sand-light);
  margin-bottom: 28px; font-weight: 400;
  opacity: 0; animation: heroFade 1.2s var(--ease-out-expo) 0.3s forwards;
}
.hero h1 {
  font-family: var(--serif); font-weight: 400;
  font-size: clamp(44px, 6.5vw, 82px); line-height: 1.08;
  color: var(--white); margin-bottom: 28px;
  opacity: 0; animation: heroSlide 1.4s var(--ease-out-expo) 0.5s forwards;
}
.hero h1 em { font-style: italic; font-weight: 400; }
.hero-sub {
  font-size: clamp(15px, 1.4vw, 18px); color: rgba(255,255,255,0.65);
  max-width: 560px; margin: 0 auto 44px; line-height: 1.75; font-weight: 300;
  opacity: 0; animation: heroFade 1.2s var(--ease-out-expo) 0.9s forwards;
}
.hero-buttons {
  display: flex; gap: 16px; justify-content: center; flex-wrap: wrap;
  opacity: 0; animation: heroFade 1.2s var(--ease-out-expo) 1.1s forwards;
}
@keyframes heroFade { to { opacity: 1; } }
@keyframes heroSlide { to { opacity: 1; transform: translateY(0); } from { transform: translateY(30px); } }
.btn-hero-primary {
  padding: 16px 44px; background: var(--white); color: var(--ink);
  border: none; font-size: 12px; letter-spacing: 2.5px;
  text-transform: uppercase; font-weight: 500;
  transition: all 0.5s var(--ease-out-expo);
}
.btn-hero-primary:hover { background: var(--sand-light); transform: translateY(-2px); box-shadow: 0 16px 48px rgba(0,0,0,0.2); }
.btn-hero-secondary {
  padding: 16px 44px; background: none;
  border: 1px solid rgba(255,255,255,0.3); color: var(--white);
  font-size: 12px; letter-spacing: 2.5px; text-transform: uppercase;
  font-weight: 400; transition: all 0.5s var(--ease-out-expo);
}
.btn-hero-secondary:hover { border-color: var(--sand-light); color: var(--sand-light); }

.hero-search-bar {
  margin-top: 40px;
  display: grid;
  background: rgba(255,255,255,0.07);
  backdrop-filter: blur(20px) saturate(1.2);
  border: 1px solid rgba(255,255,255,0.13);
  opacity: 0;
  animation: heroFade 1.2s var(--ease-out-expo) 1.3s forwards;
  max-width: 100%;
}
.hero-search-bar.type-hotel { grid-template-columns: minmax(148px,1.5fr) minmax(118px,1fr) minmax(118px,1fr) minmax(118px,1fr) minmax(126px,1fr) auto; }
.hero-search-bar.type-tour { grid-template-columns: minmax(148px,1.5fr) minmax(118px,1fr) minmax(118px,1fr) minmax(126px,1fr) auto; }
.hero-search-bar-field {
  display: flex; flex-direction: column; gap: 5px;
  padding: 14px 16px; border-right: 1px solid rgba(255,255,255,0.1); text-align: left;
  min-width: 0;
}
.hero-search-bar-field label {
  font-size: 9px; letter-spacing: 2.5px; text-transform: uppercase;
  color: rgba(255,255,255,0.45); font-weight: 500; pointer-events: none;
}
.hero-search-bar-field input,
.hero-search-bar-field select {
  background: transparent; border: none; outline: none;
  color: var(--white); font-family: var(--sans); font-size: 13px;
  padding: 0; height: 26px; width: 100%; -webkit-appearance: none; appearance: none;
}
.hero-search-bar-field select {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='rgba(255,255,255,0.5)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 2px center; padding-right: 18px; cursor: pointer;
}
.hero-search-bar-field select option { background: #1a1a20; color: var(--white); }
.hero-search-bar-field input[type="date"] { color-scheme: dark; }
.hero-search-bar-field input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(1) opacity(0.4); cursor: pointer; }
.hero-search-bar-field input[type="number"] { -moz-appearance: textfield; }
.hero-search-bar-field input[type="number"]::-webkit-outer-spin-button,
.hero-search-bar-field input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; }
.hero-search-bar-field input::placeholder { color: rgba(255,255,255,0.3); }
.hero-search-btn {
  background: var(--sand); color: var(--ink); border: none;
  font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; font-weight: 600;
  padding: 0 28px; cursor: pointer; transition: background 0.3s; white-space: nowrap; min-width: 108px;
}
.hero-search-btn:hover { background: var(--sand-light); }
.hero-type-toggle {
  display: flex; height: 26px; margin-top: 1px;
}
.hero-type-btn {
  flex: 1; background: transparent;
  border: 1px solid rgba(255,255,255,0.25);
  color: rgba(255,255,255,0.5);
  font-family: var(--sans); font-size: 11px;
  letter-spacing: 2px; text-transform: uppercase; font-weight: 500;
  padding: 0 10px; cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
  white-space: nowrap;
}
.hero-type-btn:first-child { border-right: none; }
.hero-type-btn.active {
  background: var(--sand); border-color: var(--sand);
  color: var(--ink); font-weight: 700;
}
.hero-type-btn:not(.active):hover {
  background: rgba(255,255,255,0.1);
  color: var(--white); border-color: rgba(255,255,255,0.45);
}
@media (max-width: 900px) {
  .hero-search-bar.type-hotel,
  .hero-search-bar.type-tour { grid-template-columns: repeat(2, 1fr); }
  .hero-search-bar-field { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.1); min-width: 0; }
  .hero-search-bar-field select,
  .hero-search-bar-field input { min-width: 0; }
  .hero-search-btn { grid-column: 1 / -1; padding: 16px; min-width: unset; }
}
@media (max-width: 480px) {
  .hero-search-bar.type-hotel,
  .hero-search-bar.type-tour { grid-template-columns: 1fr; }
  .hero { height: auto; min-height: 100vh; min-height: 100dvh; overflow: visible; padding: 120px 0 60px; }
}

.hero-scroll {
  position: absolute; bottom: 36px; left: 50%; transform: translateX(-50%);
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  z-index: 3; opacity: 0; animation: heroFade 1s var(--ease-out-expo) 2s forwards;
}
.hero-scroll-text { font-size: 10px; letter-spacing: 4px; text-transform: uppercase; color: rgba(255,255,255,0.4); }
.hero-scroll-line { width: 1px; height: 48px; position: relative; overflow: hidden; }
.hero-scroll-line::after {
  content: ''; position: absolute; top: -100%; left: 0; width: 1px; height: 100%;
  background: var(--sand-light); animation: scrollLine 2s ease-in-out infinite;
}
@keyframes scrollLine { 0% { top: -100%; } 50% { top: 100%; } 100% { top: 100%; } }

.hero-trust {
  position: absolute; bottom: 0; left: 0; right: 0; z-index: 3;
  padding: 20px 60px; background: rgba(0,0,0,0.2); backdrop-filter: blur(12px);
  display: flex; justify-content: center; gap: 48px; align-items: center;
  opacity: 0; animation: heroFade 1s var(--ease-out-expo) 1.8s forwards;
}
.hero-trust-item { font-size: 11px; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,255,255,0.4); font-weight: 300; }
.hero-photo-credit {
  position: absolute; bottom: 12px; right: 16px; z-index: 4;
  font-size: 10px; color: rgba(255,255,255,0.25); font-weight: 300;
  pointer-events: none; letter-spacing: 0.3px;
}

/* ═══════════ SECTION ═══════════ */
.section { padding: 140px 60px; }
.section-sm { padding: 100px 60px; }
.section-dark { background: var(--forest-deep); color: var(--cream); }
.section-cream { background: var(--cream); }
.section-sand { background: var(--cream-warm); }

.sec-label {
  font-size: 11px; letter-spacing: 4px; text-transform: uppercase;
  color: var(--accent); font-weight: 500; margin-bottom: 20px;
}
.sec-title {
  font-family: var(--serif); font-weight: 400;
  font-size: clamp(32px, 3.8vw, 54px); line-height: 1.15;
  margin-bottom: 20px;
}
.sec-title-white { color: var(--cream); }
.sec-sub {
  font-size: 16px; color: var(--text-soft); line-height: 1.75;
  max-width: 540px; font-weight: 300;
}
.sec-sub-light { color: rgba(244,240,232,0.5); }

/* ═══════════ EDITORIAL LAYOUT ═══════════ */
.editorial {
  display: grid; grid-template-columns: 1fr 1fr; gap: 0;
  max-width: 1400px; margin: 0 auto; min-height: 600px;
}
.editorial-reverse { direction: rtl; }
.editorial-reverse > * { direction: ltr; }
.editorial-img { position: relative; overflow: hidden; min-height: 500px; }
.editorial-img img {
  position: absolute; inset: 0; width: 100%; height: 100%;
  object-fit: cover; transition: transform 1.2s var(--ease-out-expo);
}
.editorial-img:hover img { transform: scale(1.04); }
.editorial-text {
  display: flex; flex-direction: column; justify-content: center;
  padding: 80px 72px;
}
.editorial-text p { font-size: 16px; line-height: 1.85; font-weight: 300; margin-bottom: 16px; color: var(--text); }

.btn-refined {
  display: inline-flex; align-items: center; gap: 12px;
  font-size: 12px; letter-spacing: 2.5px; text-transform: uppercase;
  color: var(--accent); background: none; border: none;
  font-weight: 500; margin-top: 20px;
  transition: gap 0.4s var(--ease-out-expo);
}
.btn-refined:hover { gap: 20px; }
.btn-refined-line { width: 40px; height: 1px; background: var(--accent); transition: width 0.4s var(--ease-out-expo); }
.btn-refined:hover .btn-refined-line { width: 56px; }

.btn-solid {
  display: inline-block; padding: 16px 40px;
  background: var(--forest); color: var(--white); border: none;
  font-size: 12px; letter-spacing: 2px; text-transform: uppercase;
  font-weight: 500; transition: all 0.5s var(--ease-out-expo);
}
.btn-solid:hover { background: var(--forest-deep); transform: translateY(-2px); box-shadow: 0 12px 40px rgba(44,62,45,0.25); }
.btn-border {
  display: inline-block; padding: 14px 36px;
  background: none; border: 1px solid var(--forest); color: var(--forest);
  font-size: 12px; letter-spacing: 2px; text-transform: uppercase;
  font-weight: 500; transition: all 0.5s var(--ease-out-expo);
}
.btn-border:hover { background: var(--forest); color: var(--white); }

/* ═══════════ DESTINATIONS GRID ═══════════ */
.dest-masonry {
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: 3px; max-width: 1400px; margin: 0 auto;
}
.dest-tile {
  position: relative; overflow: hidden; cursor: pointer;
  aspect-ratio: 4/3;
}
.dest-tile:nth-child(1) { grid-row: span 2; aspect-ratio: auto; }
.dest-tile img {
  width: 100%; height: 100%; object-fit: cover;
  transition: transform 1s var(--ease-out-expo);
}
.dest-tile:hover img { transform: scale(1.06); }
.dest-tile-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(0deg, rgba(15,15,20,0.7) 0%, transparent 60%);
  transition: background 0.6s;
}
.dest-tile:hover .dest-tile-overlay { background: linear-gradient(0deg, rgba(15,15,20,0.8) 0%, rgba(15,15,20,0.1) 60%); }
.dest-tile-info {
  position: absolute; bottom: 0; left: 0; right: 0; padding: 32px;
  transform: translateY(12px); transition: transform 0.6s var(--ease-out-expo);
}
.dest-tile:hover .dest-tile-info { transform: translateY(0); }
.dest-tile-region {
  font-size: 10px; letter-spacing: 3px; text-transform: uppercase;
  color: var(--sand-light); margin-bottom: 8px; font-weight: 400;
}
.dest-tile-name {
  font-family: var(--serif); font-size: clamp(22px, 2vw, 30px);
  font-weight: 400; color: var(--white); margin-bottom: 6px;
}
.dest-tile-brief {
  font-size: 13px; color: rgba(255,255,255,0.6); font-weight: 300; line-height: 1.5;
  max-width: 300px; opacity: 0; transform: translateY(8px);
  transition: all 0.5s var(--ease-out-expo) 0.1s;
}
.dest-tile:hover .dest-tile-brief { opacity: 1; transform: translateY(0); }

/* ═══════════ SERVICES ═══════════ */
.svc-grid {
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: 1px; background: rgba(244,240,232,0.08);
  max-width: 1200px; margin: 0 auto;
}
.svc-card {
  padding: 56px 40px; background: var(--forest-deep);
  transition: background 0.5s; position: relative; overflow: hidden;
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}
.svc-card::after {
  content: ''; position: absolute; bottom: 0; left: 0;
  width: 0; height: 2px; background: var(--sand);
  transition: width 0.6s var(--ease-out-expo);
}
.svc-card:hover::after { width: 100%; }
.svc-card:hover { background: #243526; }
.svc-icon { font-size: 24px; color: var(--sand); margin-bottom: 24px; display: block; }
.svc-name {
  font-family: var(--serif); font-size: 22px; font-weight: 400;
  color: var(--cream); margin-bottom: 14px;
}
.svc-desc { font-size: 14px; color: rgba(244,240,232,0.45); line-height: 1.7; font-weight: 300; }

/* ═══════════ VALUES ═══════════ */
.val-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 48px;
  max-width: 1100px; margin: 0 auto;
}

/* ═══════════ ABOUT STATS ═══════════ */
.about-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 32px; }
@media (max-width: 600px) {
  .about-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 28px 16px; }
}
.val-num {
  font-family: var(--serif); font-size: 42px; font-weight: 300;
  color: var(--sage); margin-bottom: 16px; line-height: 1;
}
.val-title {
  font-family: var(--serif); font-size: 20px; font-weight: 500;
  color: var(--ink); margin-bottom: 10px;
}
.val-desc { font-size: 14px; color: var(--text-soft); line-height: 1.75; font-weight: 300; }

/* ═══════════ BLOG ═══════════ */
.blog-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; max-width: 1200px; margin: 0 auto; }
.blog-card { cursor: pointer; }
.blog-card-img {
  aspect-ratio: 16/10; overflow: hidden; margin-bottom: 20px;
  position: relative;
}
.blog-card-img img {
  width: 100%; height: 100%; object-fit: cover;
  transition: transform 0.8s var(--ease-out-expo);
}
.blog-card:hover .blog-card-img img { transform: scale(1.05); }
.blog-card-cat {
  position: absolute; top: 16px; left: 16px; padding: 5px 14px;
  background: var(--white); font-size: 10px; letter-spacing: 2px;
  text-transform: uppercase; color: var(--text); font-weight: 500;
}
.blog-card-date { font-size: 12px; color: var(--text-soft); margin-bottom: 10px; letter-spacing: 0.5px; }
.blog-card-title {
  font-family: var(--serif); font-size: 22px; font-weight: 400;
  color: var(--ink); margin-bottom: 10px; line-height: 1.3;
  transition: color 0.3s;
}
.blog-card:hover .blog-card-title { color: var(--accent); }
.blog-card-excerpt { font-size: 14px; color: var(--text-soft); line-height: 1.65; font-weight: 300; }

/* ═══════════ PARTNERS CAROUSEL ═══════════ */
.partners-strip {
  overflow: hidden; padding: 56px 0; background: var(--white);
  border-top: 1px solid rgba(0,0,0,0.07);
  border-bottom: 1px solid rgba(0,0,0,0.07);
  position: relative;
}
.partners-strip::before, .partners-strip::after {
  content: ''; position: absolute; top: 0; bottom: 0; width: 140px; z-index: 2; pointer-events: none;
}
.partners-strip::before { left: 0; background: linear-gradient(to right, var(--white) 0%, transparent 100%); }
.partners-strip::after  { right: 0; background: linear-gradient(to left,  var(--white) 0%, transparent 100%); }
.partners-track {
  display: flex; align-items: center; gap: 72px;
  width: max-content;
  animation: partnersScroll 32s linear infinite;
}
.partners-strip:hover .partners-track { animation-play-state: paused; }
.partner-logo {
  height: 44px; width: auto; flex-shrink: 0;
  opacity: 0.38; filter: grayscale(1);
  transition: opacity 0.35s, filter 0.35s;
}
.partner-logo:hover { opacity: 0.85; filter: grayscale(0); }
@keyframes partnersScroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

/* ═══════════ FOOTER ═══════════ */
.footer {
  background: var(--ink); padding: 100px 60px 48px;
}
.footer-inner { max-width: 1300px; margin: 0 auto; }
.footer-top {
  display: grid; grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 60px; margin-bottom: 80px;
}
.footer-brand { font-family: var(--serif); font-size: 24px; color: var(--white); font-weight: 400; margin-bottom: 16px; }
.footer-brand span { color: var(--sand); }
.footer-tagline { font-size: 14px; color: rgba(255,255,255,0.35); line-height: 1.7; font-weight: 300; max-width: 280px; }
.footer-col-title { font-size: 10px; letter-spacing: 3px; text-transform: uppercase; color: var(--sand); margin-bottom: 20px; font-weight: 500; }
.footer-link {
  display: block; font-size: 14px; color: rgba(255,255,255,0.45);
  margin-bottom: 14px; background: none; border: none; text-align: left;
  font-family: var(--sans); font-weight: 300; cursor: pointer;
  transition: color 0.3s;
}
.footer-link:hover { color: var(--sand); }
.footer-bottom {
  border-top: 1px solid rgba(255,255,255,0.06); padding-top: 32px;
  display: flex; justify-content: space-between; align-items: center;
}
.footer-copy { font-size: 12px; color: rgba(255,255,255,0.25); }
.footer-socials { display: flex; gap: 16px; }
.footer-social {
  width: 40px; height: 40px; border: 1px solid rgba(255,255,255,0.1);
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; color: rgba(255,255,255,0.4); cursor: pointer;
  transition: all 0.4s;
}
.footer-social:hover { border-color: var(--sand); color: var(--sand); }

/* ═══════════ WHATSAPP CHAT BUTTON ═══════════ */
.chat-btn {
  position: fixed; bottom: 32px; right: 32px; z-index: 9999;
  width: 56px; height: 56px; border-radius: 50%;
  background: #25D366; color: #fff;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 20px rgba(37,211,102,0.45);
  transition: transform 0.25s, box-shadow 0.25s;
  text-decoration: none;
}
.chat-btn:hover { transform: scale(1.1); box-shadow: 0 6px 28px rgba(37,211,102,0.6); }
.chat-btn svg { width: 28px; height: 28px; fill: #fff; }
@media (max-width: 600px) {
  .chat-btn { bottom: 20px; right: 20px; width: 50px; height: 50px; }
  .chat-btn svg { width: 24px; height: 24px; }
}

/* ═══════════ INNER PAGES ═══════════ */
.page-hero {
  padding: 180px 60px 100px; position: relative; overflow: hidden;
  background: var(--forest-deep);
}
.page-hero-bg {
  position: absolute; inset: 0; opacity: 0.25;
  background-size: cover; background-position: center;
}
.page-hero-content { position: relative; z-index: 2; max-width: 700px; }
.page-hero .sec-label { color: var(--sand); }

.pkg-detail-grid {
  display: grid; grid-template-columns: 1fr 360px; gap: 80px;
  max-width: 1100px; margin: 0 auto; padding: 100px 60px;
}
.pkg-detail-main {}
.pkg-detail-sidebar { position: sticky; top: 100px; align-self: start; }
.pkg-sidebar-card {
  background: var(--cream); padding: 40px;
}
.pkg-sidebar-price {
  font-family: var(--serif); font-size: 36px; font-weight: 400;
  color: var(--ink); margin-bottom: 4px;
}
.pkg-sidebar-from { font-size: 13px; color: var(--text-soft); margin-bottom: 24px; }
.pkg-sidebar-meta { margin-bottom: 24px; }
.pkg-sidebar-row {
  display: flex; justify-content: space-between; padding: 12px 0;
  border-bottom: 1px solid rgba(0,0,0,0.05); font-size: 14px;
}
.pkg-sidebar-row span:first-child { color: var(--text-soft); font-weight: 300; }
.pkg-sidebar-row span:last-child { color: var(--ink); font-weight: 500; }
.pkg-group-pricing { margin-bottom: 24px; }
.pkg-group-pricing__label {
  font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase;
  color: var(--text-soft); margin-bottom: 8px; font-weight: 500;
}
.pkg-group-pricing__select {
  width: 100%; padding: 11px 14px;
  border: 1px solid rgba(0,0,0,0.14); background: #fff;
  font-family: var(--sans); font-size: 14px; color: var(--ink);
  cursor: pointer; border-radius: 0; appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 12px center;
  padding-right: 36px;
}

/* ── Accommodation date picker & calculator ── */
.acc-price-from-label {
  font-size: 11px; letter-spacing: 2px; text-transform: uppercase;
  color: var(--text-soft); font-weight: 500; margin-bottom: 2px;
}
.acc-dates {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(0,0,0,0.07);
}
.acc-dates__title {
  font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase;
  color: var(--text-soft); font-weight: 500; margin-bottom: 12px;
}
.acc-dates__grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 0;
}
.acc-dates__field label {
  display: block; font-size: 11px; color: var(--text-soft); font-weight: 500;
  letter-spacing: 1px; text-transform: uppercase; margin-bottom: 5px;
}
.acc-dates__field input[type="date"] {
  width: 100%; padding: 9px 10px; border: 1px solid rgba(0,0,0,0.14);
  background: #fff; font-family: var(--sans); font-size: 13px; color: var(--ink);
  cursor: pointer; border-radius: 0; box-sizing: border-box;
}
.acc-dates__field input[type="date"]:disabled { opacity: 0.45; cursor: not-allowed; }
.acc-calc {
  margin-top: 14px; background: var(--ink); padding: 14px 16px;
}
.acc-calc__row {
  display: flex; justify-content: space-between;
  font-size: 13px; color: rgba(255,255,255,0.75); margin-bottom: 6px;
}
.acc-calc__total {
  display: flex; justify-content: space-between; align-items: baseline;
  padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.15);
  font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase;
  color: rgba(255,255,255,0.6);
}
.acc-calc__total span:last-child {
  font-family: var(--serif); font-size: 22px; color: #fff; letter-spacing: 0; text-transform: none;
}
.acc-card-total {
  font-size: 12px; color: var(--accent); margin-top: 5px;
  font-family: var(--sans); font-weight: 400;
}
.acc-card-total strong { font-weight: 600; }

.pkg-detail-side {
  position: sticky;
  top: 100px;
  align-self: start;
}
.pkg-detail-info {
  background: var(--cream);
  padding: 32px;
  border: 1px solid rgba(0,0,0,0.04);
}
.pkg-info-row {
  display: grid;
  grid-template-columns: minmax(120px, 0.82fr) minmax(0, 1fr);
  gap: 20px;
  align-items: baseline;
  padding: 14px 0;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
.pkg-info-row:first-child { padding-top: 0; }
.pkg-info-row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}
.pkg-info-label {
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 1.8px;
  line-height: 1.4;
  text-transform: uppercase;
}
.pkg-info-val {
  color: var(--ink);
  font-size: 15px;
  font-weight: 400;
  line-height: 1.5;
  text-align: right;
  overflow-wrap: anywhere;
}

.pkg-h3 {
  font-family: var(--serif); font-size: 28px; font-weight: 400;
  color: var(--ink); margin-bottom: 24px; padding-bottom: 16px;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
.pkg-day { padding: 14px 0; border-bottom: 1px solid rgba(0,0,0,0.04); font-size: 15px; line-height: 1.7; font-weight: 300; }
.pkg-list { list-style: none; }
.pkg-list li { padding: 10px 0 10px 28px; font-size: 15px; font-weight: 300; position: relative; }
.pkg-list li::before { content: ''; position: absolute; left: 0; top: 18px; width: 12px; height: 1px; background: var(--sage); }
.pkg-list.excluded li::before { background: var(--danger); }

/* ═══════════ BOOKING MODAL ═══════════ */
.modal-bg {
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(15,15,20,0.6); backdrop-filter: blur(10px);
  display: flex; align-items: flex-start; justify-content: center;
  padding: 48px 24px; overflow-y: auto;
  animation: modalIn 0.4s var(--ease-out-expo);
}
@keyframes modalIn { from { opacity: 0; } to { opacity: 1; } }
.modal-box {
  background: var(--white); max-width: 660px; width: 100%;
  animation: modalSlide 0.5s var(--ease-out-expo);
  margin: 40px 0;
}
@keyframes modalSlide { from { opacity: 0; transform: translateY(32px); } to { opacity: 1; transform: translateY(0); } }
.modal-head { padding: 44px 48px 0; }
.modal-x {
  position: absolute; top: 20px; right: 24px; width: 44px; height: 44px;
  border: none; background: none; font-size: 18px; color: var(--text-soft);
  cursor: pointer; transition: color 0.3s; display: flex; align-items: center; justify-content: center;
}
.modal-x:hover { color: var(--ink); }
.modal-body { padding: 36px 48px 48px; }
.form-sec { margin-bottom: 32px; }
.form-sec-title { font-size: 10px; letter-spacing: 3px; text-transform: uppercase; color: var(--accent); margin-bottom: 16px; font-weight: 500; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.form-grp { margin-bottom: 16px; }
.form-lbl { display: block; font-size: 13px; color: var(--text); margin-bottom: 6px; font-weight: 400; }
.form-inp {
  width: 100%; padding: 13px 16px; border: 1px solid rgba(0,0,0,0.08);
  background: var(--white); font-size: 14px; font-family: var(--sans);
  color: var(--ink); outline: none; transition: border-color 0.3s;
}
.form-inp:focus { border-color: var(--sage); }
.form-inp.ro { background: var(--cream); color: var(--text-soft); cursor: not-allowed; }
.form-inp.err { border-color: var(--danger); }
.form-ta { min-height: 80px; resize: vertical; }
.form-chk-row { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; }
.form-chk { width: 18px; height: 18px; accent-color: var(--forest); margin-top: 3px; flex-shrink: 0; }
.form-chk-lbl { font-size: 14px; color: var(--text); font-weight: 300; }
.form-err { font-size: 12px; color: var(--danger); margin-top: 4px; }
.form-submit {
  width: 100%; padding: 16px; background: var(--forest); border: none;
  color: var(--white); font-size: 12px; letter-spacing: 2px;
  text-transform: uppercase; font-weight: 500; margin-top: 8px;
  transition: all 0.4s var(--ease-out-expo);
}
.form-submit:hover { background: var(--forest-deep); }
.form-submit:disabled { opacity: 0.4; cursor: not-allowed; }
.bk-counter {
  display: flex; align-items: center;
  border: 1px solid rgba(0,0,0,0.15); height: 46px;
  background: var(--parchment);
}
.bk-counter.err { border-color: #dc2626; }
.bk-counter-btn {
  width: 46px; height: 100%; border: none; background: transparent;
  font-size: 20px; font-weight: 300; color: var(--forest);
  cursor: pointer; transition: background 0.15s; flex-shrink: 0;
}
.bk-counter-btn:hover:not(:disabled) { background: rgba(0,0,0,0.05); }
.bk-counter-btn:disabled { color: rgba(0,0,0,0.2); cursor: not-allowed; }
.bk-counter-val {
  flex: 1; text-align: center; font-size: 16px;
  font-family: var(--sans); color: var(--ink);
  border-left: 1px solid rgba(0,0,0,0.08);
  border-right: 1px solid rgba(0,0,0,0.08);
}
.bk-price-calc {
  margin-top: 16px; background: var(--cream);
  border: 1px solid rgba(0,0,0,0.07); padding: 16px 18px;
}
.bk-price-calc__row {
  display: flex; justify-content: space-between; align-items: baseline;
}
.bk-price-calc__label { font-size: 14px; color: var(--text-soft); font-weight: 300; }
.bk-price-calc__price {
  font-size: 22px; font-family: var(--serif); color: var(--forest);
  font-weight: 400; letter-spacing: -0.5px;
}
.bk-price-calc__hint { font-size: 12px; color: var(--text-soft); margin-top: 4px; font-style: italic; }
.bk-price-calc__options {
  display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px;
}
.bk-price-opt {
  font-size: 12px; padding: 4px 10px; border: 1px solid rgba(0,0,0,0.15);
  background: var(--white); color: var(--ink); cursor: pointer;
  font-family: var(--sans); transition: all 0.15s; white-space: nowrap;
}
.bk-price-opt.active {
  background: var(--forest); color: var(--white); border-color: var(--forest);
}
.bk-price-opt:hover:not(.active) { background: var(--parchment); }

/* ═══════════ FILTER ═══════════ */
.filter-panel {
  max-width: 1120px;
  margin: 0 auto 56px;
  display: grid;
  gap: 18px;
}
.hotel-search {
  display: grid;
  grid-template-columns: minmax(180px, 1.5fr) repeat(3, minmax(150px, 1fr));
  gap: 12px;
  padding: 22px;
  background: var(--white);
  border: 1px solid rgba(0,0,0,0.07);
  align-items: end;
}
.hotel-search-2col { grid-template-columns: minmax(220px,2fr) minmax(160px,1fr); }
.hotel-search-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.hotel-search-field label {
  font-size: 10px;
  line-height: 1.2;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--accent);
}
.hotel-search-field input,
.hotel-search-field select {
  width: 100%;
  height: 46px;
  min-height: 46px;
  border: 1px solid rgba(0,0,0,0.1);
  background: var(--parchment);
  color: var(--ink);
  font-family: var(--sans);
  font-size: 14px;
  padding: 0 14px;
  border-radius: 0;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
}
.hotel-search-field select {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%234A4A50' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;
  cursor: pointer;
}
.hotel-search-field input:focus,
.hotel-search-field select:focus {
  outline: none;
  border-color: var(--forest);
  background: var(--white);
}
.hotel-search-field input[type="date"] { color-scheme: light; cursor: pointer; }
.hotel-search-field input[type="date"]::-webkit-calendar-picker-indicator {
  opacity: 0.35; cursor: pointer;
  filter: sepia(1) saturate(2) hue-rotate(90deg);
}
.hotel-search-field input[type="number"] { -moz-appearance: textfield; }
.hotel-search-field input[type="number"]::-webkit-outer-spin-button,
.hotel-search-field input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; }
.acc-guest-counter {
  display: flex; align-items: center;
  height: 46px; border: 1px solid rgba(0,0,0,0.1);
  background: var(--parchment);
}
.acc-guest-btn {
  width: 46px; height: 100%; border: none; background: transparent;
  font-size: 20px; line-height: 1; color: var(--forest);
  cursor: pointer; transition: background 0.2s; flex-shrink: 0;
  font-weight: 300;
}
.acc-guest-btn:hover:not(:disabled) { background: rgba(0,0,0,0.05); }
.acc-guest-btn:disabled { color: rgba(0,0,0,0.2); cursor: not-allowed; }
.acc-guest-val {
  flex: 1; text-align: center; font-size: 14px;
  font-family: var(--sans); color: var(--ink);
  border-left: 1px solid rgba(0,0,0,0.08);
  border-right: 1px solid rgba(0,0,0,0.08);
}
.filter-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  align-items: start;
  gap: 18px;
}
.filter-row-label {
  padding-top: 11px;
  font-size: 11px;
  line-height: 1.2;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--accent);
}
.filter-bar { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-bottom: 0; width: 100%; box-sizing: border-box; }
.section > .filter-bar { margin-bottom: 56px; }
.filter-bar--secondary { margin-top: 0; margin-bottom: 0; }
.filter-pill {
  padding: 10px 24px; border: 1px solid rgba(0,0,0,0.08);
  background: none; font-size: 13px; color: var(--text); cursor: pointer;
  transition: all 0.3s; letter-spacing: 0.5px; white-space: nowrap;
}
.filter-pill.on { background: var(--forest); color: var(--white); border-color: var(--forest); }
@media (max-width: 720px) {
  .hotel-search, .hotel-search-2col { grid-template-columns: 1fr; padding: 16px; }
  .filter-row { grid-template-columns: 1fr; gap: 8px; }
  .filter-row-label { padding-top: 0; }
  .filter-bar { flex-wrap: nowrap; overflow-x: auto; justify-content: flex-start; padding-bottom: 4px; scrollbar-width: none; }
  .filter-bar::-webkit-scrollbar { display: none; }
}
@media (min-width: 721px) and (max-width: 1100px) {
  .hotel-search, .hotel-search-2col { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .hotel-search .hotel-search-field:last-child { grid-column: 1 / -1; }
}

/* ═══════════ CONTACT ═══════════ */
.contact-split > * { min-width: 0; }
.contact-split {
  display: grid; grid-template-columns: 1fr 1fr; gap: 80px;
  max-width: 1100px; margin: 0 auto;
}
.contact-line { display: flex; gap: 16px; margin-bottom: 28px; }
.contact-line-icon { font-size: 16px; color: var(--accent); width: 24px; flex-shrink: 0; margin-top: 4px; }
.contact-line-label { font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: var(--accent); margin-bottom: 4px; font-weight: 500; }
.contact-line-val { font-size: 15px; color: var(--ink); font-weight: 300; }

/* ═══════════ PKG CARDS ═══════════ */
.pkg-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(340px, 1fr)); gap: 24px; }
.pkg-card {
  border: 1px solid rgba(0,0,0,0.05); background: var(--white);
  padding: 36px; cursor: pointer; transition: all 0.5s var(--ease-out-expo);
}
.pkg-card:hover { transform: translateY(-6px); box-shadow: 0 24px 64px rgba(0,0,0,0.07); }
.pkg-card-tag { font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: var(--accent); margin-bottom: 14px; font-weight: 500; }
.pkg-card-name { font-family: var(--serif); font-size: 24px; font-weight: 400; color: var(--ink); margin-bottom: 10px; }
.pkg-card-brief { font-size: 14px; color: var(--text-soft); margin-bottom: 18px; line-height: 1.6; font-weight: 300; }
.pkg-card-row { display: flex; gap: 24px; font-size: 13px; color: var(--text-soft); margin-bottom: 18px; }
.pkg-card-price { font-family: var(--serif); font-size: 24px; color: var(--ink); }
.pkg-card-price small { font-size: 13px; color: var(--text-soft); font-family: var(--sans); }

.hotel-card-media {
  position: relative;
  height: 238px;
  margin: -36px -36px 24px;
  overflow: hidden;
  background: var(--cream);
}
.hotel-card-media__cover {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: transform 0.7s var(--ease-out-expo);
}
.pkg-card:hover .hotel-card-media__cover { transform: scale(1.04); }
.hotel-card-media__shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.5) 100%);
  pointer-events: none;
}
.hotel-card-media__caption {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 16px;
  color: var(--white);
  font-size: 13px;
  line-height: 1.45;
  text-shadow: 0 1px 12px rgba(0,0,0,0.35);
}
.hotel-card-media__thumbs {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 6px;
  align-items: center;
}
.hotel-card-media__thumbs img,
.hotel-card-media__thumbs span {
  width: 42px;
  height: 42px;
  border: 2px solid rgba(255,255,255,0.78);
  box-shadow: 0 8px 24px rgba(0,0,0,0.18);
}
.hotel-card-media__thumbs img {
  display: block;
  object-fit: cover;
}
.hotel-card-media__thumbs span {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(24,34,28,0.78);
  color: var(--white);
  font-size: 12px;
  font-weight: 500;
}

.hotel-gallery {
  margin-bottom: 48px;
}
.hotel-gallery__lead {
  position: relative;
  overflow: hidden;
  background: var(--cream);
  aspect-ratio: 16 / 10;
  margin-bottom: 12px;
}
.hotel-gallery__lead img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}
.hotel-gallery__caption {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 18px;
  padding: 10px 12px;
  background: rgba(24,34,28,0.74);
  color: var(--white);
  font-size: 13px;
  line-height: 1.45;
  backdrop-filter: blur(8px);
}
.hotel-gallery__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.hotel-gallery__item {
  position: relative;
  min-height: 170px;
  margin: 0;
  overflow: hidden;
  background: var(--cream);
}
.hotel-gallery__item img {
  width: 100%;
  height: 100%;
  min-height: 170px;
  display: block;
  object-fit: cover;
  transition: transform 0.6s var(--ease-out-expo);
}
.hotel-gallery__item:hover img { transform: scale(1.035); }
.hotel-gallery__item figcaption {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 10px;
  padding: 8px 10px;
  background: rgba(255,255,255,0.88);
  color: var(--ink);
  font-size: 12px;
  line-height: 1.4;
  backdrop-filter: blur(8px);
}

/* ═══════════ RESPONSIVE ═══════════ */
@media (max-width: 1024px) {
  .editorial { grid-template-columns: 1fr; }
  .editorial-img { min-height: 400px; }
  .editorial-text { padding: 60px 48px; }
  .dest-masonry { grid-template-columns: 1fr 1fr; }
  .dest-tile:nth-child(1) { grid-row: span 1; }
  .svc-grid { grid-template-columns: 1fr 1fr; }
  .val-grid { grid-template-columns: 1fr 1fr; }
  .blog-row { grid-template-columns: 1fr; max-width: 600px; }
  .footer-top { grid-template-columns: 1fr 1fr; }
  .pkg-detail-grid { grid-template-columns: 1fr; }
  .pkg-detail-sidebar { position: static; }
  .pkg-detail-side { position: static; }
  .contact-split { grid-template-columns: 1fr; gap: 48px; }
}
@media (max-width: 768px) {
  .nav { padding: 0 24px; }
  .nav-links { display: none; }
  .mobile-toggle { display: block; color: var(--white); }
  .nav.solid .mobile-toggle { color: var(--ink); }
  .section, .section-sm { padding: 80px 24px; }
  .editorial-text { padding: 48px 24px; }
  .dest-masonry { grid-template-columns: 1fr; }
  .svc-grid { grid-template-columns: 1fr; }
  .val-grid { grid-template-columns: 1fr; gap: 32px; }
  .page-hero { padding: 140px 24px 64px; }
  .pkg-detail-grid { padding: 60px 24px; gap: 40px; }
  .footer { padding: 64px 24px 36px; }
  .footer-top { grid-template-columns: 1fr; gap: 36px; }
  .footer-bottom { flex-direction: column; gap: 16px; }
  .hero-trust { display: none; }
  .hero-scroll { display: none; }
  .form-row { grid-template-columns: 1fr; }
  .modal-head, .modal-body { padding-left: 24px; padding-right: 24px; }
  .about-intro { grid-template-columns: 1fr; }
  .about-intro-img { max-height: 400px; }
  .hotel-card-media { height: 220px; }
  .hotel-gallery__grid { grid-template-columns: 1fr; }
  .pkg-detail-info { padding: 24px; }
  .pkg-info-row {
    grid-template-columns: 1fr;
    gap: 6px;
  }
  .pkg-info-val { text-align: left; }
  .pkg-cards { grid-template-columns: 1fr; }
  .pkg-card { padding: 24px; }
  .section > .filter-bar { margin-bottom: 36px; }
}
.about-intro {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  max-width: 1100px;
  margin: 0 auto;
  align-items: stretch;
}
.about-intro-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
`;

// ─── Components ───

function Nav({ page, onNav, scrolled, lang, setLang }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHome = page === 'home';
  const navClass = scrolled || !isHome ? 'solid' : 'transparent';

  return (
    <>
    <nav className={`nav ${navClass}`}>
      <div className="nav-logo" onClick={() => onNav('home')}>
        Let's Travel
      </div>
      <div className="nav-links">
        {[['destinations',t('nav_dest',lang)],['accommodation',t('nav_acc',lang)],['services',t('nav_svc',lang)],['about',t('nav_about',lang)],['values',t('nav_val',lang)],['blog',t('nav_blog',lang)],['contact',t('nav_contact',lang)]].map(([k,v]) => (
          <button key={k} className="nav-link" onClick={() => onNav(k)}>{v}</button>
        ))}
        <div className="lang-sw">{LANGS.map(l=><button key={l.code} className={`lang-btn ${lang===l.code?'active':''}`} onClick={()=>setLang(l.code)}>{l.label}</button>)}</div>
        <button className="nav-cta" onClick={() => onNav('contact')}>{t('nav_cta',lang)}</button>
      </div>
      <button className="mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)}>☰</button>
    </nav>
      {mobileOpen && (
        <div className="mobile-menu-overlay">
          <div className="mobile-menu-header">
            <div className="nav-logo" onClick={() => { onNav('home'); setMobileOpen(false); }} style={{color: 'var(--cream)'}}>
              Let's Travel
            </div>
            <button className="mobile-menu-close" onClick={() => setMobileOpen(false)}>✕</button>
          </div>
          <div className="mobile-menu-links">
            {[['home',t('nav_home',lang)],['destinations',t('nav_dest',lang)],['accommodation',t('nav_acc',lang)],['services',t('nav_svc',lang)],['about',t('nav_about',lang)],['values',t('nav_val',lang)],['blog',t('nav_blog',lang)],['contact',t('nav_contact',lang)]].map(([k,v]) => (
              <button key={k} className="mobile-menu-link" onClick={() => { onNav(k); setMobileOpen(false); }}>{v}</button>
            ))}
          </div>
          <div className="mobile-menu-footer">
            <div className="mobile-lang-sw">{LANGS.map(l=><button key={l.code} className={`mobile-lang-btn ${lang===l.code?'active':''}`} onClick={()=>setLang(l.code)}>{l.label}</button>)}</div>
            <button className="mobile-menu-cta" onClick={() => { onNav('contact'); setMobileOpen(false); }}>{t('nav_cta',lang)}</button>
            <div className="mobile-menu-contacts">
              <a href="tel:+3725101959">+372 510 1959</a>
              <a href="mailto:info@letstravel.ee">info@letstravel.ee</a>
            </div>
            <div className="mobile-menu-socials">
              <a href="https://www.instagram.com/letstravelestonia?igsh=cXhnMG05d256anE4&utm_source=qr" target="_blank" rel="noopener noreferrer" className="mobile-menu-social">IG</a>
              <a href="https://www.facebook.com/share/1DQ8ouNwYW/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="mobile-menu-social">FB</a>
              <span className="mobile-menu-social">TG</span>
              <span className="mobile-menu-social">WA</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Hero({ onNav, onSearch, lang }) {
  const [bgIndex, setBgIndex] = useState(0);
  const [dest, setDest] = useState('all');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState('');
  const [adults, setAdults] = useState('2');
  const [children, setChildren] = useState('0');
  const [type, setType] = useState('hotel');
  const destinations = getDestinations();

  useEffect(() => {
    const interval = setInterval(() => {
      setBgIndex(prev => (prev + 1) % HERO_BACKGROUND_IMAGES.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = () => {
    if (type === 'hotel') {
      onSearch({ dest, checkIn, checkOut, guests, type });
      onNav('accommodation');
    } else {
      onSearch({ dest, adults, children, type });
      if (dest !== 'all') {
        onNav('destination', Number(dest));
      } else {
        onNav('destinations');
      }
    }
  };

  const L = {
    dest:     { ru:'Направление',  et:'Sihtkoht',      lv:'Galamērķis',    en:'Destination' },
    allDest:  { ru:'Все страны',   et:'Kõik riigid',   lv:'Visas valstis', en:'All Countries' },
    cin:      { ru:'Дата заезда',  et:'Saabumine',     lv:'Ierašanās',     en:'Check-in' },
    cout:     { ru:'Дата выезда',  et:'Lahkumine',     lv:'Izbraukšana',   en:'Check-out' },
    guests:   { ru:'Кол-во гостей',et:'Külalised',     lv:'Viesi',         en:'Guests' },
    adults:   { ru:'Взрослых',     et:'Täiskasvanuid', lv:'Pieaugušie',    en:'Adults' },
    children: { ru:'Детей',        et:'Lapsi',         lv:'Bērni',         en:'Children' },
    type:     { ru:'Тип',          et:'Tüüp',          lv:'Tips',          en:'Type' },
    hotel:    { ru:'Отель',        et:'Hotell',        lv:'Viesnīca',      en:'Hotel' },
    tour:     { ru:'Тур',          et:'Pakett',        lv:'Paka',          en:'Tour' },
    search:   { ru:'Искать',       et:'Otsi',          lv:'Meklēt',        en:'Search' },
  };
  const lbl = k => (L[k][lang] || L[k].en);

  return (
    <section className="hero">
      <div className="hero-bg">
        {HERO_BACKGROUND_IMAGES.map((slide, index) => (
          <div
            key={slide.url}
            className={`hero-bg-layer ${index === bgIndex ? 'is-active' : ''}`}
            aria-hidden="true"
          >
            {index === 0 ? (
              <img
                src={slide.url}
                fetchpriority="high"
                decoding="async"
                style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}}
                alt=""
              />
            ) : (
              <div style={{backgroundImage:`url(${slide.url})`,backgroundSize:'cover',backgroundPosition:'center',position:'absolute',inset:0}} />
            )}
          </div>
        ))}
      </div>
      <div className="hero-overlay" />
      <div className="hero-grain" />
      <div className="hero-content">
        <img src={`${import.meta.env.BASE_URL}logo_white.svg`} alt="LetsTravel" style={{height:'clamp(44px, 6.5vw, 82px)',width:'auto',display:'block',margin:'0 auto 20px',opacity:0.95}} />
        <div className="hero-eyebrow">{t('hero_ey',lang)}</div>
        <h1>{t("hero_t1",lang)} <em>{t("hero_t2",lang)}</em></h1>
        <p className="hero-sub">{t("hero_sub",lang)}</p>
        <div className="hero-buttons">
          <button className="btn-hero-primary" onClick={() => onNav('contact')}>{t("hero_btn1",lang)}</button>
          <button className="btn-hero-secondary" onClick={() => onNav('destinations')}>{t("hero_btn2",lang)}</button>
        </div>
        <div className={`hero-search-bar type-${type === 'hotel' ? 'hotel' : 'tour'}`}>
          <div className="hero-search-bar-field">
            <label>{lbl('dest')}</label>
            <select value={dest} onChange={e => setDest(e.target.value)}>
              <option value="all">{lbl('allDest')}</option>
              {destinations.map(d => <option key={d.id} value={d.id}>{dName(d.id, lang)}</option>)}
            </select>
          </div>
          {type === 'hotel' ? (
            <>
              <div className="hero-search-bar-field">
                <label>{lbl('cin')}</label>
                <input type="date" lang="et" value={checkIn} onChange={e => setCheckIn(e.target.value)} />
              </div>
              <div className="hero-search-bar-field">
                <label>{lbl('cout')}</label>
                <input type="date" lang="et" value={checkOut} min={checkIn || undefined} onChange={e => setCheckOut(e.target.value)} />
              </div>
              <div className="hero-search-bar-field">
                <label>{lbl('guests')}</label>
                <input type="number" min="1" value={guests} placeholder="2" onChange={e => setGuests(e.target.value)} />
              </div>
            </>
          ) : (
            <>
              <div className="hero-search-bar-field">
                <label>{lbl('adults')}</label>
                <select value={adults} onChange={e => setAdults(e.target.value)}>
                  {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="hero-search-bar-field">
                <label>{lbl('children')}</label>
                <select value={children} onChange={e => setChildren(e.target.value)}>
                  {[0,1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </>
          )}
          <div className="hero-search-bar-field">
            <label>{lbl('type')}</label>
            <div className="hero-type-toggle">
              <button type="button" className={`hero-type-btn${type === 'hotel' ? ' active' : ''}`} onClick={() => setType('hotel')}>{lbl('hotel')}</button>
              <button type="button" className={`hero-type-btn${type === 'tour' ? ' active' : ''}`} onClick={() => setType('tour')}>{lbl('tour')}</button>
            </div>
          </div>
          <button className="hero-search-btn" onClick={handleSearch}>{lbl('search')}</button>
        </div>
      </div>
      <div className="hero-scroll">
        <span className="hero-scroll-text">{t("hero_scroll",lang)}</span>
        <div className="hero-scroll-line" />
      </div>
      <div className="hero-trust">
        {['Condé Nast Traveller','Virtuoso Member','Luxury Travel Advisor','Travel + Leisure'].map(t => (
          <span key={t} className="hero-trust-item">{t}</span>
        ))}
      </div>
      {HERO_BACKGROUND_IMAGES[bgIndex].credit && (
        <span className="hero-photo-credit">{HERO_BACKGROUND_IMAGES[bgIndex].credit}</span>
      )}
    </section>
  );
}

function HomeDestinations({ onNav, lang }) {
  const [filter, setFilter] = useState('all');
  const all = getDestinations();
  const list = filter === 'all' ? all : all.filter(d =>
    Array.isArray(d.categories) ? d.categories.includes(filter) : (d.categories || '').includes(filter)
  );
  return (
    <section className="section">
      <Reveal>
        <div style={{textAlign:'center', marginBottom: 40}}>
          <div className="sec-label">{t("s_dest",lang)}</div>
          <h2 className="sec-title">{t("s_dest_t",lang)}</h2>
          <p className="sec-sub" style={{margin:'0 auto'}}>{t("s_dest_s",lang)}</p>
        </div>
      </Reveal>
      <div className="filter-bar">
        {TOUR_CATS.map(c => (
          <button
            key={c.key}
            className={`filter-pill ${filter === c.key ? 'on' : ''}`}
            onClick={() => setFilter(c.key)}
          >
            {c.label(lang)}
          </button>
        ))}
      </div>
      <div className="dest-masonry">
        {list.map((d, i) => (
          <div key={d.id} className="dest-tile" onClick={() => onNav('destination', d.id)} style={{animationDelay: `${i*0.1}s`}}>
            <img src={d.image} alt={d.name} loading="lazy" />
            <div className="dest-tile-overlay" />
            <div className="dest-tile-info">
              <div className="dest-tile-region">{dRegion(d.id,lang)}</div>
              <div className="dest-tile-name">{dName(d.id,lang)}</div>
              <div className="dest-tile-brief">{dBrief(d.id,lang)}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function HomeAbout({ onNav, lang }) {
  return (
    <div className="editorial">
      <div className="editorial-img">
        <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=900&q=80&fm=webp" alt="Beach" loading="lazy" />
      </div>
      <Reveal className="editorial-text">
        <div className="sec-label">{t("s_about",lang)}</div>
        <h2 className="sec-title">{t("s_about_t",lang)}</h2>
        <p>{t("s_about_p1",lang)}</p>
        <p>{t("s_about_p2",lang)}</p>
        <button className="btn-refined" onClick={() => onNav('about')}>
          <span>{t("s_about_more",lang)}</span>
          <span className="btn-refined-line" />
          <span>→</span>
        </button>
      </Reveal>
    </div>
  );
}

const CLIENTS = Array.from({length: 14}, (_, i) => `${import.meta.env.BASE_URL}clients/client-${String(i + 1).padStart(2, '0')}.svg`);

function PartnersCarousel() {
  const doubled = [...CLIENTS, ...CLIENTS];
  return (
    <div className="partners-strip">
      <div className="partners-track">
        {doubled.map((src, i) => (
          <img key={i} src={src} alt={`Partner ${(i % 14) + 1}`} className="partner-logo" />
        ))}
      </div>
    </div>
  );
}

function HomeServices({ onNav, lang }) {
  return (
    <section className="section section-dark">
      <Reveal>
        <div style={{textAlign:'center', marginBottom: 64}}>
          <div className="sec-label" style={{color:'var(--sand)'}}>{t('s_svc',lang)}</div>
          <h2 className="sec-title sec-title-white">{t("s_svc_t",lang)}</h2>
          <p className="sec-sub sec-sub-light" style={{margin:'0 auto'}}>{t('s_svc_s',lang)}</p>
        </div>
      </Reveal>
      <div className="svc-grid">
        {getServices(lang).map((s, i) => (
          <Reveal key={s.id} delay={i * 0.08}>
            <div className="svc-card" onClick={() => onNav('services')}>
              <span className="svc-icon">{s.icon}</span>
              <div className="svc-name">{s.name}</div>
              <div className="svc-desc">{s.desc}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function HomeValues({ lang }) {
  return (
    <section className="section section-cream">
      <Reveal>
        <div style={{textAlign:'center', marginBottom: 64}}>
          <div className="sec-label">{t("s_val",lang)}</div>
          <h2 className="sec-title">{t("s_val_t",lang)}</h2>
        </div>
      </Reveal>
      <div className="val-grid">
        {getValues(lang).slice(0, 3).map((v, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div>
              <div className="val-num">{v.icon}</div>
              <div className="val-title">{v.title}</div>
              <div className="val-desc">{v.desc}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function HomeQuote({ lang }) {
  return (
    <ParallaxImg src="https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1600&q=80&fm=webp" height={500} intensity={0.12}>
      <Reveal>
        <div style={{maxWidth: 600}}>
          <p style={{fontFamily:'var(--serif)', fontSize:'clamp(24px,3vw,38px)', fontWeight:400, fontStyle:'italic', color:'var(--white)', lineHeight:1.4, marginBottom:12}}>{t("quote",lang)}</p>
          <span style={{fontSize:12, letterSpacing:3, textTransform:'uppercase', color:'var(--sand-light)'}}>{t("quote_a",lang)}</span>
        </div>
      </Reveal>
    </ParallaxImg>
  );
}

const FALLBACK_IMGS = [
  'https://images.unsplash.com/photo-1530841377377-3ff06c0ca713?w=600&q=80&fm=webp',
  'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=600&q=80&fm=webp',
  'https://images.unsplash.com/photo-1528164344705-47542687000d?w=600&q=80&fm=webp'
];

function HomeBlog({ onNav, lang }) {
  const [posts, setPosts] = useState(getBlog(lang));
  useEffect(() => {
    adminAPI.getPosts().then(data => {
      if (data.length > 0) setPosts(mapApiPosts(data.slice(0, 3), lang));
    }).catch(() => {});
  }, [lang]);
  return (
    <section className="section">
      <Reveal>
        <div style={{textAlign:'center', marginBottom: 64}}>
          <div className="sec-label">{t("s_blog",lang)}</div>
          <h2 className="sec-title">{t("s_blog_t",lang)}</h2>
        </div>
      </Reveal>
      <div className="blog-row">
        {posts.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <div className="blog-card" style={{ cursor: 'default' }}>
              <div className="blog-card-img">
                <img src={p.image || FALLBACK_IMGS[i % 3]} alt="" loading="lazy" />
              </div>
              <div className="blog-card-title">{p.title}</div>
              <button className="btn-refined" style={{marginTop:12}} onClick={() => onNav('blog-post', p.id)}>
                <span>{t('btn_read',lang)}</span><span className="btn-refined-line"/><span>→</span>
              </button>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function HomeCTA({ onNav, lang }) {
  return (
    <section className="section-sm section-sand" style={{textAlign:'center'}}>
      <Reveal>
        <div className="sec-label">{t("s_cta_l",lang)}</div>
        <h2 className="sec-title" style={{marginBottom: 16}}>{t("s_cta_t",lang)}</h2>
        <p className="sec-sub" style={{margin:'0 auto 36px'}}>{t('s_cta_s',lang)}</p>
        <button className="btn-solid" onClick={() => onNav('contact')}>{t('s_cta_btn',lang)}</button>
      </Reveal>
    </section>
  );
}

function Footer({ onNav, lang }) {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-top">
          <div>
            <div className="footer-brand">Let's Travel</div>
            <p className="footer-tagline">{t("ft_tag",lang)}</p>
          </div>
          <div>
            <div className="footer-col-title">{t("ft_nav",lang)}</div>
            {[['home',t('nav_home',lang)],['destinations',t('nav_dest',lang)],['accommodation',t('nav_acc',lang)],['services',t('nav_svc',lang)],['about',t('nav_about',lang)],['values',t('nav_val',lang)],['blog',t('nav_blog',lang)],['contact',t('nav_contact',lang)],['tingimused',t('ft_terms',lang)]].map(([k,v]) => (
              <button key={k} className="footer-link" onClick={() => onNav(k)}>{v}</button>
            ))}
          </div>
          <div>
            <div className="footer-col-title">{t("ft_dest",lang)}</div>
            {getDestinations().map(d => (
              <button key={d.id} className="footer-link" onClick={() => onNav('destination', d.id)}>{dName(d.id,lang)}</button>
            ))}
          </div>
          <div>
            <div className="footer-col-title">{t("ft_contact",lang)}</div>
            <a href="tel:+3725101959" className="footer-link">+372 510 1959</a>
            <a href="mailto:info@letstravel.ee" className="footer-link">info@letstravel.ee</a>
            <p className="footer-link" style={{cursor:'default'}}>Tallinn, Estonia</p>
            <div style={{display:'flex', gap:12, marginTop:20}}>
              <a href="https://www.instagram.com/letstravelestonia?igsh=cXhnMG05d256anE4&utm_source=qr" target="_blank" rel="noopener noreferrer" className="footer-social">IG</a>
              <a href="https://www.facebook.com/share/1DQ8ouNwYW/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="footer-social">FB</a>
              <span className="footer-social">TG</span>
              <span className="footer-social">WA</span>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <span className="footer-copy">{t("ft_copy",lang)} &nbsp;·&nbsp; Reg. nr. 11273008</span>
          <div style={{display:'flex', gap:16, alignItems:'center'}}>
            <button className="footer-link" style={{fontSize:12, opacity:0.5}} onClick={() => onNav('tingimused')}>{t('ft_terms',lang)}</button>
            <button className="footer-link" style={{fontSize:10, opacity:0.15}} onClick={() => onNav('admin')}>•</button>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── PAGES ───

function HomePage({ onNav, onSearch, lang }) {
  return (
    <>
      <Hero onNav={onNav} onSearch={onSearch} lang={lang} />
      <HomeDestinations onNav={onNav} lang={lang} />
      <HomeAbout onNav={onNav} lang={lang} />
      <PartnersCarousel />
      <HomeServices onNav={onNav} lang={lang} />
      <HomeQuote lang={lang} />
      <HomeValues lang={lang} />
      <HomeBlog onNav={onNav} lang={lang} />
      <HomeCTA onNav={onNav} lang={lang} />
    </>
  );
}

const TOUR_CATS = [
  { key: 'all',    label: lang => t('cat_all', lang) },
  { key: 'hot',    label: lang => t('cat_hot', lang) },
  { key: 'beach',  label: lang => t('cat_beach', lang) },
  { key: 'family', label: lang => t('cat_family', lang) },
  { key: 'exotic', label: lang => t('cat_exotic', lang) },
];

const DEST_SORTS = [
  { key:'default', label: lg => ({ru:'Без сортировки',et:'Vaikimisi',lv:'Bez kārtošanas',en:'No Sorting'})[lg]||'No Sorting' },
  { key:'az',      label: lg => ({ru:'А → Я',et:'A → Z',lv:'A → Z',en:'A → Z'})[lg]||'A → Z' },
  { key:'pkgs',    label: lg => ({ru:'По пакетам',et:'Pakettide järgi',lv:'Pēc paketēm',en:'By Packages'})[lg]||'By Packages' },
];

function DestinationsPage({ onNav, lang }) {
  const [catFilter, setCatFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const all = getDestinations();

  let list = all.filter(d => {
    const matchCat = catFilter === 'all' || (Array.isArray(d.categories) ? d.categories.includes(catFilter) : (d.categories||'').includes(catFilter));
    const matchText = !searchText.trim() || dName(d.id, lang).toLowerCase().includes(searchText.trim().toLowerCase());
    return matchCat && matchText;
  });
  if (sortBy === 'az')   list = [...list].sort((a,b) => dName(a.id,lang).localeCompare(dName(b.id,lang)));
  if (sortBy === 'pkgs') list = [...list].sort((a,b) => (b.packages?.length||0) - (a.packages?.length||0));

  const SL = {
    search:      { ru:'Поиск направления', et:'Sihtkoha otsing', lv:'Meklēt galamērķi', en:'Search destination' },
    placeholder: { ru:'Турция, Греция…',   et:'Türgi, Kreeka…',  lv:'Turcija, Grieķija…', en:'Turkey, Greece…' },
    sort:        { ru:'Сортировка',        et:'Sorteerimine',    lv:'Kārtošana',          en:'Sort by' },
    cat:         { ru:'Категория',         et:'Kategooria',      lv:'Kategorija',         en:'Category' },
  };
  const sl = k => SL[k][lang] || SL[k].en;

  return (
    <>
      <div className="page-hero">
        <div className="page-hero-bg" style={{backgroundImage:"url('https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1600&q=80&fm=webp')"}} />
        <div className="page-hero-content">
          <div className="sec-label" style={{color:'var(--sand)'}}>{t('dp_l',lang)}</div>
          <h1 className="sec-title sec-title-white">{t("dp_t",lang)}</h1>
          <p className="sec-sub sec-sub-light">Every place is a story waiting to be told</p>
        </div>
      </div>
      <section className="section">
        <div className="filter-panel">
          <div className="hotel-search hotel-search-2col">
            <div className="hotel-search-field">
              <label>{sl('search')}</label>
              <input
                type="text"
                value={searchText}
                placeholder={sl('placeholder')}
                onChange={e => setSearchText(e.target.value)}
              />
            </div>
            <div className="hotel-search-field">
              <label>{sl('sort')}</label>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                {DEST_SORTS.map(s => <option key={s.key} value={s.key}>{s.label(lang)}</option>)}
              </select>
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-row-label">{sl('cat')}</div>
            <div className="filter-bar">
              {TOUR_CATS.map(c => (
                <button key={c.key} className={`filter-pill ${catFilter===c.key?'on':''}`} onClick={() => setCatFilter(c.key)}>
                  {c.label(lang)}
                </button>
              ))}
            </div>
          </div>
        </div>
        {list.length === 0 ? (
          <div style={{textAlign:'center',padding:'80px 0',color:'var(--text-soft)'}}>
            <p style={{fontSize:18}}>{lang==='ru'?'Направления не найдены':'No destinations found'}</p>
          </div>
        ) : (
          <div className="dest-masonry">
            {list.map(d => (
              <div key={d.id} className="dest-tile" onClick={() => onNav('destination', d.id)}>
                <img src={d.image} alt={d.name} loading="lazy" />
                <div className="dest-tile-overlay" />
                <div className="dest-tile-info">
                  <div className="dest-tile-region">{dRegion(d.id,lang)} · {d.packages.length} {d.packages.length === 1 ? 'package' : 'packages'}</div>
                  <div className="dest-tile-name">{dName(d.id,lang)}</div>
                  <div className="dest-tile-brief">{dBrief(d.id,lang)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function DestinationPage({ id, onNav, onBook, lang, heroSearch }) {
  const dest = getDestinations().find(d => d.id === id);
  const allPkgs = getPackages().filter(p => Number(p.destinationId) === id);
  const hotels = getHotels().filter(h => Number(h.destinationId) === id && h.status !== 'inactive');
  if (!dest) return null;
  const searchAdults = heroSearch?.type === 'tour' ? Number(heroSearch.adults) || 0 : 0;
  const searchChildren = heroSearch?.type === 'tour' ? Number(heroSearch.children) || 0 : 0;
  const totalPeople = searchAdults + searchChildren;
  const pkgs = totalPeople > 0
    ? allPkgs.filter(p => {
        if (Array.isArray(p.groupPricing) && p.groupPricing.length > 0) {
          return p.groupPricing.some(row =>
            Number(row.adults) + Number(row.children) >= totalPeople
          );
        }
        if (p.peopleCount) return Number(p.peopleCount) >= totalPeople;
        return true;
      })
    : allPkgs;
  return (
    <>
      <div className="page-hero" style={{minHeight: 480}}>
        <div className="page-hero-bg" style={{backgroundImage:`url(${dest.image})`, opacity: 0.35}} />
        <div className="page-hero-content">
          <button className="btn-refined" onClick={() => onNav('destinations')} style={{color:'var(--sand)', marginBottom: 24}}>
            <span>{t("btn_all_d",lang)}</span>
          </button>
          <h1 className="sec-title sec-title-white" style={{fontSize:'clamp(40px,5vw,64px)'}}>{dName(dest.id,lang)}</h1>
          <p className="sec-sub sec-sub-light" style={{maxWidth:500}}>{dBrief(dest.id,lang)}</p>
        </div>
      </div>
      <section className="section">
        <div style={{maxWidth: 900, margin: '0 auto'}}>
          <Reveal>
            <p style={{fontSize:17, lineHeight:1.9, fontWeight:300, marginBottom:64, color:'var(--text)'}}>{dDescription(dest.id, lang)}</p>
          </Reveal>
          <Reveal>
            <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:28, flexWrap:'wrap'}}>
              <div className="sec-label" style={{marginBottom:0}}>{t('pkg_avail',lang)}</div>
              {totalPeople > 0 && (
                <span style={{
                  fontSize:11, letterSpacing:'1.5px', textTransform:'uppercase',
                  background:'var(--forest)', color:'#fff',
                  padding:'4px 12px', fontWeight:500
                }}>
                  {searchAdults} {lang==='ru'?'взр.':lang==='et'?'täisk.':lang==='lv'?'pieaug.':'adult(s)'}
                  {searchChildren > 0 && ` + ${searchChildren} ${lang==='ru'?'дет.':lang==='et'?'last':lang==='lv'?'bērni':'child(ren)'}`}
                </span>
              )}
            </div>
          </Reveal>
          <div className="pkg-cards">
            {pkgs.map((p, i) => (
              <Reveal key={p.id} delay={i*0.1}>
                <div className="pkg-card" onClick={() => onNav('package', p.id)}>
                  {pPhotos(p).length > 0 && (
                    <HotelCardPhotoPreview hotel={p} lang={lang} photos={pPhotos(p)} />
                  )}
                  <div className="pkg-card-tag">{t('pkg_type',lang)} · {pkgFormat(p.format,lang)}</div>
                  <div className="pkg-card-name">{pName(p.id,lang)}</div>
                  <div className="pkg-card-brief">{pBrief(p.id,lang)}</div>
                  <div className="pkg-card-row"><span>{p.duration}</span><span>{p.season}</span></div>
                  <div className="pkg-card-price">{p.priceMode==='from'&&<small>{t('l_from',lang)} </small>}{p.price.toLocaleString()} {p.currency}</div>
                  <button className="btn-refined" style={{marginTop:12}}><span>{t('btn_det',lang)}</span><span className="btn-refined-line"/><span>→</span></button>
                </div>
              </Reveal>
            ))}
          </div>
          {hotels.length > 0 && (
            <>
              <Reveal>
                <div className="sec-label" style={{marginTop:72, marginBottom:28}}>{t('nav_acc',lang)}</div>
              </Reveal>
              <div className="pkg-cards">
                {hotels.map((h, i) => (
                  <Reveal key={h.id} delay={i*0.1}>
                    <div className="pkg-card" onClick={() => onNav('accommodation-detail', h.id)} style={{cursor:'pointer'}}>
                      {hPhotos(h).length > 0 && (
                        <HotelCardPhotoPreview hotel={h} lang={lang} />
                      )}
                      <div className="pkg-card-tag">{h.stars}{h.format ? ` · ${pkgFormat(h.format, lang)}` : ''}{h.isSpecialOffer ? ' · ⭐' : ''}</div>
                      <div className="pkg-card-name">{hName(h, lang)}</div>
                      <div className="pkg-card-brief">{hBrief(h, lang)}</div>
                      {(hRoomType(h, lang) || hMealType(h, lang) || hPeopleCount(h) > 0) && (
                        <div className="pkg-card-row">
                          {hRoomType(h, lang) && <span>{hRoomType(h, lang)}</span>}
                          {hMealType(h, lang) && <span>{hMealType(h, lang)}</span>}
                          {hPeopleCount(h) > 0 && <span>{hPeopleCount(h)} {lang === 'ru' ? 'чел.' : 'guests'}</span>}
                        </div>
                      )}
                      <div className="pkg-card-price">
                        <small>{t('l_from',lang)} </small>
                        {Number(h.pricePerNight).toLocaleString()} {h.currency || '€'}
                        <small> / {t('acc_ppn',lang)}</small>
                      </div>
                      <button className="btn-refined" style={{marginTop:12}}><span>{t('btn_det',lang)}</span><span className="btn-refined-line"/><span>→</span></button>
                    </div>
                  </Reveal>
                ))}
              </div>
            </>
          )}
          <Reveal>
            <div style={{background:'var(--cream)', padding:'60px', textAlign:'center', marginTop:80}}>
              <h3 className="sec-title" style={{fontSize:30, marginBottom:12}}>{t('nf_t',lang)}</h3>
              <p className="sec-sub" style={{margin:'0 auto 28px'}}>{t('nf_s',lang)}</p>
              <button className="btn-solid" onClick={() => onNav('contact')}>{t('btn_custom',lang)}</button>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function PackagePage({ id, onNav, onBook, lang }) {
  const [selectedGroupIdx, setSelectedGroupIdx] = useState(0);
  const pkg = getPackages().find(p => p.id === id);
  const dest = pkg ? getDestinations().find(d => d.id === Number(pkg.destinationId)) : null;
  if (!pkg || !dest) return null;
  const photos = pPhotos(pkg);
  const heroImg = photos[0]?.src || dest.image;
  const descText = pDescription(pkg.id, lang);
  const groupPricing = Array.isArray(pkg.groupPricing) && pkg.groupPricing.length > 0 ? pkg.groupPricing : null;
  const selectedGroup = groupPricing ? groupPricing[Math.min(selectedGroupIdx, groupPricing.length - 1)] : null;
  const displayPrice = selectedGroup ? Number(selectedGroup.price) : Number(pkg.price);
  return (
    <>
      <div className="page-hero">
        <div className="page-hero-bg" style={{backgroundImage:`url(${heroImg})`, opacity: 0.25}} />
        <div className="page-hero-content">
          <button className="btn-refined" onClick={() => onNav('destination', dest.id)} style={{color:'var(--sand)', marginBottom: 24}}>
            <span>←</span><span>{dName(dest.id,lang)}</span>
          </button>
          <h1 className="sec-title sec-title-white" style={{fontSize:'clamp(36px,4.5vw,56px)'}}>{pName(pkg.id,lang)}</h1>
          <p className="sec-sub sec-sub-light">{pBrief(pkg.id,lang)}</p>
        </div>
      </div>
      <div className="pkg-detail-grid">
        <div className="pkg-detail-main">
          {photos.length > 0 && (
            <Reveal>
              <HotelDetailGallery hotel={pkg} photos={photos} lang={lang} />
            </Reveal>
          )}
          {descText && (
            <>
              <Reveal><h3 className="pkg-h3">{lang==='ru'?'Описание тура':lang==='et'?'Reisikirjeldus':lang==='lv'?'Ceļojuma apraksts':'Tour Description'}</h3></Reveal>
              {descText.split('\n').filter(Boolean).map((d, i) => (
                <Reveal key={i} delay={i*0.04}><p style={{fontSize:16, lineHeight:1.85, fontWeight:300, marginBottom:12, color:'var(--text)'}}>{d}</p></Reveal>
              ))}
            </>
          )}
        </div>
        <div className="pkg-detail-sidebar">
          <div className="pkg-sidebar-card">
            <div className="pkg-sidebar-price">{displayPrice.toLocaleString()} {pkg.currency}</div>
            {groupPricing ? (
              <div className="pkg-group-pricing">
                <div className="pkg-group-pricing__label">
                  {lang==='ru'?'Состав группы':lang==='et'?'Grupi koosseis':lang==='lv'?'Grupas sastāvs':'Group composition'}
                </div>
                <select
                  className="pkg-group-pricing__select"
                  value={Math.min(selectedGroupIdx, groupPricing.length - 1)}
                  onChange={e => setSelectedGroupIdx(Number(e.target.value))}
                >
                  {groupPricing.map((row, i) => {
                    const adults = Number(row.adults);
                    const children = Number(row.children);
                    const price = Number(row.price);
                    let label;
                    if (lang === 'ru') {
                      label = `${adults} ${adults===1?'взрослый':adults<5?'взрослых':'взрослых'}`;
                      if (children > 0) label += ` + ${children} ${children===1?'ребёнок':children<5?'ребёнка':'детей'}`;
                      label += ` — ${price.toLocaleString()} ${pkg.currency}`;
                    } else if (lang === 'et') {
                      label = `${adults} täisk.`;
                      if (children > 0) label += ` + ${children} laps${children>1?'t':''}`;
                      label += ` — ${price.toLocaleString()} ${pkg.currency}`;
                    } else if (lang === 'lv') {
                      label = `${adults} pieaugušie`;
                      if (children > 0) label += ` + ${children} bērn${children===1?'s':'i'}`;
                      label += ` — ${price.toLocaleString()} ${pkg.currency}`;
                    } else {
                      label = `${adults} adult${adults>1?'s':''}`;
                      if (children > 0) label += ` + ${children} child${children>1?'ren':''}`;
                      label += ` — ${price.toLocaleString()} ${pkg.currency}`;
                    }
                    return <option key={i} value={i}>{label}</option>;
                  })}
                </select>
              </div>
            ) : (
              <div className="pkg-sidebar-from">{pPriceCaption(pkg, lang)}</div>
            )}
            <div className="pkg-sidebar-meta">
              {[
                [t('l_dur',lang), pDuration(pkg,lang)],
                [t('l_season',lang), pSeason(pkg,lang)],
                [t('l_style',lang), pkgFormat(pkg.format,lang)],
                [t('l_dest',lang), dName(dest.id,lang)],
                ...(pDateRange(pkg) ? [[lang==='ru'?'Даты':lang==='et'?'Kuupäevad':lang==='lv'?'Datumi':'Dates', pDateRange(pkg)]] : []),
                ...(pRoomType(pkg,lang) ? [[lang==='ru'?'Тип номера':lang==='et'?'Toa tüüp':lang==='lv'?'Numura tips':'Room Type', pRoomType(pkg,lang)]] : []),
                ...(pMealType(pkg,lang) ? [[lang==='ru'?'Тип питания':lang==='et'?'Toitlustus':lang==='lv'?'Ēdināšana':'Meal Plan', pMealType(pkg,lang)]] : []),
                ...(pkg.peopleCount > 0 ? [[lang==='ru'?'Гости':lang==='et'?'Külalised':lang==='lv'?'Viesi':'Guests', pkg.peopleCount]] : []),
              ].filter(([,v]) => v).map(([l,v]) => (
                <div key={l} className="pkg-sidebar-row"><span>{l}</span><span>{v}</span></div>
              ))}
            </div>
            <button className="btn-solid" style={{width:'100%', textAlign:'center'}} onClick={() => onBook(pkg)}>{t('btn_book',lang)}</button>
            <button className="btn-border" style={{width:'100%', textAlign:'center', marginTop:12}} onClick={() => onNav('contact')}>{t('btn_ask',lang)}</button>
          </div>
        </div>
      </div>
    </>
  );
}

const HOTEL_STARS = [
  { key: 'all', label: (lang) => ({ ru:'Все', et:'Kõik', lv:'Visi', en:'All' })[lang] || 'All' },
  { key: '5★', label: () => '5★' },
  { key: '4★', label: () => '4★' },
  { key: '3★', label: () => '3★' },
  { key: 'Boutique', label: () => 'Boutique' },
  { key: 'Resort', label: () => 'Resort' },
];

const HOTEL_FORMATS = [
  { key: 'all', label: (lang) => ({ ru:'Все форматы', et:'Kõik formaadid', lv:'Visi formāti', en:'All Styles' })[lang] || 'All Styles' },
  { key: 'Family', label: (lang) => pkgFormat('Family', lang) },
  { key: 'Romance', label: (lang) => pkgFormat('Romance', lang) },
  { key: 'Luxury', label: (lang) => pkgFormat('Luxury', lang) },
  { key: 'Adventure', label: (lang) => pkgFormat('Adventure', lang) },
];

const HOTEL_PRICE_SORTS = [
  { key: 'default', label: (lang) => ({ ru:'Без сортировки', et:'Sorteerimata', lv:'Bez kārtošanas', en:'No Sorting' })[lang] || 'No Sorting' },
  { key: 'low-high', label: (lang) => ({ ru:'Сначала дешевле', et:'Odavamad ees', lv:'Lētākie vispirms', en:'Lowest Price First' })[lang] || 'Lowest Price First' },
  { key: 'high-low', label: (lang) => ({ ru:'Сначала дороже', et:'Kallimad ees', lv:'Dārgākie vispirms', en:'Highest Price First' })[lang] || 'Highest Price First' },
];

function HotelCardPhotoPreview({ hotel, lang, photos: photosProp }) {
  const photos = photosProp || hPhotos(hotel);
  const cover = photos[0];
  const thumbs = photos.slice(1, 4);
  const extraCount = Math.max(photos.length - 4, 0);

  if (!cover) return null;

  return (
    <div className="hotel-card-media">
      <img className="hotel-card-media__cover" src={cover.src} alt={hName(hotel, lang)} loading="lazy" />
      <div className="hotel-card-media__shade" />
      {cover.caption && <div className="hotel-card-media__caption">{cover.caption}</div>}
      {photos.length > 1 && (
        <div className="hotel-card-media__thumbs" aria-label={lang === 'ru' ? 'Фотографии отеля' : 'Hotel photos'}>
          {thumbs.map((photo, index) => (
            <img key={`${photo.url}-${index}`} src={photo.src} alt="" loading="lazy" />
          ))}
          {extraCount > 0 && <span>+{extraCount}</span>}
        </div>
      )}
    </div>
  );
}

function HotelDetailGallery({ hotel, photos, lang }) {
  const cover = photos[0];
  const galleryLabel = lang === 'ru' ? 'Галерея отеля' : lang === 'et' ? 'Hotelli galerii' : lang === 'lv' ? 'Viesnīcas galerija' : 'Hotel Gallery';

  if (!cover) return null;

  return (
    <section className="hotel-gallery" aria-label={galleryLabel}>
      <div className="hotel-gallery__lead">
        <img src={cover.src} alt={cover.caption || hName(hotel, lang)} loading="lazy" />
        {cover.caption && <div className="hotel-gallery__caption">{cover.caption}</div>}
      </div>
      {photos.length > 1 && (
        <div className="hotel-gallery__grid">
          {photos.slice(1).map((photo, index) => (
            <figure key={`${photo.url}-${index}`} className="hotel-gallery__item">
              <img src={photo.src} alt={photo.caption || `${hName(hotel, lang)} ${index + 2}`} loading="lazy" />
              {photo.caption && <figcaption>{photo.caption}</figcaption>}
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}

function AccommodationPage({ onNav, lang, initialSearch }) {
  const [filter, setFilter] = useState('all');
  const [destinationFilter, setDestinationFilter] = useState(initialSearch?.dest || 'all');
  const [formatFilter, setFormatFilter] = useState('all');
  const [checkIn, setCheckIn] = useState(initialSearch?.checkIn || '');
  const [checkOut, setCheckOut] = useState(initialSearch?.checkOut || '');
  const [guestFilter, setGuestFilter] = useState(initialSearch?.guests || '');
  const [priceSort, setPriceSort] = useState('default');
  const all = getHotels().filter(h => h.status !== 'inactive');
  const hotelDestinationIds = new Set(all.map(h => Number(h.destinationId)).filter(id => !Number.isNaN(id)));
  const hotelDestinations = getDestinations().filter(d => hotelDestinationIds.has(Number(d.id)));
  const destinationOptions = hotelDestinations.length > 0 ? hotelDestinations : getDestinations();
  const selectedGuests = Number(guestFilter) || 0;
  const searchNights = checkIn && checkOut
    ? Math.max(0, Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000))
    : 0;
  const list = all.filter(h => {
    const matchesCategory = filter === 'all' || h.stars === filter;
    const matchesDestination = destinationFilter === 'all' || Number(h.destinationId) === Number(destinationFilter);
    const matchesFormat = formatFilter === 'all' || h.format === formatFilter;
    const matchesGuests = !selectedGuests || !hPeopleCount(h) || hPeopleCount(h) >= selectedGuests;
    return matchesCategory && matchesDestination && matchesFormat && matchesGuests;
  }).sort((a, b) => {
    if (priceSort === 'low-high') return (Number(a.pricePerNight) || 0) - (Number(b.pricePerNight) || 0);
    if (priceSort === 'high-low') return (Number(b.pricePerNight) || 0) - (Number(a.pricePerNight) || 0);
    return 0;
  });
  return (
    <>
      <div className="page-hero">
        <div className="page-hero-bg" style={{backgroundImage:"url('https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1600&q=80&fm=webp')"}} />
        <div className="page-hero-content">
          <div className="sec-label" style={{color:'var(--sand)'}}>{t('acc_l',lang)}</div>
          <h1 className="sec-title sec-title-white">{t('acc_t',lang)}</h1>
          <p className="sec-sub sec-sub-light">{t('acc_s',lang)}</p>
        </div>
      </div>
      <section className="section">
        <div className="filter-panel">
          <div className="hotel-search">
            <div className="hotel-search-field">
              <label>{t('l_dest', lang)}</label>
              <select value={destinationFilter} onChange={(e) => setDestinationFilter(e.target.value)}>
                <option value="all">{({ ru:'Все страны', et:'Kõik riigid', lv:'Visas valstis', en:'All Countries' })[lang] || 'All Countries'}</option>
                {destinationOptions.map(dest => (
                  <option key={dest.id} value={dest.id}>{dName(dest.id, lang)}</option>
                ))}
              </select>
            </div>
            <div className="hotel-search-field">
              <label>{lang === 'ru' ? 'Дата заезда' : lang === 'et' ? 'Saabumine' : lang === 'lv' ? 'Ierašanās' : 'Check-in'}</label>
              <input type="date" lang="et" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
            </div>
            <div className="hotel-search-field">
              <label>{lang === 'ru' ? 'Дата выезда' : lang === 'et' ? 'Lahkumine' : lang === 'lv' ? 'Izbraukšana' : 'Check-out'}</label>
              <input type="date" lang="et" value={checkOut} min={checkIn || undefined} onChange={(e) => setCheckOut(e.target.value)} />
            </div>
            <div className="hotel-search-field">
              <label>{lang === 'ru' ? 'Гостей' : lang === 'et' ? 'Külalised' : lang === 'lv' ? 'Viesi' : 'Guests'}</label>
              <div className="acc-guest-counter">
                <button type="button" className="acc-guest-btn" onClick={() => setGuestFilter(v => String(Math.max(1, Number(v || 1) - 1)))} disabled={!guestFilter || Number(guestFilter) <= 1}>−</button>
                <span className="acc-guest-val">{guestFilter || '—'}</span>
                <button type="button" className="acc-guest-btn" onClick={() => setGuestFilter(v => String(Number(v || 0) + 1))}>+</button>
              </div>
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-row-label">{lang === 'ru' ? 'Сортировка' : lang === 'et' ? 'Sorteeri' : lang === 'lv' ? 'Kārtot' : 'Sort'}</div>
            <div className="filter-bar filter-bar--secondary">
              {HOTEL_PRICE_SORTS.map(option => (
                <button key={option.key} className={`filter-pill ${priceSort === option.key ? 'on' : ''}`} onClick={() => setPriceSort(option.key)}>
                  {option.label(lang)}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-row-label">{lang === 'ru' ? 'Категория' : 'Category'}</div>
            <div className="filter-bar">
              {HOTEL_STARS.map(s => (
                <button key={s.key} className={`filter-pill ${filter===s.key?'on':''}`} onClick={() => setFilter(s.key)}>
                  {s.label(lang)}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-row-label">{t('l_style', lang)}</div>
            <div className="filter-bar filter-bar--secondary">
              {HOTEL_FORMATS.map(format => (
                <button
                  key={format.key}
                  className={`filter-pill ${formatFilter === format.key ? 'on' : ''}`}
                  onClick={() => setFormatFilter(format.key)}
                >
                  {format.label(lang)}
                </button>
              ))}
            </div>
          </div>
        </div>
        {list.length === 0 ? (
          <div style={{textAlign:'center', padding:'80px 0', color:'var(--text-soft)'}}>
            <p style={{fontSize:18}}>{lang==='ru'?'Отели по выбранным фильтрам скоро появятся':'Hotels matching these filters are coming soon'}</p>
          </div>
        ) : (
          <div className="pkg-cards">
            {list.map((h, i) => (
              <Reveal key={h.id} delay={i*0.08}>
                <div className="pkg-card" onClick={() => onNav('accommodation-detail', h.id)} style={{cursor:'pointer'}}>
                  {hPhotos(h).length > 0 && (
                    <HotelCardPhotoPreview hotel={h} lang={lang} />
                  )}
                  <div className="pkg-card-tag">{h.stars}{h.format ? ` · ${pkgFormat(h.format, lang)}` : ''}{h.isSpecialOffer ? ' · ⭐' : ''}</div>
                  <div className="pkg-card-name">{hName(h, lang)}</div>
                  <div className="pkg-card-brief">{hBrief(h, lang)}</div>
                  {(hRoomType(h, lang) || hMealType(h, lang) || hPeopleCount(h) > 0) && (
                    <div className="pkg-card-row">
                      {hRoomType(h, lang) && <span>{hRoomType(h, lang)}</span>}
                      {hMealType(h, lang) && <span>{hMealType(h, lang)}</span>}
                      {hPeopleCount(h) > 0 && <span>{hPeopleCount(h)} {lang === 'ru' ? 'чел.' : 'guests'}</span>}
                    </div>
                  )}
                  <div className="pkg-card-price">
                    <small>{t('l_from',lang)} </small>
                    {Number(h.pricePerNight).toLocaleString()} {h.currency || '€'}
                    <small> / {t('acc_ppn',lang)}</small>
                    {searchNights > 0 && (
                      <div className="acc-card-total">
                        {searchNights} {lang==='ru'?(searchNights===1?'ночь':searchNights<5?'ночи':'ночей'):lang==='et'?`${searchNights} öö`:lang==='lv'?`${searchNights} naktis`:`${searchNights} nights`}
                        {' = '}
                        <strong>{(searchNights * Number(h.pricePerNight)).toLocaleString()} {h.currency || '€'}</strong>
                      </div>
                    )}
                  </div>
                  <button className="btn-refined" style={{marginTop:12}}><span>{t('btn_det',lang)}</span><span className="btn-refined-line"/><span>→</span></button>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function AccommodationDetailPage({ id, onNav, onBook, lang }) {
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const hotel = getHotels().find(h => h.id === id);
  const dest = hotel ? getDestinations().find(d => d.id === Number(hotel.destinationId)) : null;
  if (!hotel) return null;
  const today = new Date().toISOString().split('T')[0];
  const nights = checkIn && checkOut
    ? Math.max(0, Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000))
    : 0;
  const totalPrice = nights > 0 ? nights * Number(hotel.pricePerNight) : 0;
  const photos = hPhotos(hotel);
  const heroImg = photos[0]?.src;
  return (
    <>
      <div className="page-hero" style={{minHeight:480}}>
        {heroImg && <div className="page-hero-bg" style={{backgroundImage:`url(${heroImg})`, opacity:0.35}} />}
        <div className="page-hero-content">
          <button className="btn-refined" onClick={() => onNav('accommodation')} style={{color:'var(--sand)', marginBottom:24}}>
            <span>{t('acc_back',lang)}</span>
          </button>
          <div style={{fontSize:20, marginBottom:8, color:'var(--sand)'}}>{hotel.stars}</div>
          <h1 className="sec-title sec-title-white" style={{fontSize:'clamp(36px,4.5vw,56px)'}}>{hName(hotel, lang)}</h1>
          <p className="sec-sub sec-sub-light">{hBrief(hotel, lang)}</p>
        </div>
      </div>
      <div className="pkg-detail-grid">
        <div className="pkg-detail-main">
          {photos.length > 0 && (
            <Reveal>
              <HotelDetailGallery hotel={hotel} photos={photos} lang={lang} />
            </Reveal>
          )}
          {hDescription(hotel, lang) && (
            <>
              <Reveal><h3 className="pkg-h3">{lang === 'ru' ? 'Описание' : lang === 'et' ? 'Kirjeldus' : lang === 'lv' ? 'Apraksts' : 'Description'}</h3></Reveal>
              <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300, marginBottom:48, color:'var(--text)'}}>{hDescription(hotel, lang)}</p></Reveal>
            </>
          )}
        </div>
        <div className="pkg-detail-side">
          <div className="pkg-sidebar-card">
            <div className="acc-price-from-label">{t('l_from',lang)}</div>
            <div className="pkg-sidebar-price">{Number(hotel.pricePerNight).toLocaleString()} {hotel.currency || '€'}</div>
            <div className="pkg-sidebar-from">{t('acc_ppn',lang)}</div>

            <div className="acc-dates">
              <div className="acc-dates__title">
                {lang==='ru'?'Выберите даты':lang==='et'?'Vali kuupäevad':lang==='lv'?'Izvēlieties datumus':'Select dates'}
              </div>
              <div className="acc-dates__grid">
                <div className="acc-dates__field">
                  <label>{lang==='ru'?'Заезд':lang==='et'?'Saabumine':lang==='lv'?'Ierašanās':'Check-in'}</label>
                  <input
                    type="date" lang="et"
                    value={checkIn}
                    min={today}
                    onChange={e => { setCheckIn(e.target.value); if (checkOut && e.target.value >= checkOut) setCheckOut(''); }}
                  />
                </div>
                <div className="acc-dates__field">
                  <label>{lang==='ru'?'Выезд':lang==='et'?'Lahkumine':lang==='lv'?'Izbraukšana':'Check-out'}</label>
                  <input
                    type="date" lang="et"
                    value={checkOut}
                    min={checkIn || today}
                    disabled={!checkIn}
                    onChange={e => setCheckOut(e.target.value)}
                  />
                </div>
              </div>
              {nights > 0 && (
                <div className="acc-calc">
                  <div className="acc-calc__row">
                    <span>{nights} {lang==='ru'?(nights===1?'ночь':nights<5?'ночи':'ночей'):lang==='et'?`${nights} öö`:lang==='lv'?`${nights} nakt${nights===1?'s':'is'}`:`${nights} night${nights>1?'s':''}`}</span>
                    <span>× {Number(hotel.pricePerNight).toLocaleString()} {hotel.currency || '€'}</span>
                  </div>
                  <div className="acc-calc__total">
                    <span>{lang==='ru'?'Итого':lang==='et'?'Kokku':lang==='lv'?'Kopā':'Total'}</span>
                    <span>{totalPrice.toLocaleString()} {hotel.currency || '€'}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="pkg-sidebar-meta">
              <div className="pkg-sidebar-row">
                <span>{lang==='ru'?'Категория':lang==='et'?'Kategooria':lang==='lv'?'Kategorija':'Category'}</span>
                <span>{hotel.stars}</span>
              </div>
              {hotel.format && (
                <div className="pkg-sidebar-row">
                  <span>{t('l_style',lang)}</span>
                  <span>{pkgFormat(hotel.format, lang)}</span>
                </div>
              )}
              {hRoomType(hotel, lang) && (
                <div className="pkg-sidebar-row">
                  <span>{lang==='ru'?'Тип номера':lang==='et'?'Toa tüüp':lang==='lv'?'Numura tips':'Room Type'}</span>
                  <span>{hRoomType(hotel, lang)}</span>
                </div>
              )}
              {hMealType(hotel, lang) && (
                <div className="pkg-sidebar-row">
                  <span>{lang==='ru'?'Питание':lang==='et'?'Toitlustus':lang==='lv'?'Ēdināšana':'Meal Plan'}</span>
                  <span>{hMealType(hotel, lang)}</span>
                </div>
              )}
              {hPeopleCount(hotel) > 0 && (
                <div className="pkg-sidebar-row">
                  <span>{lang==='ru'?'Гости':lang==='et'?'Külalised':lang==='lv'?'Viesi':'Guests'}</span>
                  <span>{hPeopleCount(hotel)}</span>
                </div>
              )}
              {dest && (
                <div className="pkg-sidebar-row">
                  <span>{t('l_dest',lang)}</span>
                  <span>{dest[`name_${lang}`] || dest.name_ru || dest.name_en}</span>
                </div>
              )}
            </div>

            <button className="btn-solid" style={{width:'100%', textAlign:'center'}} onClick={() => onBook && onBook(hotel)}>
              {t('btn_book',lang)}
            </button>
            <button className="btn-border" style={{width:'100%', textAlign:'center', marginTop:12}} onClick={() => onNav('contact')}>
              {t('btn_ask',lang)}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function ServicesPage({ onNav, lang }) {
  return (
    <>
      <div className="page-hero">
        <div className="page-hero-bg" style={{backgroundImage:"url('https://images.unsplash.com/photo-1436491865332-7a61a109db05?w=1600&q=80&fm=webp')"}} />
        <div className="page-hero-content">
          <div className="sec-label" style={{color:'var(--sand)'}}>{t('sp_l',lang)}</div>
          <h1 className="sec-title sec-title-white">{t("sp_t",lang)}</h1>
          <p className="sec-sub sec-sub-light">Everything for a flawless journey — in one place</p>
        </div>
      </div>
      {(lang === 'ru' || lang === 'en' || lang === 'et' || lang === 'lv') && (
        <section className="section" style={{borderBottom:'1px solid rgba(0,0,0,0.07)'}}>
          <div style={{maxWidth:800, margin:'0 auto'}}>
            <Reveal>
              {lang === 'ru' ? (<>
                <h2 style={{fontFamily:'var(--serif)', fontSize:30, fontWeight:400, color:'var(--ink)', marginBottom:10}}>Туры от всех туроператоров Эстонии</h2>
                <h3 style={{fontFamily:'var(--serif)', fontSize:20, fontWeight:400, color:'var(--accent)', marginBottom:24}}>Все турпакеты Эстонии в одном месте</h3>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:12}}>Мы сотрудничаем со всеми ведущими туроператорами Эстонии — Coral Travel, Tez Tour, Anex Tour, Join Up и другими.</p>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:12}}>Через нас вы можете забронировать любые турпакеты по самым выгодным ценам.</p>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:8}}>Мы предлагаем:</p>
                <ul style={{listStyle:'none', padding:0, margin:'0 0 16px 0'}}>
                  {['Полный ассортимент туров по всему миру','Подбор пакета под ваши интересы и бюджет','Удобное бронирование и оформление документов','Надёжную поддержку на всех этапах путешествия'].map((item,i) => (
                    <li key={i} style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:4}}>{'• '+item}</li>
                  ))}
                </ul>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)'}}>С нами планирование отдыха становится простым, удобным и выгодным — все туры Эстонии доступны в одном месте.</p>
              </>) : lang === 'et' ? (<>
                <h2 style={{fontFamily:'var(--serif)', fontSize:30, fontWeight:400, color:'var(--ink)', marginBottom:10}}>Reisid kõigilt Eesti reisikorraldajatelt</h2>
                <h3 style={{fontFamily:'var(--serif)', fontSize:20, fontWeight:400, color:'var(--accent)', marginBottom:24}}>Kõik Eesti pakettreisid ühes kohas</h3>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:12}}>Teeme koostööd kõigi Eesti juhtivate reisikorraldajatega — Coral Travel, Tez Tour, Anex Tour, Join UP ja teised.</p>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:12}}>Meie kaudu saate broneerida mistahes pakettreisi parima hinnaga.</p>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:8}}>Me pakume:</p>
                <ul style={{listStyle:'none', padding:0, margin:'0 0 16px 0'}}>
                  {['Täielik valik reise üle maailma','Pakettide valik vastavalt teie huvidele ja eelarvele','Mugav broneerimine ja dokumentide vormistamine','Usaldusväärne tugi igas reisi etapis'].map((item,i) => (
                    <li key={i} style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:4}}>{'• '+item}</li>
                  ))}
                </ul>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)'}}>Meiega muutub puhkuse planeerimine lihtsaks, mugavaks ja soodsaks — kõik Eesti reisid on ühes kohas saadaval.</p>
              </>) : lang === 'lv' ? (<>
                <h2 style={{fontFamily:'var(--serif)', fontSize:30, fontWeight:400, color:'var(--ink)', marginBottom:10}}>Ceļojumi no visiem Igaunijas tūroperatoriem</h2>
                <h3 style={{fontFamily:'var(--serif)', fontSize:20, fontWeight:400, color:'var(--accent)', marginBottom:24}}>Visi Igaunijas ceļojumu paketes vienuviet</h3>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:12}}>Mēs sadarbojamies ar visiem vadošajiem tūroperatoriem Igaunijā — Coral Travel, Tez Tour, Anex Tour, Join UP un citiem.</p>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:12}}>Ar mums jūs varat rezervēt jebkuru ceļojumu paketi par visizdevīgākajām cenām.</p>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:8}}>Mēs piedāvājam:</p>
                <ul style={{listStyle:'none', padding:0, margin:'0 0 16px 0'}}>
                  {['Pilnu ceļojumu klāstu visā pasaulē','Paketes izvēli atbilstoši jūsu interesēm un budžetam','Ērtu rezervāciju un dokumentu noformēšanu','Uzticamu atbalstu visos ceļojuma posmos'].map((item,i) => (
                    <li key={i} style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:4}}>{'• '+item}</li>
                  ))}
                </ul>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)'}}>Ar mums atpūtas plānošana kļūst vienkārša, ērta un izdevīga — visi Igaunijas ceļojumi vienuviet.</p>
              </>) : (<>
                <h2 style={{fontFamily:'var(--serif)', fontSize:30, fontWeight:400, color:'var(--ink)', marginBottom:10}}>Tours from all Estonian tour operators</h2>
                <h3 style={{fontFamily:'var(--serif)', fontSize:20, fontWeight:400, color:'var(--accent)', marginBottom:24}}>All Estonian tour packages in one place</h3>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:12}}>We work with all leading tour operators in Estonia — Coral Travel, Tez Tour, Anex Tour, Join UP and others.</p>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:12}}>With us, you can book any travel package at the best possible prices.</p>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:8}}>We offer:</p>
                <ul style={{listStyle:'none', padding:0, margin:'0 0 16px 0'}}>
                  {['A full range of tours worldwide','Tailor-made package selection to match your interests and budget','Convenient booking and document processing','Reliable support at every stage of your trip'].map((item,i) => (
                    <li key={i} style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:4}}>{'• '+item}</li>
                  ))}
                </ul>
                <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)'}}>With us, planning your holiday becomes simple, convenient, and cost-effective — all Estonian tours are available in one place.</p>
              </>)}
            </Reveal>
          </div>
        </section>
      )}
      <section className="section">
        <div style={{maxWidth:800, margin:'0 auto'}}>
          {getServices(lang).map((s, i) => (
            <Reveal key={s.id} delay={i*0.08}>
              <div style={{display:'grid', gridTemplateColumns:'80px 1fr', gap:36, padding:'56px 0', borderBottom: i<getServices(lang).length-1?'1px solid rgba(0,0,0,0.05)':'none', alignItems:'start'}}>
                <span style={{fontSize:28, color:'var(--accent)', width:80, height:80, display:'flex', alignItems:'center', justifyContent:'center', background:'var(--cream)'}}>{s.icon}</span>
                <div>
                  <h3 style={{fontFamily:'var(--serif)', fontSize:26, fontWeight:400, color:'var(--ink)', marginBottom:14}}>{s.name}</h3>
                  {!s.body && <p style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:20}}>{s.desc}</p>}
                  {s.body && s.body.map((block, bi) => {
                    if (block.t === 'p') return <p key={bi} style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:12}}>{block.v}</p>;
                    if (block.t === 'list') return <ul key={bi} style={{listStyle:'none', padding:0, margin:'0 0 16px 0'}}>{block.v.map((item,j) => <li key={j} style={{fontSize:15, lineHeight:1.85, fontWeight:300, color:'var(--text-soft)', marginBottom:4}}>{'• '+item}</li>)}</ul>;
                    return null;
                  })}
                  <button className="btn-refined" onClick={() => onNav('contact')}><span>{t('btn_enq',lang)}</span><span className="btn-refined-line"/><span>→</span></button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}

function AboutPage({ onNav, lang }) {
  return (
    <>
      <div className="page-hero">
        <div className="page-hero-bg" style={{backgroundImage:"url('https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1600&q=80&fm=webp')"}} />
        <div className="page-hero-content">
          <div className="sec-label" style={{color:'var(--sand)'}}>{t('ap_l',lang)}</div>
          <h1 className="sec-title sec-title-white">{t('ap_t',lang)}</h1>
          <p className="sec-sub sec-sub-light">A team that turned a passion for exploration into art</p>
        </div>
      </div>
      <section className="section">
        <div className="about-intro">
          <div style={{overflow:'hidden'}}>
            <img src="/images/about.png" alt="About Let's Travel" className="about-intro-img" />
          </div>
          <div style={{display:'flex', flexDirection:'column', justifyContent:'center'}}>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300, marginBottom:20}}>{t('ap_p1',lang)}</p></Reveal>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300, marginBottom:20}}>{t('ap_p2',lang)}</p></Reveal>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300}}>{t('ap_p3',lang)}</p></Reveal>
          </div>
        </div>
      </section>
      <div style={{background:'var(--forest-deep)', padding:'64px 60px'}}>
        <div className="about-stats-grid" style={{maxWidth:1000, margin:'0 auto', textAlign:'center'}}>
          {[['3,000+',t('ap_s1',lang)],['65','Countries'],['12',t('ap_s3',lang)],['98%',t('ap_s4',lang)]].map(([n,l],i) => (
            <Reveal key={i} delay={i*0.1}>
              <div>
                <div style={{fontFamily:'var(--serif)', fontSize:48, fontWeight:300, color:'var(--sand)', lineHeight:1}}>{n}</div>
                <div style={{fontSize:11, letterSpacing:2, textTransform:'uppercase', color:'rgba(244,240,232,0.4)', marginTop:10}}>{l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <section className="section">
        <Reveal><div style={{textAlign:'center', marginBottom:64}}><div className="sec-label">{t('ap_why_l',lang)}</div><h2 className="sec-title">{t('ap_why_t',lang)}</h2></div></Reveal>
        <div className="val-grid">
          {getValues(lang).map((v, i) => (
            <Reveal key={i} delay={i*0.08}>
              <div style={{padding:36, border:'1px solid rgba(0,0,0,0.05)'}}>
                <div className="val-num">{v.icon}</div>
                <div className="val-title">{v.title}</div>
                <div className="val-desc">{v.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="section-sm section-cream" style={{textAlign:'center'}}>
        <Reveal>
          <h2 className="sec-title" style={{marginBottom:16}}>{t('ap_ready',lang)}</h2>
          <p className="sec-sub" style={{margin:'0 auto 32px'}}>Tell us about your dream journey — we'll make it a reality</p>
          <button className="btn-solid" onClick={() => onNav('contact')}>{t('btn_consult',lang)}</button>
        </Reveal>
      </section>
    </>
  );
}

function ValuesPage({ lang }) {
  return (
    <>
      <div className="page-hero">
        <div className="page-hero-bg" style={{backgroundImage:"url('https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600&q=80&fm=webp')"}} />
        <div className="page-hero-content">
          <div className="sec-label" style={{color:'var(--sand)'}}>{t('vp_l',lang)}</div>
          <h1 className="sec-title sec-title-white">{t('vp_t',lang)}</h1>
          <p className="sec-sub sec-sub-light">The principles that guide every decision we make</p>
        </div>
      </div>
      <section className="section">
        <div style={{maxWidth:720, margin:'0 auto 64px'}}>
          {lang === 'ru' ? (<>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300, marginBottom:20}}>Let's Travel создаёт путешествия, продуманные до мелочей, чтобы каждый момент был комфортным и незабываемым. Мы ценим ваше время и силы, поэтому берём на себя все организационные вопросы, позволяя вам полностью погрузиться в впечатления.</p></Reveal>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300, marginBottom:20}}>Наш подход всегда индивидуальный: мы учитываем все ваши предпочтения, интересы и особенности, чтобы каждая поездка была уникальной. Мы работаем только с надёжными партнёрами по всему миру, гарантируя высокий уровень сервиса на каждом этапе.</p></Reveal>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300}}>Для нас важен результат и ваше спокойствие — поэтому вы можете быть уверены, что всё будет выполнено точно в срок и без лишней суеты. Просто наслаждайтесь моментом, а обо всём остальном позаботится Let's Travel.</p></Reveal>
          </>) : lang === 'et' ? (<>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300, marginBottom:20}}>Let's Travel loob reisid, mis on läbi mõeldud kuni pisimate detailideni, et iga hetk oleks mugav ja meeldejääv. Me hindame teie aega ja energiat, seetõttu võtame enda kanda kogu korraldusliku poole — nii saate täielikult keskenduda elamustele.</p></Reveal>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300, marginBottom:20}}>Meie lähenemine on alati personaalne: arvestame teie eelistuste, huvide ja eripäradega, et iga reis oleks ainulaadne. Teeme koostööd ainult usaldusväärsete partneritega üle maailma, tagades kõrge teenindustaseme igas etapis.</p></Reveal>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300}}>Meile on oluline tulemus ja teie rahu — seetõttu võite olla kindel, et kõik tehakse täpselt õigeks ajaks ja ilma liigse saginata. Nautige hetke — ülejäänu eest hoolitseb Let's Travel.</p></Reveal>
          </>) : lang === 'lv' ? (<>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300, marginBottom:20}}>Let's Travel veido ceļojumus, kas ir pārdomāti līdz vissīkākajām detaļām, lai katrs mirklis būtu ērts un neaizmirstams. Mēs novērtējam jūsu laiku un enerģiju, tāpēc uzņemamies visus organizatoriskos jautājumus — lai jūs varētu pilnībā izbaudīt piedzīvojumu.</p></Reveal>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300, marginBottom:20}}>Mūsu pieeja vienmēr ir individuāla: mēs ņemam vērā jūsu vēlmes, intereses un vajadzības, lai katrs ceļojums būtu unikāls. Mēs sadarbojamies tikai ar uzticamiem partneriem visā pasaulē, nodrošinot augstu servisa līmeni katrā posmā.</p></Reveal>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300}}>Mums ir svarīgs rezultāts un jūsu miers — tāpēc varat būt pārliecināti, ka viss tiks izdarīts laikā un bez liekas steigas. Vienkārši izbaudiet mirkli, par pārējo parūpēsies Let's Travel.</p></Reveal>
          </>) : (<>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300, marginBottom:20}}>Let's Travel creates journeys planned down to the smallest details, so every moment is comfortable and truly unforgettable. We value your time and energy, so we take care of all organizational matters — allowing you to fully immerse yourself in the experience.</p></Reveal>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300, marginBottom:20}}>Our approach is always personal: we consider your preferences, interests, and individual needs so that every trip feels unique. We work only with trusted partners worldwide, ensuring a high level of service at every stage.</p></Reveal>
            <Reveal><p style={{fontSize:17, lineHeight:1.9, fontWeight:300}}>For us, the result and your peace of mind matter most — so you can be sure everything will be delivered on time and without unnecessary hassle. Just enjoy the moment, and Let's Travel will take care of everything else.</p></Reveal>
          </>)}
        </div>
        <div className="val-grid" style={{maxWidth:1100, margin:'0 auto'}}>
          {getValues(lang).map((v, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div style={{padding:44, background:'var(--white)', border:'1px solid rgba(0,0,0,0.04)'}}>
                <div className="val-num">{v.icon}</div>
                <div className="val-title">{v.title}</div>
                <div className="val-desc">{v.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}

function BlogPage({ onNav, lang }) {
  const [allPosts, setAllPosts] = useState(getBlog(lang));
  const [filter, setFilter] = useState('all');
  useEffect(() => {
    adminAPI.getPosts().then(data => {
      if (data.length > 0) setAllPosts(mapApiPosts(data, lang));
    }).catch(() => {});
  }, [lang]);
  const filters = [
    { key: 'all', label: t('b_all', lang) },
    { key: 'destinations', label: t('b_dest', lang) },
    { key: 'advice', label: t('b_adv', lang) },
    { key: 'inspiration', label: t('b_insp', lang) },
    { key: 'news', label: t('b_news', lang) },
  ];
  const visible = filter === 'all' ? allPosts : allPosts.filter(p => p.category === filter);

  return (
    <>
      <div className="page-hero">
        <div className="page-hero-bg" style={{backgroundImage:"url('https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1600&q=80&fm=webp')"}} />
        <div className="page-hero-content">
          <div className="sec-label" style={{color:'var(--sand)'}}>{t('bp_l',lang)}</div>
          <h1 className="sec-title sec-title-white">{t("bp_t",lang)}</h1>
          <p className="sec-sub sec-sub-light">Guides, insights, and tales from the road</p>
        </div>
      </div>
      <section className="section">
        <div className="filter-bar">
          {filters.map(f => (
            <button key={f.key} className={`filter-pill ${filter === f.key ? 'on' : ''}`} onClick={() => setFilter(f.key)}>{f.label}</button>
          ))}
        </div>
        <div className="blog-row" style={{maxWidth:1000,margin:'0 auto'}}>
          {visible.map((p, i) => (
            <Reveal key={p.id} delay={i*0.1}>
              <div className="blog-card" style={{ cursor: 'default' }}>
                <div className="blog-card-img"><img src={p.image || FALLBACK_IMGS[i % 3]} alt="" loading="lazy" /></div>
                <button
                  className="blog-card-title"
                  style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', width: '100%', cursor: 'pointer' }}
                  onClick={() => onNav('blog-post', p.id)}
                >
                  {p.title}
                </button>
                <button className="btn-refined" style={{marginTop:12}} onClick={() => onNav('blog-post', p.id)}>
                  <span>{t('btn_read',lang)}</span><span className="btn-refined-line"/><span>→</span>
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}

function BlogPostPage({ id, onNav, lang }) {
  const [post, setPost] = useState(null);

  useEffect(() => {
    let active = true;

    adminAPI.getPosts()
      .then((data) => {
        if (!active) return;
        const mapped = mapApiPosts(data, lang);
        const found = mapped.find((item) => Number(item.id) === Number(id));
        if (found) {
          setPost(found);
          return;
        }

        const fallback = getBlog(lang).find((item) => Number(item.id) === Number(id));
        if (fallback) {
          setPost({ ...fallback, body: fallback.excerpt || '' });
        } else {
          setPost(null);
        }
      })
      .catch(() => {
        const fallback = getBlog(lang).find((item) => Number(item.id) === Number(id));
        if (!active) return;
        if (fallback) {
          setPost({ ...fallback, body: fallback.excerpt || '' });
        } else {
          setPost(null);
        }
      });

    return () => {
      active = false;
    };
  }, [id, lang]);

  if (!post) {
    return (
      <section className="section" style={{ paddingTop: 140 }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <button className="btn-refined" onClick={() => onNav('blog')} style={{ marginBottom: 24 }}>
            <span>←</span><span>{t('bp_l', lang)}</span>
          </button>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="section" style={{ paddingTop: 140 }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <button className="btn-refined" onClick={() => onNav('blog')} style={{ marginBottom: 24 }}>
            <span>←</span><span>{t('bp_l', lang)}</span>
          </button>

          <div className="blog-card-img" style={{ marginBottom: 24 }}>
            <img src={post.image || FALLBACK_IMGS[0]} alt={post.title} loading="lazy" />
            {post.cat && <span className="blog-card-cat">{post.cat}</span>}
          </div>

          {post.date && <div className="blog-card-date" style={{ marginBottom: 14 }}>{post.date}</div>}
          <h1 className="sec-title" style={{ marginBottom: 20, textAlign: 'left' }}>{post.title}</h1>

          {post.body
            .split('\n')
            .filter(Boolean)
            .map((paragraph, index) => (
              <p key={index} style={{ fontSize: 18, lineHeight: 1.9, color: 'var(--text)', marginBottom: 16, fontWeight: 300 }}>
                {paragraph}
              </p>
            ))}
        </div>
      </section>
    </>
  );
}

function ContactPage({ lang }) {
  const [form, setForm]       = useState({ fn:'', ln:'', em:'', ph:'', topic:'', msg:'', consent:false });
  const [errors, setErrors]   = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(false);
  const [sendErr, setSendErr] = useState('');

  const u = (k, v) => { setForm(p => ({...p, [k]: v})); setErrors(p => ({...p, [k]: null})); };

  const validate = () => {
    const e = {};
    if (!form.fn.trim()) e.fn = t('e_req', lang);
    if (!form.em.trim()) e.em = t('e_req', lang);
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.em)) e.em = t('e_em_inv', lang);
    if (!form.msg.trim()) e.msg = t('e_req', lang);
    if (!form.consent) e.consent = t('e_consent', lang);
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async () => {
    if (!validate()) return;
    setSending(true);
    setSendErr('');
    try {
      const res = await fetch(`${API_BASE}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:    `${form.fn} ${form.ln}`.trim(),
          email:   form.em,
          phone:   form.ph || '',
          topic:   form.topic || t('f_t_gen', lang),
          message: form.msg,
        }),
      });
      if (!res.ok) throw new Error();
      setSent(true);
    } catch {
      setSendErr('Ошибка отправки. Пожалуйста, напишите напрямую на info@letstravel.ee');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="page-hero">
        <div className="page-hero-bg" style={{backgroundImage:"url('https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1600&q=80&fm=webp')"}} />
        <div className="page-hero-content">
          <div className="sec-label" style={{color:'var(--sand)'}}>Get in Touch</div>
          <h1 className="sec-title sec-title-white">{t('cp_t',lang)}</h1>
          <p className="sec-sub sec-sub-light">We'd love to hear about your next adventure</p>
        </div>
      </div>
      <section className="section">
        <div className="contact-split">
          <Reveal>
            <div>
              <h3 className="sec-title" style={{fontSize:30, marginBottom:20}}>{t('cp_how',lang)}</h3>
              <p style={{fontSize:15, lineHeight:1.8, fontWeight:300, color:'var(--text-soft)', marginBottom:36}}>{t('cp_how_s',lang)}</p>
              {[
                ['✆', t('cp_phone',lang), <a href="tel:+3725101959" style={{color:'inherit'}}>+372 510 1959</a>],
                ['✉', t('cp_email',lang), <a href="mailto:info@letstravel.ee" style={{color:'inherit'}}>info@letstravel.ee</a>],
                ['◎', t('cp_addr',lang),  'Tallinn, Estonia'],
                ['◷', t('cp_hours',lang), '24/7'],
              ].map(([ic, lbl, val]) => (
                <div key={lbl} className="contact-line">
                  <div className="contact-line-icon">{ic}</div>
                  <div><div className="contact-line-label">{lbl}</div><div className="contact-line-val" style={{whiteSpace:'pre-line'}}>{val}</div></div>
                </div>
              ))}
              <div style={{marginTop:36}}>
                <div className="contact-line-label" style={{marginBottom:12}}>
                  {lang==='ru'?'Банковские реквизиты':lang==='et'?'Pangaandmed':lang==='lv'?'Bankas rekvizīti':'Bank Accounts'}
                </div>
                {[
                  { iban: 'EE222200221032516480', bank: 'Swedbank' },
                  { iban: 'EE107700771011888304', bank: 'LHV Pank' },
                ].map(({ iban, bank }) => (
                  <div key={iban} className="contact-line" style={{marginBottom:12}}>
                    <div className="contact-line-icon">⬡</div>
                    <div>
                      <div className="contact-line-label">{bank}</div>
                      <div className="contact-line-val" style={{fontFamily:'monospace', letterSpacing:'0.5px'}}>{iban}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{marginTop:28}}>
                <div className="sec-label" style={{marginBottom:16}}>{t('cp_follow',lang)}</div>
                <div style={{display:'flex', gap:12, flexWrap:'wrap'}}>
                  <a href="https://www.instagram.com/letstravelestonia?igsh=cXhnMG05d256anE4&utm_source=qr" target="_blank" rel="noopener noreferrer" style={{padding:'8px 18px', border:'1px solid rgba(0,0,0,0.08)', fontSize:12, color:'var(--text-soft)', textDecoration:'none'}}>Instagram</a>
                  <a href="https://www.facebook.com/share/1DQ8ouNwYW/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" style={{padding:'8px 18px', border:'1px solid rgba(0,0,0,0.08)', fontSize:12, color:'var(--text-soft)', textDecoration:'none'}}>Facebook</a>
                  <a href="https://t.me/+3725101959" target="_blank" rel="noopener noreferrer" style={{padding:'8px 18px', border:'1px solid rgba(0,0,0,0.08)', fontSize:12, color:'var(--text-soft)', textDecoration:'none'}}>Telegram</a>
                  <a href="https://wa.me/3725101959" target="_blank" rel="noopener noreferrer" style={{padding:'8px 18px', border:'1px solid rgba(0,0,0,0.08)', fontSize:12, color:'var(--text-soft)', textDecoration:'none'}}>WhatsApp</a>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div>
              <h3 className="sec-title" style={{fontSize:26, marginBottom:24}}>{t('cp_form',lang)}</h3>
              {sent ? (
                <div style={{textAlign:'center', padding:48, background:'var(--cream)'}}>
                  <div style={{fontSize:40, color:'var(--sage)', marginBottom:16}}>✓</div>
                  <h4 style={{fontFamily:'var(--serif)', fontSize:24, fontWeight:400, marginBottom:8}}>{t('cp_sent',lang)}</h4>
                  <p style={{color:'var(--text-soft)', fontWeight:300}}>{t('cp_sent_s',lang)}</p>
                </div>
              ) : (
                <>
                  <div className="form-row">
                    <div className="form-grp">
                      <label className="form-lbl">{t("f_fn",lang)} *</label>
                      <input className={`form-inp${errors.fn?' err':''}`} value={form.fn} onChange={e=>u('fn',e.target.value)} placeholder={t('f_fn',lang)} />
                      {errors.fn && <div className="form-err">{errors.fn}</div>}
                    </div>
                    <div className="form-grp">
                      <label className="form-lbl">{t("f_ln",lang)}</label>
                      <input className="form-inp" value={form.ln} onChange={e=>u('ln',e.target.value)} placeholder={t('f_ln',lang)} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-grp">
                      <label className="form-lbl">{t("f_em",lang)} *</label>
                      <input className={`form-inp${errors.em?' err':''}`} type="email" value={form.em} onChange={e=>u('em',e.target.value)} placeholder="email@example.com" />
                      {errors.em && <div className="form-err">{errors.em}</div>}
                    </div>
                    <div className="form-grp">
                      <label className="form-lbl">{t('f_ph',lang)}</label>
                      <input className="form-inp" type="tel" value={form.ph} onChange={e=>u('ph',e.target.value)} placeholder="+372 ..." />
                    </div>
                  </div>
                  <div className="form-grp">
                    <label className="form-lbl">{t("f_topic",lang)}</label>
                    <select className="form-inp" value={form.topic} onChange={e=>u('topic',e.target.value)}>
                      <option value="">{t('f_t_gen',lang)}</option>
                      <option value={t('f_t_tour',lang)}>{t('f_t_tour',lang)}</option>
                      <option value={t('f_t_fl',lang)}>{t('f_t_fl',lang)}</option>
                      <option value={t('f_t_htl',lang)}>{t('f_t_htl',lang)}</option>
                      <option value={t('f_t_jet',lang)}>{t('f_t_jet',lang)}</option>
                      <option value={t('f_t_oth',lang)}>{t('f_t_oth',lang)}</option>
                    </select>
                  </div>
                  <div className="form-grp">
                    <label className="form-lbl">{t("f_msg",lang)} *</label>
                    <textarea className={`form-inp form-ta${errors.msg?' err':''}`} value={form.msg} onChange={e=>u('msg',e.target.value)} placeholder={t("f_msg_ph",lang)} />
                    {errors.msg && <div className="form-err">{errors.msg}</div>}
                  </div>
                  <div className="form-chk-row">
                    <input type="checkbox" className="form-chk" checked={form.consent} onChange={e=>u('consent',e.target.checked)} />
                    <span className="form-chk-lbl">{t('f_consent',lang)}</span>
                  </div>
                  {errors.consent && <div className="form-err" style={{marginTop:-8,marginBottom:12}}>{errors.consent}</div>}
                  {sendErr && <div className="form-err" style={{marginBottom:12}}>{sendErr}</div>}
                  <button className="form-submit" onClick={submit} disabled={sending}>
                    {sending ? '...' : t('btn_send',lang)}
                  </button>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

// ─── TINGIMUSED PAGE ───
function TingimisedPage({ onNav, lang }) {
  const L = lang || 'et';
  const s = {
    h2: {fontFamily:'var(--serif)',fontSize:22,fontWeight:400,marginBottom:12,marginTop:36,color:'var(--ink)'},
    h3: {fontFamily:'var(--serif)',fontSize:17,fontWeight:400,marginBottom:8,marginTop:24,color:'var(--ink)'},
    p:  {fontSize:15,lineHeight:1.85,fontWeight:300,color:'var(--text-soft)',marginBottom:12},
    li: {fontSize:15,lineHeight:1.85,fontWeight:300,color:'var(--text-soft)',marginBottom:6},
  };
  const C = {
    et: {
      heroTitle: 'Tingimused',
      heroSub: 'Teenuse osutamise tingimused · Reg. nr. 11273008',
      intro: 'Käesolevad tingimused reguleerivad LETSTRAVEL OÜ (registrikood 11273008, asukoht Tallinn, Eesti) osutatavaid reisiteenuseid. Teenuse kasutamisega nõustub klient käesolevate tingimustega täies ulatuses.',
      s1: '1. Ettevõtte andmed', s1loc: 'Asukoht: Tallinn, Eesti',
      s2: '2. Teenuste kirjeldus', s2p: 'LETSTRAVEL OÜ pakub individuaalseid reisiteenuseid, sealhulgas:',
      s2items: ['Individuaalsete reisipakettide koostamine','Majutuse broneerimine','Lennupiletite broneerimine','Transferide korraldamine','Reisikindlustuse vahendamine','Ekskursioonide ja giiditeenuste korraldamine'],
      s3: '3. Broneerimine ja tasumine',
      s31: '3.1 Broneerimise kinnitamine', s31p: 'Broneering loetakse kinnitatuks pärast ettemaksu laekumist ja kirjaliku broneeringukinnituse saatmist kliendile.',
      s32: '3.2 Lõppmakse', s32p: ['Lõppmakse tasutakse üldjuhul hiljemalt 30 päeva enne väljumiskuupäeva.','Konkreetsed maksetähtajad võivad erineda sõltuvalt reisikorraldaja tingimustest, valitud tariifist ja teenusepakkujatest.','Täpsed lõppmakse tingimused ja tähtajad on märgitud lepingus, mis esitatakse kliendile enne broneeringu kinnitamist.'],
      s33: '3.3 Makseviisid', s33p: 'Võtame vastu pangaülekandeid. Makseandmed edastatakse broneeringukinnitusega.',
      s4: '4. Tühistamine ja tagasimaksed', s4p: 'Tühistamise ja broneeringu muutmise tingimused määratakse individuaalselt ning on märgitud lepingus, mis esitatakse kliendile enne teenuse kinnitamist. Iga juhtumit käsitletakse eraldi.',
      s4cancel: 'Tühistamine tuleb esitada kirjalikult e-posti aadressile',
      s5: '5. Vastutus', s5p: 'LETSTRAVEL OÜ vastutab reisiteenuste nõuetekohase korraldamise eest vastavalt Eesti Vabariigi seadustele ja Euroopa Liidu tarbijakaitsenormidele. Ettevõte ei vastuta vääramatu jõu (looduskatastroofid, poliitilised sündmused, streigid jms) tõttu tekkinud kahju eest.',
      s6: '6. Isikuandmete töötlemine', s6p: 'LETSTRAVEL OÜ töötleb kliendi isikuandmeid vastavalt GDPR nõuetele. Andmeid kasutatakse üksnes broneeringu täitmiseks ega edastata kolmandatele isikutele ilma kliendi nõusolekuta.',
      s7: '7. Vaidluste lahendamine', s7p: 'Pooled püüavad vaidlused lahendada läbirääkimiste teel. Kliendil on õigus pöörduda Tarbijakaitse ja Tehnilise Järelevalve Ameti poole. Kohaldatakse Eesti õigust.',
      s8: '8. Tingimuste muutmine', s8p: 'LETSTRAVEL OÜ-l on õigus tingimusi igal ajal muuta. Muudatused avaldatakse veebilehel ja jõustuvad avaldamise päevast.',
      s9: '9. Reisitingimused', s9p: 'LETSTRAVEL OÜ müüb teiste reisikorraldajate reise, siis lähtutakse selle reisikorraldaja (vastutav reisikorraldaja) reisitingimustest:',
      s9docs: 'Standardinfo teabeleht', s9cond: 'Reisitingimused',
      tezNote: 'Kehtivad standardhinnaga pakettreisidele. Varajase tellija erihinnaga, algusega mujalt kui Tallinn ja soodshinnaga pakettreiside puhul lisanduvad reisilepingule eritingimused.',
      latvianNote: 'NB! Läti reisikorraldaja.',
      s9travel: 'Reisija vastutab reisi broneerides, et reisidokumendid vastavad sihtriiki sisenemiseks vajalikele nõuetele. Võimalik, et teil on vaja taotleda viisat (või muud reisidokumenti) sisenemiseks oma sihtriiki või ka riiki, kus reisi jätkamiseks tuleb ümber istuda teisele lennukile. Täpsemat informatsiooni selle kohta leiab välisministeeriumi koduleheküljelt.',
      updated: 'Viimati uuendatud: veebruar 2026',
      back: '← Tagasi avalehele',
    },
    ru: {
      heroTitle: 'Условия',
      heroSub: 'Условия оказания услуг · Рег. № 11273008',
      intro: 'Настоящие условия регулируют туристические услуги, оказываемые LETSTRAVEL OÜ (регистрационный код 11273008, г. Таллин, Эстония). Используя услуги, клиент полностью принимает настоящие условия.',
      s1: '1. Данные компании', s1loc: 'Адрес: Таллин, Эстония',
      s2: '2. Описание услуг', s2p: 'LETSTRAVEL OÜ предлагает индивидуальные туристические услуги, в том числе:',
      s2items: ['туристические пакеты','Бронирование проживания','Бронирование авиабилетов','Организация трансферов','Посредничество в оформлении туристической страховки','Организация экскурсий и гидовых услуг'],
      s3: '3. Бронирование и оплата',
      s31: '3.1 Подтверждение бронирования', s31p: 'Бронирование считается подтверждённым после поступления аванса и направления клиенту письменного подтверждения.',
      s32: '3.2 Окончательный платёж', s32p: ['Окончательный платёж по бронированию, как правило, производится не позднее чем за 30 дней до даты выезда.','Конкретные сроки оплаты могут варьироваться в зависимости от условий туроператора, выбранного тарифа и поставщиков услуг.','Точные условия и сроки окончательного платежа указываются в договоре, предоставляемом клиенту перед подтверждением бронирования.'],
      s33: '3.3 Способы оплаты', s33p: 'Принимаем банковские переводы. Платёжные реквизиты направляются вместе с подтверждением бронирования.',
      s4: '4. Отмена и возврат средств', s4p: 'Условия отмены и изменения бронирования определяются индивидуально и указываются в договоре, предоставляемом клиенту перед подтверждением услуги. Каждый случай рассматривается отдельно.',
      s4cancel: 'Отмена должна быть оформлена в письменном виде на адрес электронной почты',
      s5: '5. Ответственность', s5p: 'LETSTRAVEL OÜ несёт ответственность за надлежащую организацию туристических услуг в соответствии с законодательством Эстонской Республики и нормами защиты прав потребителей ЕС. Компания не несёт ответственности за ущерб, причинённый обстоятельствами непреодолимой силы (стихийные бедствия, политические события, забастовки и т.п.).',
      s6: '6. Обработка персональных данных', s6p: 'LETSTRAVEL OÜ обрабатывает персональные данные клиента в соответствии с требованиями GDPR. Данные используются исключительно для выполнения бронирования и не передаются третьим лицам без согласия клиента.',
      s7: '7. Разрешение споров', s7p: 'Стороны стремятся разрешать споры путём переговоров. Клиент вправе обратиться в Департамент защиты прав потребителей и технического надзора. Применяется законодательство Эстонии.',
      s8: '8. Изменение условий', s8p: 'LETSTRAVEL OÜ вправе в любое время изменять настоящие условия. Изменения публикуются на веб-сайте и вступают в силу с даты публикации.',
      s9: '9. Условия путешествий', s9p: 'LETSTRAVEL OÜ продаёт туры других туроператоров — в таких случаях применяются условия соответствующего туроператора (ответственного туроператора):',
      s9docs: 'Стандартный информационный листок', s9cond: 'Условия путешествий',
      tezNote: 'Действуют для пакетных туров по стандартной цене. Для туров по специальной цене раннего бронирования, с вылетом не из Таллина и по льготной цене к договору добавляются особые условия.',
      latvianNote: 'NB! Латвийский туроператор.',
      s9travel: 'Путешественник несёт ответственность за то, чтобы при бронировании поездки его документы соответствовали требованиям въезда в страну назначения. Возможно, вам потребуется получить визу (или иной документ) для въезда в страну назначения или в страну пересадки. Более подробную информацию можно найти на сайте Министерства иностранных дел.',
      updated: 'Последнее обновление: февраль 2026',
      back: '← На главную',
    },
    en: {
      heroTitle: 'Terms & Conditions',
      heroSub: 'Terms of service · Reg. no. 11273008',
      intro: 'These terms govern travel services provided by LETSTRAVEL OÜ (registry code 11273008, Tallinn, Estonia). By using the service, the client agrees to these terms in full.',
      s1: '1. Company details', s1loc: 'Address: Tallinn, Estonia',
      s2: '2. Services', s2p: 'LETSTRAVEL OÜ provides individual travel services, including:',
      s2items: ['Tailored travel package planning','Accommodation booking','Flight ticket booking','Transfer arrangements','Travel insurance mediation','Excursion and guide service organisation'],
      s3: '3. Booking and payment',
      s31: '3.1 Booking confirmation', s31p: 'A booking is confirmed after the deposit has been received and a written booking confirmation sent to the client.',
      s32: '3.2 Final payment', s32p: ['The final payment for the booking is generally due no later than 30 days before the departure date.','Specific payment deadlines may vary depending on the tour operator\'s conditions, the selected fare, and service providers.','The exact terms and deadlines for the final payment are specified in the contract provided to the client before booking confirmation.'],
      s33: '3.3 Payment methods', s33p: 'We accept bank transfers. Payment details are provided with the booking confirmation.',
      s4: '4. Cancellation and refunds', s4p: 'Cancellation and amendment terms are determined individually and specified in the contract provided to the client before service confirmation. Each case is handled separately.',
      s4cancel: 'Cancellations must be submitted in writing to',
      s5: '5. Liability', s5p: 'LETSTRAVEL OÜ is responsible for the proper organisation of travel services in accordance with Estonian law and EU consumer protection regulations. The company is not liable for damages caused by force majeure (natural disasters, political events, strikes, etc.).',
      s6: '6. Personal data processing', s6p: 'LETSTRAVEL OÜ processes client personal data in accordance with GDPR requirements. Data is used solely for fulfilling the booking and is not shared with third parties without the client\'s consent.',
      s7: '7. Dispute resolution', s7p: 'The parties seek to resolve disputes through negotiation. The client has the right to contact the Consumer Protection and Technical Regulatory Authority. Estonian law applies.',
      s8: '8. Changes to terms', s8p: 'LETSTRAVEL OÜ reserves the right to amend these terms at any time. Changes are published on the website and take effect from the date of publication.',
      s9: '9. Travel conditions', s9p: 'When LETSTRAVEL OÜ sells tours operated by other tour operators, the conditions of that tour operator (the responsible tour operator) apply:',
      s9docs: 'Standard information sheet', s9cond: 'Travel conditions',
      tezNote: 'Applicable to standard-price package tours. Special conditions are added to the travel contract for early-bird, non-Tallinn departure, and discounted packages.',
      latvianNote: 'NB! Latvian tour operator.',
      s9travel: 'The traveller is responsible for ensuring that their travel documents meet the entry requirements of the destination country. You may need to apply for a visa (or other travel document) to enter your destination or a transit country. More information can be found on the website of the Ministry of Foreign Affairs.',
      updated: 'Last updated: February 2026',
      back: '← Back to home',
    },
    lv: {
      heroTitle: 'Noteikumi',
      heroSub: 'Pakalpojumu sniegšanas noteikumi · Reģ. nr. 11273008',
      intro: 'Šie noteikumi regulē LETSTRAVEL OÜ (reģistrācijas kods 11273008, Tallina, Igaunija) sniegtos tūrisma pakalpojumus. Izmantojot pakalpojumus, klients pilnībā piekrīt šiem noteikumiem.',
      s1: '1. Uzņēmuma dati', s1loc: 'Adrese: Tallina, Igaunija',
      s2: '2. Pakalpojumu apraksts', s2p: 'LETSTRAVEL OÜ piedāvā individuālus tūrisma pakalpojumus, tostarp:',
      s2items: ['Individuālu ceļojumu pakešu izveide','Izmitināšanas rezervācija','Aviobiļešu rezervācija','Transferu organizēšana','Ceļojumu apdrošināšanas starpniecība','Ekskursiju un gidu pakalpojumu organizēšana'],
      s3: '3. Rezervācija un apmaksa',
      s31: '3.1 Rezervācijas apstiprināšana', s31p: 'Rezervācija tiek uzskatīta par apstiprinātu pēc avansa saņemšanas un rakstiska apstiprinājuma nosūtīšanas klientam.',
      s32: '3.2 Galīgais maksājums', s32p: ['Galīgais maksājums par rezervāciju parasti jāveic ne vēlāk kā 30 dienas pirms izbraukšanas datuma.','Konkrētie maksājumu termiņi var atšķirties atkarībā no tūroperatora nosacījumiem, izvēlētā tarifa un pakalpojumu sniedzējiem.','Precīzie galīgā maksājuma noteikumi un termiņi ir norādīti līgumā, kas tiek sniegts klientam pirms rezervācijas apstiprināšanas.'],
      s33: '3.3 Maksājuma veidi', s33p: 'Pieņemam bankas pārskaitījumus. Maksājuma rekvizīti tiek nosūtīti kopā ar rezervācijas apstiprinājumu.',
      s4: '4. Atcelšana un atmaksa', s4p: 'Atcelšanas un rezervācijas izmaiņu noteikumi tiek noteikti individuāli un norādīti līgumā, kas tiek sniegts klientam pirms pakalpojuma apstiprināšanas. Katrs gadījums tiek izskatīts atsevišķi.',
      s4cancel: 'Atcelšana jāiesniedz rakstiski uz e-pasta adresi',
      s5: '5. Atbildība', s5p: 'LETSTRAVEL OÜ ir atbildīgs par tūrisma pakalpojumu pienācīgu organizēšanu saskaņā ar Igaunijas Republikas likumiem un ES patērētāju tiesību aizsardzības normām. Uzņēmums neatbild par zaudējumiem, kas radušies nepārvaramas varas apstākļu dēļ (dabas katastrofas, politiski notikumi, streiki u.tml.).',
      s6: '6. Personas datu apstrāde', s6p: 'LETSTRAVEL OÜ apstrādā klienta personas datus saskaņā ar GDPR prasībām. Dati tiek izmantoti tikai rezervācijas izpildei un netiek nodoti trešajām personām bez klienta piekrišanas.',
      s7: '7. Strīdu risināšana', s7p: 'Puses cenšas strīdus risināt sarunu ceļā. Klientam ir tiesības vērsties Patērētāju tiesību aizsardzības un tehniskās uzraudzības iestādē. Piemērojami Igaunijas tiesību akti.',
      s8: '8. Noteikumu maiņa', s8p: 'LETSTRAVEL OÜ ir tiesības jebkurā laikā mainīt šos noteikumus. Izmaiņas tiek publicētas tīmekļa vietnē un stājas spēkā publicēšanas dienā.',
      s9: '9. Ceļojumu noteikumi', s9p: 'Kad LETSTRAVEL OÜ pārdod citu tūroperatoru ceļojumus, piemērojami attiecīgā tūroperatora (atbildīgā tūroperatora) noteikumi:',
      s9docs: 'Standarta informācijas lapa', s9cond: 'Ceļojumu noteikumi',
      tezNote: 'Attiecas uz pakešceļojumiem par standarta cenu. Īpaši nosacījumi tiek pievienoti ceļojuma līgumam agrīnās rezervācijas, izbraukšanas no citām pilsētām (ne Tallinas) un atlaides cenas gadījumā.',
      latvianNote: 'NB! Latvijas tūroperators.',
      s9travel: 'Ceļotājs ir atbildīgs par to, lai ceļojuma rezervēšanas brīdī viņa ceļojuma dokumenti atbilstu galamērķa valsts ieceļošanas prasībām. Iespējams, jums būs jāpiesakās vīzai (vai citam ceļošanas dokumentam), lai ieceļotu galamērķa valstī vai tranzīta valstī. Sīkāku informāciju var atrast Ārlietu ministrijas tīmekļa vietnē.',
      updated: 'Pēdējo reizi atjaunināts: 2026. gada februāris',
      back: '← Atpakaļ uz sākumlapu',
    },
  };
  const c = C[L] || C.et;
  const OPERATORS = [
    {
      name: 'Novatours OÜ',
      meta: 'Reg-nr 11013879 · MKM nr TRE000096 · Rävala pst 6, Tallinn',
      links: [
        { label: 'Reisitingimused (ET)', href: '/docs/novatours/novatours-reisitingimused-et.pdf' },
        { label: 'Travel conditions (EN)', href: '/docs/novatours/novatours-reisitingimused-en.pdf' },
        { label: 'Условия поездки (RU)', href: '/docs/novatours/novatours-reisitingimused-ru.pdf' },
      ],
      note: null,
    },
    {
      name: 'Tez Tour OÜ',
      meta: 'Reg-nr 11468166 · MKM nr TRE000482 · Laeva 2, Tallinn',
      links: [
        { label: 'TEZ TOUR pakettreisilepingu standardinfo teabeleht', href: 'https://www.teztour.ee/static/photos/common/files/TEZ%20TOUR%20Pakettreisilepingu%20standardinfo%20teabeleht2025.pdf' },
        { label: 'Reisilepingu üldtingimused pakettreisidele', href: 'https://www.teztour.ee/page/reisilepingu-tingimused-pakettreisidele-2025.et.html' },
        { label: 'Reisilepingu üldtingimused liinilendudega pakettreisidele', href: 'https://www.teztour.ee/page/reisilepingu-liinilendudega-reisidele.et.html' },
        { label: 'TEZ TOUR pakettreiside üldinfo ja terminite selgitused', href: 'https://www.teztour.ee/page/important-information.et.html' },
      ],
      note: c.tezNote,
    },
    {
      name: 'Coral Travel Estonia OÜ',
      meta: 'Reg-nr 16194673 · MKM nr TRE001007 · Liivalaia tn. 22, Tallinn',
      links: [
        { label: 'Standardinfo teabeleht', href: 'https://b2ccdn.coraltravel.ee/content/Pakettreisilepingu_standardinfo_teabeleht_Coral_Travel.pdf' },
        { label: 'Reisitingimused', href: 'https://b2ccdn.coraltravel.ee/content/Charter%20flights%20general%20terms%20and%20conditions_2024%20(2).pdf' },
        { label: 'coraltravel.ee/documents', href: 'https://www.coraltravel.ee/documents/' },
      ],
      note: null,
    },
    {
      name: 'SIA "Join Up Baltic"',
      meta: 'Reg-nr 50203351701 · Vienības gatve 109, Riia',
      links: [
        { label: 'Standardinfo teabeleht (JOIN UP! BALTIC)', href: '/docs/joinup/Teabevorm.pdf' },
        { label: 'Lennupiletite ostmise reeglid', href: '/docs/joinup/purchase-rules_ET.pdf' },
        { label: 'Kliendileping – Zanzibar / Tansaania (LK/TZ)', href: '/docs/joinup/client-agreement-LK-TZ-et.pdf' },
        { label: 'Kliendileping – Türgi / Egiptus / Montenegro / Kreeka', href: '/docs/joinup/client-agreement-TR-EG-ME-GR-et.pdf' },
        { label: 'Kliendileping – Araabia Ühendemiraadid (UAE)', href: '/docs/joinup/client-agreement-UAE-et.pdf' },
      ],
      note: c.latvianNote,
    },
    {
      name: 'TUI Baltics SIA / TT Baltics',
      meta: 'Registrikood 40203149224 · KMKR LV40203149224 · Vienības t 109–311, Riia, LV-1058, Läti · Technopolis Ülemiste, Lõõtsa 2B, 5 korrus, Tallinn',
      links: [
        { label: c.s9docs, href: 'https://www.reis24.ee/wp-content/uploads/TUI-standard-form.pdf' },
        { label: c.s9cond, href: 'https://www.reis24.ee/wp-content/uploads/TUI_Baltics_SIA_reisileping.pdf' },
      ],
      note: c.latvianNote,
    },
    {
      name: 'SIA Baltic World Eesti filiaal (Anex Tour)',
      meta: 'Reg-nr 16678968 · T-2023-5 · Liivalaia 22, 6 korrus, Tallinn',
      links: [
        { label: 'anextour.ee', href: 'https://anextour.ee' },
        { label: c.s9cond, href: 'https://www.anextour.ee/pages/documents' },
      ],
      note: null,
    },
  ];
  return (
    <>
      <div className="page-hero" style={{padding:'160px 60px 80px'}}>
        <div className="page-hero-bg" style={{backgroundImage:"url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1600&q=80&fm=webp')"}} />
        <div className="page-hero-content">
          <div className="sec-label" style={{color:'var(--sand)'}}>LETSTRAVEL OÜ</div>
          <h1 className="sec-title sec-title-white">{c.heroTitle}</h1>
          <p className="sec-sub sec-sub-light">{c.heroSub}</p>
        </div>
      </div>
      <section style={{maxWidth:800,margin:'0 auto',padding:'80px 40px'}}>
        <p style={s.p}>{c.intro}</p>

        <h2 style={s.h2}>{c.s1}</h2>
        <p style={s.p}><strong>LETSTRAVEL OÜ</strong><br />{L==='ru'?'Регистрационный код':L==='en'?'Registry code':L==='lv'?'Reģistrācijas kods':'Registrikood'}: 11273008<br />{c.s1loc}<br />{L==='ru'?'Эл. почта':L==='en'?'Email':L==='lv'?'E-pasts':'E-post'}: <a href="mailto:info@letstravel.ee" style={{color:'var(--accent)'}}>info@letstravel.ee</a><br />{L==='ru'?'Телефон':L==='lv'?'Tālrunis':'Telefon'}: <a href="tel:+3725101959" style={{color:'var(--accent)'}}>+372 510 1959</a></p>

        <h2 style={s.h2}>{c.s2}</h2>
        <p style={s.p}>{c.s2p}</p>
        <ul style={{paddingLeft:20,marginBottom:16}}>
          {c.s2items.map(i => <li key={i} style={s.li}>{i}</li>)}
        </ul>

        <h2 style={s.h2}>{c.s3}</h2>
        <h3 style={s.h3}>{c.s31}</h3>
        <p style={s.p}>{c.s31p}</p>
        <h3 style={s.h3}>{c.s32}</h3>
        {(Array.isArray(c.s32p)?c.s32p:[c.s32p]).map((t,i)=><p key={i} style={s.p}>{t}</p>)}
        <h3 style={s.h3}>{c.s33}</h3>
        <p style={s.p}>{c.s33p}</p>

        <h2 style={s.h2}>{c.s4}</h2>
        <p style={s.p}>{c.s4p}</p>
        <p style={s.p}>{c.s4cancel} <a href="mailto:info@letstravel.ee" style={{color:'var(--accent)'}}>info@letstravel.ee</a>.</p>

        <h2 style={s.h2}>{c.s5}</h2>
        <p style={s.p}>{c.s5p}</p>

        <h2 style={s.h2}>{c.s6}</h2>
        <p style={s.p}>{c.s6p}</p>

        <h2 style={s.h2}>{c.s7}</h2>
        <p style={s.p}>{c.s7p} <a href="https://www.ttja.ee" target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)'}}>www.ttja.ee</a></p>

        <h2 style={s.h2}>{c.s8}</h2>
        <p style={s.p}>{c.s8p}</p>

        <h2 style={s.h2}>{c.s9}</h2>
        <p style={s.p}>{c.s9p}</p>

        {OPERATORS.map(op => (
          <div key={op.name} style={{border:'1px solid rgba(0,0,0,0.09)',padding:'20px 24px',marginBottom:16}}>
            <div style={{fontWeight:600,fontSize:15,marginBottom:4}}>{op.name}</div>
            <div style={{fontSize:12,color:'#888',marginBottom:op.note?8:12}}>{op.meta}</div>
            {op.note && <div style={{fontSize:12,color:'#b08d57',marginBottom:12,fontStyle:'italic'}}>{op.note}</div>}
            <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
              {op.links.map(lk => (
                <a key={lk.label} href={lk.href} target="_blank" rel="noopener noreferrer" style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:13,color:'var(--accent)',textDecoration:'none',borderBottom:'1px solid currentColor',paddingBottom:1}}>
                  <span>↗</span><span>{lk.label}</span>
                </a>
              ))}
            </div>
          </div>
        ))}

        <p style={{...s.p, marginTop:16}}>{c.s9travel}</p>

        <p style={{...s.p,marginTop:8,borderTop:'1px solid rgba(0,0,0,0.08)',paddingTop:24,fontSize:13}}>{c.updated} &nbsp;·&nbsp; LETSTRAVEL OÜ</p>
        <button className="btn-refined" style={{marginTop:32}} onClick={() => onNav('home')}>
          <span>{c.back}</span><span className="btn-refined-line"/>
        </button>
      </section>
    </>
  );
}

// ─── BOOKING MODAL ───
function BookingModal({ pkg, onClose, lang }) {
  const dest = DESTINATIONS.find(d => d.id === pkg.destinationId);
  const [approx, setApprox] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({ ds:'', de:'', at:'', adults:2, kids:0, kn:'', fl:false, tr:false, ins:false, pj:false, fn:'', ln:'', em:'', ph:'', cm:'', ok:false });
  const [err, setErr] = useState({});
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState('');
  const u = (k,v) => { setF(p=>({...p,[k]:v})); if(err[k]) setErr(p=>({...p,[k]:null})); };
  const validate = () => { const e={}; if(!f.fn.trim()) e.fn='Required'; if(!f.em.trim()&&!f.ph.trim()) e.em='Email or phone required'; if(f.em&&!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.em)) e.em='Invalid email'; if(!approx&&!f.ds) e.ds='Required'; if(approx&&!f.at.trim()) e.at='Required'; if(f.adults<1) e.adults='Min 1'; if(!f.ok) e.ok='Consent required'; setErr(e); return !Object.keys(e).length; };
  const submit = async () => {
    if (!validate()) return;
    setSending(true);
    setSendErr('');
    try {
      const res = await fetch(`${API_BASE}/booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          packageName: pName(pkg.id, lang),
          destination: dest ? dName(dest.id, lang) : '',
          dates: approx ? f.at : (f.ds && f.de ? `${fmtDate(f.ds)} — ${fmtDate(f.de)}` : fmtDate(f.ds)),
          adults: f.adults,
          kids: f.kids,
          kidsAges: f.kn,
          flight: f.fl,
          transfer: f.tr,
          insurance: f.ins,
          privateJet: f.pj,
          name: `${f.fn} ${f.ln}`.trim(),
          email: f.em,
          phone: f.ph,
          comment: f.cm,
        }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch {
      setSendErr('Ошибка отправки. Пожалуйста, напишите напрямую на info@letstravel.ee');
    } finally {
      setSending(false);
    }
  };

  if (done) return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal-box" onClick={e=>e.stopPropagation()} style={{position:'relative'}}>
        <button className="modal-x" onClick={onClose}>✕</button>
        <div style={{padding:64, textAlign:'center'}}>
          <div style={{fontSize:48, color:'var(--sage)', marginBottom:20}}>✓</div>
          <h2 style={{fontFamily:'var(--serif)', fontSize:32, fontWeight:400, color:'var(--ink)', marginBottom:12}}>{t('bk_thx',lang)}, {f.fn}!</h2>
          <p style={{color:'var(--text-soft)', fontWeight:300, lineHeight:1.7, marginBottom:28}}>{t('bk_recv',lang)}</p>
          <div style={{background:'var(--cream)', padding:28, textAlign:'left', fontSize:14, lineHeight:1.9, fontWeight:300}}>
            <strong style={{fontWeight:500}}>{t('bk_summary',lang)}</strong><br/>
            {t("bk_pkg",lang)}: {pName(pkg.id,lang)}<br/>{t("l_dest",lang)}: {dName(dest.id,lang)}<br/>
            Dates: {approx ? f.at : `${fmtDate(f.ds)} — ${fmtDate(f.de)}`}<br/>
            Guests: {f.adults} adult{f.adults>1?'s':''}{f.kids>0?`, ${f.kids} child${f.kids>1?'ren':''}`:''}<br/>
            {f.em && <>{t("f_em",lang)}: {f.em}<br/></>}{f.ph && <>{t("f_ph",lang)}: {f.ph}<br/></>}
          </div>
          <button className="btn-solid" style={{marginTop:32}} onClick={onClose}>{t('btn_close',lang)}</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal-box" onClick={e=>e.stopPropagation()} style={{position:'relative'}}>
        <button className="modal-x" onClick={onClose}>✕</button>
        <div className="modal-head">
          <h2 style={{fontFamily:'var(--serif)', fontSize:30, fontWeight:400, color:'var(--ink)', marginBottom:6}}>{t('bk_t',lang)}</h2>
          <p style={{fontSize:14, color:'var(--text-soft)', fontWeight:300}}>{t('bk_s',lang)}</p>
        </div>
        <div className="modal-body">
          <div className="form-sec">
            <div className="form-sec-title">{t('bk_pkg',lang)}</div>
            <div className="form-row">
              <div className="form-grp"><label className="form-lbl">{t("bk_pkg",lang)}</label><input className="form-inp ro" value={pName(pkg.id,lang)} readOnly /></div>
              <div className="form-grp"><label className="form-lbl">{t("l_dest",lang)}</label><input className="form-inp ro" value={dName(dest.id,lang)} readOnly /></div>
            </div>
          </div>
          <div className="form-sec">
            <div className="form-sec-title">{t('bk_dates',lang)}</div>
            <div className="form-chk-row" style={{marginBottom:14}}><input type="checkbox" className="form-chk" checked={approx} onChange={e=>setApprox(e.target.checked)} /><span className="form-chk-lbl">{t('bk_approx',lang)}</span></div>
            {approx ? (
              <div className="form-grp"><label className="form-lbl">{t('bk_approx_f',lang)}</label><input className={`form-inp ${err.at?'err':''}`} placeholder={t("bk_approx_ph",lang)} value={f.at} onChange={e=>u('at',e.target.value)} />{err.at && <div className="form-err">{err.at}</div>}</div>
            ) : (
              <div className="form-row"><div className="form-grp"><label className="form-lbl">{t('bk_ds',lang)}</label><input className={`form-inp ${err.ds?'err':''}`} type="date" lang="et" value={f.ds} onChange={e=>u('ds',e.target.value)} />{err.ds && <div className="form-err">{err.ds}</div>}</div><div className="form-grp"><label className="form-lbl">{t('bk_de',lang)}</label><input className="form-inp" type="date" lang="et" value={f.de} onChange={e=>u('de',e.target.value)} /></div></div>
            )}
          </div>
          <div className="form-sec">
            <div className="form-sec-title">{t('bk_guests',lang)}</div>
            <div className="form-row">
              <div className="form-grp">
                <label className="form-lbl">{t('bk_adults',lang)}</label>
                <div className={`bk-counter ${err.adults?'err':''}`}>
                  <button type="button" className="bk-counter-btn" onClick={() => u('adults', Math.max(1, f.adults - 1))} disabled={f.adults <= 1}>−</button>
                  <span className="bk-counter-val">{f.adults}</span>
                  <button type="button" className="bk-counter-btn" onClick={() => u('adults', f.adults + 1)}>+</button>
                </div>
                {err.adults && <div className="form-err">{err.adults}</div>}
              </div>
              <div className="form-grp">
                <label className="form-lbl">{t('bk_kids',lang)}</label>
                <div className="bk-counter">
                  <button type="button" className="bk-counter-btn" onClick={() => u('kids', Math.max(0, f.kids - 1))} disabled={f.kids <= 0}>−</button>
                  <span className="bk-counter-val">{f.kids}</span>
                  <button type="button" className="bk-counter-btn" onClick={() => u('kids', f.kids + 1)}>+</button>
                </div>
              </div>
            </div>
            {(pkg.groupPricing?.length > 0) && (() => {
              const gp = pkg.groupPricing;
              const exact = gp.find(r => r.adults === f.adults && r.children === f.kids);
              const byAdults = gp.filter(r => r.adults === f.adults).sort((a,b) => Math.abs(a.children-f.kids) - Math.abs(b.children-f.kids))[0];
              const match = exact || byAdults || gp[0];
              const currency = pkg.currency || '€';
              const noExact = !exact;
              return (
                <div className="bk-price-calc">
                  <div className="bk-price-calc__row">
                    <span className="bk-price-calc__label">
                      {match.adults} {lang==='ru'?'взр.':lang==='et'?'täisk.':lang==='lv'?'pieaug.':'adult(s)'}
                      {match.children > 0 && ` + ${match.children} ${lang==='ru'?'дет.':lang==='et'?'last':lang==='lv'?'bērni':'child(ren)'}`}
                    </span>
                    <span className="bk-price-calc__price">{currency}{Number(match.price).toLocaleString()}</span>
                  </div>
                  {noExact && <div className="bk-price-calc__hint">{lang==='ru'?'Ближайший вариант из доступных':lang==='et'?'Lähim saadaolev variant':'Nearest available option'}</div>}
                  <div className="bk-price-calc__options">
                    {gp.map((r,i) => (
                      <button key={i} type="button"
                        className={`bk-price-opt ${r.adults===f.adults&&r.children===f.kids?'active':''}`}
                        onClick={() => { u('adults', r.adults); u('kids', r.children); }}>
                        {r.adults} {lang==='ru'?'взр.':'ad.'}{r.children > 0 ? ` + ${r.children} ${lang==='ru'?'дет.':'ch.'}` : ''} — {currency}{Number(r.price).toLocaleString()}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
            {f.kids>0 && <div className="form-grp" style={{marginTop:12}}><label className="form-lbl">{t('bk_kids_age',lang)}</label><input className="form-inp" placeholder={t("bk_kids_ph",lang)} value={f.kn} onChange={e=>u('kn',e.target.value)} /></div>}
          </div>
          <div className="form-sec">
            <div className="form-sec-title">{t('bk_extras',lang)}</div>
            {[['fl','bk_fl'],['tr','bk_tr'],['ins','bk_ins'],['pj','bk_jet']].map(([k,tk])=>(
              <div key={k} className="form-chk-row"><input type="checkbox" className="form-chk" checked={f[k]} onChange={e=>u(k,e.target.checked)} /><span className="form-chk-lbl">{t(tk,lang)}</span></div>
            ))}
          </div>
          <div className="form-sec">
            <div className="form-sec-title">{t('bk_contact',lang)}</div>
            <div className="form-row"><div className="form-grp"><label className="form-lbl">{t('f_fn',lang)} *</label><input className={`form-inp ${err.fn?'err':''}`} value={f.fn} onChange={e=>u('fn',e.target.value)} />{err.fn && <div className="form-err">{err.fn}</div>}</div><div className="form-grp"><label className="form-lbl">{t('f_ln',lang)}</label><input className="form-inp" value={f.ln} onChange={e=>u('ln',e.target.value)} /></div></div>
            <div className="form-row"><div className="form-grp"><label className="form-lbl">{t('f_em',lang)} *</label><input className={`form-inp ${err.em?'err':''}`} type="email" value={f.em} onChange={e=>u('em',e.target.value)} />{err.em && <div className="form-err">{err.em}</div>}</div><div className="form-grp"><label className="form-lbl">{t("f_ph",lang)}</label><input className="form-inp" type="tel" value={f.ph} onChange={e=>u('ph',e.target.value)} /></div></div>
          </div>
          <div className="form-grp"><label className="form-lbl">{t('bk_comment',lang)}</label><textarea className="form-inp form-ta" value={f.cm} onChange={e=>u('cm',e.target.value)} placeholder={t("bk_comment_ph",lang)} /></div>
          <div className="form-chk-row" style={{marginTop:16}}><input type="checkbox" className="form-chk" checked={f.ok} onChange={e=>u('ok',e.target.checked)} /><span className="form-chk-lbl">{t('f_consent',lang)} *</span></div>
          {err.ok && <div className="form-err">{err.ok}</div>}
          {sendErr && <div className="form-err" style={{marginBottom:12}}>{sendErr}</div>}
          <button className="form-submit" onClick={submit} disabled={sending}>{sending ? '...' : t('btn_submit',lang)}</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════ APP ═══════════
const getBrowserLang = () => {
  const browserLang = navigator.language?.split('-')[0] || 'ru';
  const supportedLangs = { ru: 'ru', en: 'en', et: 'et', lv: 'lv' };
  return supportedLangs[browserLang] || 'ru';
};

// Admin Page Wrapper
function AdminPage({ onLogout }) {
  const [isAuthenticated, setIsAuthenticated] = useState(adminAPI.isAuthenticated());

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:'#666'}}>Загрузка...</div>}>
        <AdminLogin onLoginSuccess={() => setIsAuthenticated(true)} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:'#666'}}>Загрузка...</div>}>
      <AdminPanel onLogout={() => { onLogout(); setIsAuthenticated(false); }} />
    </Suspense>
  );
}

function parseRouteFromPath(pathname) {
  const postMatch = pathname.match(/^\/blog\/(\d+)\/?$/);
  if (postMatch) {
    return { page: 'blog-post', pageId: Number(postMatch[1]) };
  }

  if (pathname === '/blog' || pathname === '/blog/') return { page: 'blog', pageId: null };
  if (pathname === '/admin' || pathname === '/admin/') return { page: 'admin', pageId: null };
  if (pathname === '/contact' || pathname === '/contact/') return { page: 'contact', pageId: null };
  if (pathname === '/services' || pathname === '/services/') return { page: 'services', pageId: null };
  if (pathname === '/about' || pathname === '/about/') return { page: 'about', pageId: null };
  if (pathname === '/values' || pathname === '/values/') return { page: 'values', pageId: null };
  if (pathname === '/destinations' || pathname === '/destinations/') return { page: 'destinations', pageId: null };
  const accMatch = pathname.match(/^\/accommodation\/(\d+)\/?$/);
  if (accMatch) return { page: 'accommodation-detail', pageId: Number(accMatch[1]) };
  if (pathname === '/accommodation' || pathname === '/accommodation/') return { page: 'accommodation', pageId: null };
  if (pathname === '/tingimused' || pathname === '/tingimused/') return { page: 'tingimused', pageId: null };

  return { page: 'home', pageId: null };
}

function pathForRoute(page, pageId) {
  if (page === 'blog-post' && pageId) return `/blog/${pageId}`;
  if (page === 'blog') return '/blog';
  if (page === 'admin') return '/admin';
  if (page === 'contact') return '/contact';
  if (page === 'services') return '/services';
  if (page === 'about') return '/about';
  if (page === 'values') return '/values';
  if (page === 'destinations') return '/destinations';
  if (page === 'accommodation') return '/accommodation';
  if (page === 'accommodation-detail' && pageId) return `/accommodation/${pageId}`;
  if (page === 'tingimused') return '/tingimused';
  return '/';
}

export default function App() {
  const [page, setPage] = useState('home');
  const [pageId, setPageId] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [booking, setBooking] = useState(null);
  const [lang, setLang] = useState(getBrowserLang());
  const [destinations, setDestinations] = useState(DESTINATIONS);
  const [heroSearch, setHeroSearch] = useState(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const applyRoute = () => {
      const route = parseRouteFromPath(window.location.pathname);
      setPage(route.page);
      setPageId(route.pageId);
    };

    applyRoute();

    const onPopState = () => applyRoute();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    let active = true;
    adminAPI.getPackages()
      .then((data) => {
        if (!active) return;
        if (Array.isArray(data) && data.length > 0) {
          const normalized = data.map(p => ({
            ...p,
            id: Number(p.id),
            destinationId: Number(p.destinationId),
            type: "Tour Package",
            priceMode: p.priceMode || 'from',
            currency: p.currency || '€',
          })).filter(p => !isNaN(p.id) && !isNaN(p.destinationId));
          setRuntimePackages(normalized);
        }
      })
      .catch(() => {});
    adminAPI.getHotels()
      .then((data) => {
        if (!active) return;
        if (Array.isArray(data)) {
          setRuntimeHotels(data.map(h => ({
            ...h,
            id: Number(h.id),
            destinationId: Number(h.destinationId),
          })));
        }
      })
      .catch(() => {});
    adminAPI.getDestinations()
      .then((data) => {
        if (!active) return;
        if (Array.isArray(data) && data.length > 0) {
          const normalized = data
            .map(d => normalizeDestination(d, getPackages()))
            .filter(Boolean);
          setDestinations(normalized);
          setRuntimeDestinations(normalized);
        } else {
          setRuntimeDestinations(DESTINATIONS);
        }
      })
      .catch(() => {
        setRuntimeDestinations(DESTINATIONS);
      });
    return () => { active = false; };
  }, []);

  const nav = (p, id = null) => {
    setPage(p);
    setPageId(id);

    const nextPath = pathForRoute(p, id);
    if (window.location.pathname !== nextPath) {
      window.history.pushState({ page: p, pageId: id }, '', nextPath);
    }

    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const renderPage = () => {
    switch (page) {
      case 'home': return <HomePage onNav={nav} onSearch={setHeroSearch} lang={lang} />;
      case 'destinations': return <DestinationsPage onNav={nav} lang={lang} />;
      case 'accommodation': return <AccommodationPage onNav={nav} lang={lang} initialSearch={heroSearch} />;
      case 'accommodation-detail': return <AccommodationDetailPage id={pageId} onNav={nav} onBook={setBooking} lang={lang} />;
      case 'destination': return <DestinationPage id={pageId} onNav={nav} onBook={setBooking} lang={lang} heroSearch={heroSearch} />;
      case 'package': return <PackagePage id={pageId} onNav={nav} onBook={setBooking} lang={lang} />;
      case 'services': return <ServicesPage onNav={nav} lang={lang} />;
      case 'about': return <AboutPage onNav={nav} lang={lang} />;
      case 'values': return <ValuesPage lang={lang} />;
      case 'blog': return <BlogPage onNav={nav} lang={lang} />;
      case 'blog-post': return <BlogPostPage id={pageId} onNav={nav} lang={lang} />;
      case 'contact': return <ContactPage lang={lang} />;
      case 'tingimused': return <TingimisedPage onNav={nav} lang={lang} />;
        case 'admin': return <AdminPage onLogout={() => nav('home')} />;
      default: return <HomePage onNav={nav} lang={lang} />;
    }
  };

  return (
    <>
      <style>{css}</style>
      <div className="app">
        <Nav page={page} onNav={nav} scrolled={scrolled} lang={lang} setLang={setLang} />
        {renderPage()}
        <Footer onNav={nav} lang={lang} />
        {booking && <BookingModal pkg={booking} onClose={() => setBooking(null)} lang={lang} />}
        <a
          className="chat-btn"
          href="https://wa.me/3725101959"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="WhatsApp chat"
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
            <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.532 5.856L.057 23.272a.75.75 0 0 0 .92.92l5.416-1.475A11.943 11.943 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 0 1-5.006-1.371l-.36-.214-3.733 1.016.978-3.625-.234-.373A9.818 9.818 0 1 1 12 21.818z"/>
          </svg>
        </a>
      </div>
    </>
  );
}
